import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { LogOut, Car, Users, FileText, Plus, Trash2, Download, Calendar, CalendarDays, Bell, Edit, Map, Key, Clock, Palmtree, ChevronDown, MoreVertical, UserX, UserCheck, CalendarX, RefreshCw, Star, Settings, Lock, LockOpen, Mail, MessageSquare, CheckCircle, Loader2, Shield, Receipt, FolderOpen, Upload, X, AlertTriangle, Wrench, Building, Award, SlidersHorizontal, Building2, FileOutput, FileSignature, Camera, BarChart3 } from 'lucide-react';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';

// Import constante și helpers din componente admin
import { MONTHS_RO, ROLE_LABELS, getRoleLabel, isActiveEmployee, PontajTab, TrasabilitateTab, ConcediiTab, RoluriTab, AlerteTab, RapoarteTab, FoiDeParcursTab, CurseActiveTab, MasiniTab, AngajatiTab, EvidentaOreTab, EmailConfigTab, MonitorizareDiferenteTab, MotivareTab, MotivareAnalizaTab, SetariMotivareTab, CheltuieliFurnizoriTab, FurnizoriTab, FacturiEmiseTab, FormularePdfTab, ErrorReportsTab } from '@/components/admin';
import { ReportProblemButton } from '@/components/ReportProblemButton';

// Import tab-uri noi
import FinanciarTab from '@/components/admin/tabs/FinanciarTab';
import DocumenteInterneTab from '@/components/admin/tabs/DocumenteInterneTab';
import SetariFlotaTab from '@/components/admin/tabs/SetariFlotaTab';
import MentenantaTab from '@/components/admin/tabs/MentenantaTab';

// Helper pentru generarea opțiunilor de ore
const timeOptions = [];
for (let h = 5; h <= 22; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour = h.toString().padStart(2, '0');
    const minute = m.toString().padStart(2, '0');
    timeOptions.push(`${hour}:${minute}`);
  }
}

// Component TimeSelect reutilizabil - folosim select nativ pentru stabilitate în Dialog
// IMPORTANT: onPointerDown stopPropagation previne conflictul cu Radix UI Dialog
const TimeSelect = ({ value, onChange, placeholder = "Selectează ora" }) => (
  <div onPointerDown={(e) => e.stopPropagation()}>
    <select 
      value={value} 
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent cursor-pointer"
    >
      {timeOptions.map((time) => (
        <option key={time} value={time}>
          {time}
        </option>
      ))}
    </select>
  </div>
);

// Component pentru afișarea mesajelor de contact
const ContactMessagesSection = ({ onMessagesChange }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contact-messages`, getAuthHeaders());
      setMessages(response.data);
      // Count only unread messages for badge
      const unreadCount = response.data.filter(m => !m.citit).length;
      if (onMessagesChange) onMessagesChange(unreadCount);
    } catch (error) {
      console.error('Failed to fetch contact messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/contact-message/${messageId}/citit`, {}, getAuthHeaders());
      const newMessages = messages.map(m => 
        m.id === messageId ? { ...m, citit: true } : m
      );
      setMessages(newMessages);
      const unreadCount = newMessages.filter(m => !m.citit).length;
      if (onMessagesChange) onMessagesChange(unreadCount);
      toast.success('Mesaj marcat ca citit');
    } catch (error) {
      toast.error('Eroare la marcarea mesajului');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Sigur doriți să ștergeți acest mesaj?')) return;
    try {
      await axios.delete(`${API}/contact-message/${messageId}`, getAuthHeaders());
      const newMessages = messages.filter(m => m.id !== messageId);
      setMessages(newMessages);
      const unreadCount = newMessages.filter(m => !m.citit).length;
      if (onMessagesChange) onMessagesChange(unreadCount);
      toast.success('Mesaj șters');
    } catch (error) {
      toast.error('Eroare la ștergerea mesajului');
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p>Nu există mesaje de contact încă.</p>
        <p className="text-sm mt-1">Mesajele trimise de vizitatori vor apărea aici.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-600">{messages.length} mesaj(e)</span>
          {messages.filter(m => !m.citit).length > 0 && (
            <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
              {messages.filter(m => !m.citit).length} necitite
            </span>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={fetchMessages}>
          <RefreshCw className="w-4 h-4 mr-1" /> Reîncarcă
        </Button>
      </div>
      {messages.map((msg) => (
        <div 
          key={msg.id} 
          className={`border rounded-lg p-4 transition-colors ${
            msg.citit 
              ? 'bg-white border-slate-200 hover:bg-slate-50' 
              : 'bg-red-50 border-red-200 hover:bg-red-100'
          }`}
        >
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                {!msg.citit && (
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                )}
                <span className="font-semibold text-slate-800">{msg.nume}</span>
                <span className="text-sm text-slate-500">{msg.email}</span>
              </div>
              <p className="text-slate-700 text-sm whitespace-pre-wrap">{msg.mesaj}</p>
              
              {/* Afișare atașamente */}
              {msg.attachments && msg.attachments.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="text-xs font-medium text-slate-500 mb-2">
                    📎 Atașamente ({msg.attachments.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {msg.attachments.map((att, idx) => (
                      <a
                        key={idx}
                        href={att.data}
                        download={att.name}
                        className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors text-sm"
                        title={`Descarcă ${att.name}`}
                      >
                        {att.type?.includes('image') ? (
                          <span className="text-green-600">🖼️</span>
                        ) : att.type?.includes('pdf') ? (
                          <span className="text-red-600">📄</span>
                        ) : (
                          <span className="text-blue-600">📎</span>
                        )}
                        <span className="text-slate-700 truncate max-w-[150px]">{att.name}</span>
                        <span className="text-xs text-slate-400">
                          ({att.size < 1024 * 1024 
                            ? Math.round(att.size / 1024) + ' KB' 
                            : (att.size / (1024 * 1024)).toFixed(1) + ' MB'})
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              <p className="text-xs text-slate-400 mt-2">
                {new Date(msg.created_at).toLocaleString('ro-RO', { 
                  day: '2-digit', month: '2-digit', year: 'numeric', 
                  hour: '2-digit', minute: '2-digit' 
                })}
              </p>
            </div>
            <div className="flex gap-1">
              {!msg.citit && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => markAsRead(msg.id)} 
                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                  title="Marchează ca citit"
                >
                  <CheckCircle className="w-4 h-4" />
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => deleteMessage(msg.id)} 
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                title="Șterge mesajul"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function AdminDashboard({ auth }) {
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [fleetRoutes, setFleetRoutes] = useState([]);
  const [openCarDialog, setOpenCarDialog] = useState(false);
  const [openEditCarDialog, setOpenEditCarDialog] = useState(false);
  const [openDriverDialog, setOpenDriverDialog] = useState(false);
  const [openEditDriverDialog, setOpenEditDriverDialog] = useState(false);
  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [openDocumentDialog, setOpenDocumentDialog] = useState(false);
  const [documentData, setDocumentData] = useState({ carName: '', licensePlate: '', fieldLabel: '', document: null, documentName: '' });
  const [editingCar, setEditingCar] = useState(null);
  const [editingDriver, setEditingDriver] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [resetPasswordData, setResetPasswordData] = useState({ password: '', confirmPassword: '' });
  const [historyData, setHistoryData] = useState({ carName: '', fieldLabel: '', history: [] });
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  
  // Pending Documents State
  const [openPendingDocDialog, setOpenPendingDocDialog] = useState(false);
  const [pendingDocData, setPendingDocData] = useState({
    carId: '',
    carName: '',
    fieldName: '',
    expiryDate: '',
    activationDate: '',
    document: null,
    documentName: ''
  });
  const [pendingDocuments, setPendingDocuments] = useState({});  // {car_id: [pending_docs]}
  
  // Popover states for date pickers in Edit Car dialog
  const [openPopovers, setOpenPopovers] = useState({
    insurance_expiry: false,
    tax_road_expiry: false,
    inspection_expiry: false,
    casco_expiry: false,
    copie_conforma_expiry: false
  });
  const [newCar, setNewCar] = useState({ 
    name: '', 
    license_plate: '', 
    vin: '',
    fuel_capacity: '',
    insurance_expiry: null,
    tax_road_expiry: null,
    inspection_expiry: null,
    casco_expiry: null,
    copie_conforma_expiry: null
  });
  const [newDriver, setNewDriver] = useState({ 
    username: '', 
    password: '',
    role: 'driver',
    nume: '',  // Numele de familie
    prenume: '',  // Prenumele
    cnp: '',  // Cod Numeric Personal
    id_expiry: null,
    driver_licence_expiry: null,
    work_medicine_expiry: null,
    catg: '',
    norma_ore: 8,
    zile_co_anuale: 21,  // Zile CO anuale din contract
    program_start: '08:00',
    program_sfarsit: '17:00',
    program_pauza: true,
    pauza_start: '12:00',
    pauza_sfarsit: '13:00',
    angajat_de_la: null,
    numar_contract: '',
    functie: ''
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  // ============ PERMISSION HELPERS (la nivel de componentă) ============
  const userPermissions = auth.user?.permissions || [];
  const isAdmin = auth.user?.role === 'admin';
  const hasPermission = (perm) => isAdmin || userPermissions.includes(perm);
  const hasAnyPermission = (...perms) => isAdmin || perms.some(p => userPermissions.includes(p));
  
  // Determină primul tab disponibil bazat pe permisiuni
  const getDefaultTab = () => {
    if (hasAnyPermission('masini.vizualizare', 'alerte.vizualizare')) return 'alerte';
    if (hasPermission('curse.vizualizare')) return 'curse-active';
    if (hasPermission('rapoarte.vizualizare')) return 'reports';
    if (hasPermission('angajati.vizualizare')) return 'drivers';
    if (hasPermission('pontaj.vizualizare')) return 'pontaj';
    if (hasPermission('concedii.vizualizare')) return 'concedii';
    if (hasPermission('trasabilitate.vizualizare')) return 'trasabilitate';
    if (hasPermission('roluri.vizualizare')) return 'admin-roles';
    if (hasPermission('setari.editare')) return 'edit-site';
    return 'alerte'; // fallback
  };
  
  const [activeTab, setActiveTabRaw] = useState(getDefaultTab());
  // Wrapper to scroll to top when switching tabs
  const setActiveTab = (tab) => {
    setActiveTabRaw(tab);
    window.scrollTo(0, 0);
  };
  const [alerts, setAlerts] = useState([]);
  const [furnizorFocus, setFurnizorFocus] = useState(null);
  const [fuelAlerts, setFuelAlerts] = useState([]);
  const [selectedDriverFilter, setSelectedDriverFilter] = useState('all');
  const [employeeSearchFilter, setEmployeeSearchFilter] = useState('');
  const [employeeStatusFilter, setEmployeeStatusFilter] = useState('active'); // 'active', 'former', 'all'
  
  // Import Excel states
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importMappings, setImportMappings] = useState({});
  
  // Roles management states
  const [rolesData, setRolesData] = useState({ roles: [], available_permissions: {} });
  const [selectedRole, setSelectedRole] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState([]);
  const [savingRoles, setSavingRoles] = useState(false);
  const [showCreateRoleDialog, setShowCreateRoleDialog] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleId, setNewRoleId] = useState('');
  
  const [expandedCar, setExpandedCar] = useState(null);

  // Echipamente state
  const [equipments, setEquipments] = useState([]);
  const [showAddEquipmentDialog, setShowAddEquipmentDialog] = useState(false);
  const [showEditEquipmentDialog, setShowEditEquipmentDialog] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [newEquipment, setNewEquipment] = useState({ name: '', type: '', itp_expiry: '', nr_iscir: '', nr_fabricatie: '', an_fabricatie: '' });
  const [expandedEquipment, setExpandedEquipment] = useState(null);
  const [equipmentRevisions, setEquipmentRevisions] = useState([]);
  const [showAddRevisionDialog, setShowAddRevisionDialog] = useState(false);
  const [newRevision, setNewRevision] = useState({ date: '', type: 'ITP', cost: '', notes: '', next_revision_date: '', category: 'revizie', documents: [] });
  const [viewingEquipmentDocs, setViewingEquipmentDocs] = useState(null); // {title, documents: [dataUrl, ...]}
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [tripSheets, setTripSheets] = useState({});
  const [closingReasons, setClosingReasons] = useState({});
  const [closingReasonsList, setClosingReasonsList] = useState([]);
  const [openEditTripDialog, setOpenEditTripDialog] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [openAddTripDialog, setOpenAddTripDialog] = useState(false);
  const [newTrip, setNewTrip] = useState({
    car_id: '',
    driver_id: '',
    driver_name: '',
    start_time: '',
    end_time: '',
    start_km: '',
    end_km: '',
    route: '',
    scop_deplasare: '',
    departure_location: '',
    destination: '',
    notes: '',
    closing_reason: '',
    fuel_liters: '',
    fuel_cost: ''
  });
  // Ref to always access the latest newTrip value (avoids stale closures)
  const newTripRef = useRef(newTrip);
  newTripRef.current = newTrip;
  const [openFuelLevelDialog, setOpenFuelLevelDialog] = useState(false);
  const [fuelLevelData, setFuelLevelData] = useState({
    car_id: '',
    month: selectedMonth,
    year: selectedYear,
    start_level: '',
    end_level: ''
  });
  const [openCentralizatorDialog, setOpenCentralizatorDialog] = useState(false);
  const [centralizatorMonth, setCentralizatorMonth] = useState(new Date().getMonth() + 1);
  const [centralizatorYear, setCentralizatorYear] = useState(new Date().getFullYear());
  
  // Centralizator Angajați states
  const [openCentralizatorAngajatiDialog, setOpenCentralizatorAngajatiDialog] = useState(false);
  const [centralizatorAngajatiMonth, setCentralizatorAngajatiMonth] = useState(new Date().getMonth() + 1);
  const [centralizatorAngajatiYear, setCentralizatorAngajatiYear] = useState(new Date().getFullYear());
  
  // CSV Import states
  const [openCSVUploadDialog, setOpenCSVUploadDialog] = useState(false);
  const [openCSVPreviewDialog, setOpenCSVPreviewDialog] = useState(false);
  const [csvFile, setCSVFile] = useState(null);
  const [csvPreviewData, setCSVPreviewData] = useState(null);
  const [importBatches, setImportBatches] = useState([]);
  const [csvUploading, setCSVUploading] = useState(false);
  const [showImportHistory, setShowImportHistory] = useState(false);
  
  // Pontaj states
  const [pontajMonth, setPontajMonth] = useState(new Date().getMonth() + 1);
  const [pontajYear, setPontajYear] = useState(new Date().getFullYear());
  const [pontajData, setPontajData] = useState({});
  const [pontajLoading, setPontajLoading] = useState(false);
  const [pontajDialog, setPontajDialog] = useState({ 
    open: false, 
    employeeId: null, 
    employeeName: '', 
    day: null, 
    isWeekend: false,
    selectedType: null,  // '8', 'CO', 'CM', 'CNC', 'CD', 'NM', 'DEM', 'X'
    useInterval: false,
    endDay: null,
    intervalDocument: null,
    intervalDocumentName: null,
    customProgram: false,
    customProgramStart: '08:00',
    customProgramSfarsit: '17:00',
    customPauza: false,
    customPauzaStart: '12:00',
    customPauzaSfarsit: '13:00',
    currentValue: null,
    pending: false
  });
  const [pontajEndDateDialog, setPontajEndDateDialog] = useState({ open: false, userId: null, userName: '', currentDate: null });
  const [selectedEndMonth, setSelectedEndMonth] = useState(new Date().getMonth() + 1);
  const [selectedEndYear, setSelectedEndYear] = useState(new Date().getFullYear());
  const [pontajEditMode, setPontajEditMode] = useState(false);
  const [documentPreviewDialog, setDocumentPreviewDialog] = useState({ open: false, employeeId: null, employeeName: '', day: null, filename: null, blobUrl: null, loading: false });
  
  // Evidență ore - folosește aceleași date ca Pontaj (oglindă)
  const [documentUploading, setDocumentUploading] = useState(false);
  const [intervalSaving, setIntervalSaving] = useState(false);
  
  // Sărbători legale
  const [sarbatoriDialog, setSarbatoriDialog] = useState(false);
  const [sarbatoriList, setSarbatoriList] = useState([]);
  const [sarbatoriLoading, setSarbatoriLoading] = useState(false);
  const [newSarbatoare, setNewSarbatoare] = useState({ zi: '', luna: '', nume: '', an: '' });

  // Tichete Masă
  const [ticheteMasaDialog, setTicheteMasaDialog] = useState(false);
  const [valoareTichet, setValoareTichet] = useState(40);
  const [ticheteSummary, setTicheteSummary] = useState({ totalAngajati: 0, totalTichete: 0, valoareTotala: 0 });

  // Concedii (Vacations) Module
  const [concediiYear, setConcediiYear] = useState(new Date().getFullYear());
  const [concediiSubTab, setConcediiSubTab] = useState('CO'); // CO, CM, CFP, CNC, CD
  const [concediiFilter, setConcediiFilter] = useState('activi'); // activi, fosti, toti
  const [concediiData, setConcediiData] = useState({ year: null, employees: [] });
  const [concediiLoading, setConcediiLoading] = useState(false);
  const [concediiDetailDialog, setConcediiDetailDialog] = useState({ 
    open: false, 
    employeeId: null, 
    employeeName: '', 
    month: null, 
    tip: 'CO',
    intervals: [],
    totalDays: 0
  });
  const [soldEditDialog, setSoldEditDialog] = useState({
    open: false,
    employeeId: null,
    employeeName: '',
    soldValue: 0
  });

  // Programator CO - calendar vizual concedii
  const [programatorMonth, setProgramatorMonth] = useState(new Date().getMonth() + 1);
  const [programatorYear, setProgramatorYear] = useState(new Date().getFullYear());
  const [programatorData, setProgramatorData] = useState([]);
  const [programatorLoading, setProgramatorLoading] = useState(false);
  const [programatorSarbatori, setProgramatorSarbatori] = useState([]);

  // Trasabilitate (Audit Log) Module
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditFilters, setAuditFilters] = useState({
    user: '',
    action: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [auditActions, setAuditActions] = useState([]);
  const [auditUsers, setAuditUsers] = useState([]);

  // Landing Page Content - Editare Site
  const [landingContent, setLandingContent] = useState({
    titlu: 'CRIMACO TRP',
    subtitlu: 'Servicii profesionale de logistică și curierat',
    descriere: '',
    servicii: [
      { titlu: 'Curierat Rapid', descriere: 'Livrări rapide și sigure în toată țara' },
      { titlu: 'Logistică', descriere: 'Soluții complete de depozitare și distribuție' },
      { titlu: 'Transport Marfă', descriere: 'Transport rutier intern și internațional' }
    ],
    contact: { email: '', adresa: '' }
  });
  const [landingLoading, setLandingLoading] = useState(false);
  const [contactMessagesCount, setContactMessagesCount] = useState(0);

  // Function to load document for preview
  const loadDocumentPreview = async (employeeId, day, filename, value) => {
    setDocumentPreviewDialog({
      open: true,
      employeeId,
      employeeName: drivers.find(d => d.id === employeeId)?.username || '',
      day,
      filename,
      value,
      blobUrl: null,
      loading: true
    });
    
    try {
      const response = await axios.get(
        `${API}/pontaj/document/${employeeId}/${pontajYear}/${pontajMonth}/${day}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const blobUrl = URL.createObjectURL(response.data);
      setDocumentPreviewDialog(prev => ({
        ...prev,
        blobUrl,
        loading: false
      }));
    } catch (error) {
      console.error('Error loading document:', error);
      const errorMsg = error.response?.data?.detail || 'Nu s-a putut încărca documentul';
      
      // Check if it's a missing file error - backend auto-cleans the reference
      if (errorMsg.includes('Document file not found')) {
        toast.info('Fișierul documentului nu mai există. Referința a fost ștearsă automat.');
        // Refresh pontaj data to remove the blue dot
        fetchPontajData();
      } else {
        toast.error(errorMsg);
      }
      
      // Close the dialog on error
      setDocumentPreviewDialog({ open: false, employeeId: null, employeeName: '', day: null, filename: null, blobUrl: null, loading: false, value: null });
    }
  };
  
  // Car search
  const [carSearchQuery, setCarSearchQuery] = useState('');
  
  // Active trips monitoring
  const [activeTrips, setActiveTrips] = useState([]);
  const [todayTrips, setTodayTrips] = useState([]);
  const [openCloseTripDialog, setOpenCloseTripDialog] = useState(false);
  const [closingTrip, setClosingTrip] = useState(null);
  const [closeTripData, setCloseTripData] = useState({
    end_km: '',
    fuel_liters: '',
    fuel_cost: '',
    closing_reason: 'final_day'
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${auth.token}` },
  });

  // Interpretează o dată de expirare ÎNTOTDEAUNA în fusul orar al firmei (Europe/Bucharest),
  // indiferent de fusul calculatorului userului, ca toți să vadă aceeași zi.
  // Acceptă atât "2027-01-16" cât și "2026-10-25T22:00:00.000Z".
  const parseBusinessDate = (val) => {
    if (!val) return null;
    const toLocalParts = (s) => {
      const [y, m, d] = s.split('-').map(Number);
      return new Date(y, m - 1, d);
    };
    if (val instanceof Date) {
      if (isNaN(val.getTime())) return null;
      return toLocalParts(val.toLocaleDateString('en-CA', { timeZone: 'Europe/Bucharest' }));
    }
    const str = String(val).trim();
    // Dată simplă fără oră → folosim direct componentele (fără decalaj de fus)
    const dateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    // Dată cu oră/fus → convertim momentul la ziua calendaristică din Europe/Bucharest
    const dt = new Date(str);
    if (!isNaN(dt.getTime())) {
      return toLocalParts(dt.toLocaleDateString('en-CA', { timeZone: 'Europe/Bucharest' }));
    }
    const fallback = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (fallback) return new Date(Number(fallback[1]), Number(fallback[2]) - 1, Number(fallback[3]));
    return null;
  };

  const checkExpiryAlerts = (carsData, driversData) => {
    const alertsList = [];
    
    // Folosim doar data (fără ora) pentru comparații corecte
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Setăm la începutul zilei
    
    const carExpiryFields = [
      { key: 'insurance_expiry', label: 'Asigurare' },
      { key: 'tax_road_expiry', label: 'Taxă Drum' },
      { key: 'inspection_expiry', label: 'ITP' },
      { key: 'casco_expiry', label: 'CASCO' },
      { key: 'copie_conforma_expiry', label: 'Copie Conformă' }
    ];

    const driverExpiryFields = [
      { key: 'id_expiry', label: 'Carte de Identitate' },
      { key: 'driver_licence_expiry', label: 'Permis de Conducere' },
      { key: 'work_medicine_expiry', label: 'Medicina Muncii' }
    ];

    // Check car expiries
    carsData.forEach(car => {
      carExpiryFields.forEach(field => {
        // Skip copie_conforma_expiry dacă mașina are copie_conforma_nenecesar
        if (field.key === 'copie_conforma_expiry' && car.copie_conforma_nenecesar) {
          return;
        }
        
        if (car[field.key]) {
          const expiryDate = parseBusinessDate(car[field.key]);
          if (!expiryDate) return;
          expiryDate.setHours(0, 0, 0, 0); // Normalizăm și data expirării
          
          // Calculăm diferența în zile întregi
          // Documentul e VALABIL inclusiv în ziua expirării
          // Expiră abia din ziua URMĂTOARE
          const diffTime = expiryDate.getTime() - today.getTime();
          const daysUntilExpiry = Math.round(diffTime / (1000 * 60 * 60 * 24));

          // Afișăm alertă pentru documente care expiră în următoarele 60 zile SAU sunt deja expirate
          if (daysUntilExpiry <= 60) {
            let urgency = 'low';
            // EXPIRAT = ziua de azi e DUPĂ data expirării (daysUntilExpiry < 0)
            // Dacă daysUntilExpiry = 0, înseamnă că azi e ultima zi de valabilitate
            if (daysUntilExpiry < 0) urgency = 'expired';
            else if (daysUntilExpiry <= 10) urgency = 'high';
            else if (daysUntilExpiry <= 30) urgency = 'medium';
            else urgency = 'warning';

            // Verifică dacă există un document viitor programat pentru acest câmp
            const carPendingDocs = pendingDocuments[car.id] || [];
            const pendingDoc = carPendingDocs.find(
              doc => doc.field_name === field.key && doc.status === 'pending'
            );
            
            // Dacă există document viitor, schimbă urgency în 'scheduled'
            const finalUrgency = pendingDoc ? 'scheduled' : urgency;

            alertsList.push({
              id: `car-${car.id}-${field.key}`,
              type: 'car',
              carId: car.id,
              carName: car.name,
              licensePlate: car.license_plate,
              item: field.label,
              fieldKey: field.key,
              expiryDate: expiryDate,
              daysUntilExpiry: daysUntilExpiry,
              urgency: finalUrgency,
              hasPendingDoc: !!pendingDoc,
              pendingDoc: pendingDoc || null
            });
          }
        }
      });
    });

    // Check driver expiries
    driversData.forEach(driver => {
      driverExpiryFields.forEach(field => {
        if (driver[field.key]) {
          const expiryDate = parseBusinessDate(driver[field.key]);
          if (!expiryDate) return;
          expiryDate.setHours(0, 0, 0, 0); // Normalizăm data expirării
          
          const diffTime = expiryDate.getTime() - today.getTime();
          const daysUntilExpiry = Math.round(diffTime / (1000 * 60 * 60 * 24));

          if (daysUntilExpiry <= 60) {
            let urgency = 'low';
            // EXPIRAT = ziua de azi e DUPĂ data expirării (daysUntilExpiry < 0)
            if (daysUntilExpiry < 0) urgency = 'expired';
            else if (daysUntilExpiry <= 10) urgency = 'high';
            else if (daysUntilExpiry <= 30) urgency = 'medium';
            else urgency = 'warning';

            alertsList.push({
              id: `driver-${driver.id}-${field.key}`,
              type: 'driver',
              driverName: driver.username,
              item: field.label,
              expiryDate: expiryDate,
              daysUntilExpiry: daysUntilExpiry,
              urgency: urgency,
              hasPendingDoc: false,
              pendingDoc: null
            });
          }
        }
      });
    });

    // Sort by urgency: scheduled (green) last, then RED (expired) first, then ORANGE (high), then YELLOW (medium/warning)
    const urgencyOrder = { 'expired': 0, 'high': 1, 'medium': 2, 'warning': 3, 'low': 4, 'scheduled': 5 };
    alertsList.sort((a, b) => {
      // First sort by urgency level
      if (urgencyOrder[a.urgency] !== urgencyOrder[b.urgency]) {
        return urgencyOrder[a.urgency] - urgencyOrder[b.urgency];
      }
      // Within same urgency, sort by days (expired items by most overdue first)
      return a.daysUntilExpiry - b.daysUntilExpiry;
    });

    setAlerts(alertsList);
  };

  // Fetch contact messages count for badge (only unread)
  const fetchContactMessagesCount = async () => {
    try {
      const response = await axios.get(`${API}/contact-messages/unread-count`, getAuthHeaders());
      setContactMessagesCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch contact messages count:', error);
    }
  };

  // Fetch suspicious fuel alerts
  const fetchFuelAlerts = async () => {
    try {
      const response = await axios.get(`${API}/alerts/fuel-suspicious`, getAuthHeaders());
      setFuelAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error fetching fuel alerts:', error);
    }
  };

  // Verificare și activare automată a documentelor programate (o dată pe zi)
  const checkAndActivatePendingDocuments = async () => {
    try {
      const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
      const lastCheck = localStorage.getItem('lastPendingDocsCheck');
      
      // Dacă am verificat deja azi, nu mai verificăm
      if (lastCheck === today) {
        return;
      }
      
      // Verifică dacă userul este admin (doar adminii pot activa)
      if (auth?.user?.role !== 'admin') {
        return;
      }
      
      console.log('[Auto-Activation] Verificare documente programate...');
      
      const response = await axios.post(`${API}/cars/activate-pending-documents`, {}, getAuthHeaders());
      
      // Salvează data verificării
      localStorage.setItem('lastPendingDocsCheck', today);
      
      // Dacă s-au activat documente, afișează notificare și reîncarcă datele
      if (response.data.activated_count > 0) {
        toast.success(
          `${response.data.activated_count} document${response.data.activated_count > 1 ? 'e' : ''} programat${response.data.activated_count > 1 ? 'e' : ''} activat${response.data.activated_count > 1 ? 'e' : ''} automat`,
          { duration: 5000 }
        );
        
        // Reîncarcă mașinile pentru a actualiza alertele
        await fetchCars();
      }
    } catch (error) {
      // Eroare silențioasă - nu deranjăm utilizatorul
      console.error('[Auto-Activation] Eroare:', error);
    }
  };

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        // Load critical data first
        await Promise.all([
          fetchCars(),
          fetchDrivers(),
          fetchClosingReasons(),
        ]);
        // Then load secondary data
        if (isMounted) {
          await Promise.all([
            fetchTrips(),
            fetchActiveTrips(),
            fetchContactMessagesCount(),
            fetchFleetRoutes(),
            fetchFuelAlerts()
          ]);
        }
        
        // După încărcarea datelor, verifică și activează documentele programate (o dată pe zi)
        if (isMounted) {
          checkAndActivatePendingDocuments();
        }
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Auto-refresh trips when driver filter changes
  useEffect(() => {
    if (activeTab === 'reports') {
      fetchTrips();
    }
  }, [selectedDriverFilter, startDate, endDate]);

  // Load pontaj data when month/year changes or tab is opened
  useEffect(() => {
    if (activeTab === 'pontaj') {
      fetchPontajData();
    }
  }, [activeTab, pontajMonth, pontajYear]);

  // Recalculate alerts whenever cars, drivers, or pendingDocuments change
  useEffect(() => {
    if (cars.length > 0 || drivers.length > 0) {
      checkExpiryAlerts(cars, drivers);
    }
  }, [cars, drivers, pendingDocuments]);

  // Load roles data when admin-roles tab is opened
  useEffect(() => {
    if (activeTab === 'admin-roles') {
      fetchRoles();
    }
  }, [activeTab]);

  // Fetch roles when admin tab is active

  // Load all trip sheets when Foi de Parcurs tab is opened (for km gap alerts)
  useEffect(() => {
    const loadAllSheets = async () => {
      if (activeTab === 'foi-parcurs' && cars && cars.length > 0) {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const headers = { headers: { Authorization: `Bearer ${token}` } };
        
        try {
          const promises = cars.map(car => 
            axios.get(
              `${API}/cars/${car.id}/trip-sheets?month=${selectedMonth}&year=${selectedYear}`,
              headers
            ).then(response => ({ carId: car.id, data: response.data }))
             .catch(() => ({ carId: car.id, data: null }))
          );
          
          const results = await Promise.all(promises);
          const newTripSheets = {};
          results.forEach(result => {
            if (result.data) {
              newTripSheets[result.carId] = result.data;
            }
          });
          setTripSheets(newTripSheets);
        } catch (error) {
          console.error('Failed to fetch all trip sheets:', error);
        }
      }
    };
    loadAllSheets();
  }, [activeTab, cars.length, selectedMonth, selectedYear]);

  const fetchCars = async () => {
    try {
      const response = await axios.get(`${API}/cars`, getAuthHeaders());
      const carsData = response.data;
      setCars(carsData);

      // Fetch equipments too
      try {
        const eqRes = await axios.get(`${API}/equipments`, getAuthHeaders());
        setEquipments(eqRes.data || []);
      } catch {}
      
      // Pre-load trip sheets for km gap alerts (if on foi-parcurs tab)
      if (carsData.length > 0) {
        const token = localStorage.getItem('token');
        if (token) {
          const headers = { headers: { Authorization: `Bearer ${token}` } };
          const now = new Date();
          const month = now.getMonth() + 1;
          const year = now.getFullYear();
          
          const promises = carsData.map(car => 
            axios.get(
              `${API}/cars/${car.id}/trip-sheets?month=${month}&year=${year}`,
              headers
            ).then(res => ({ carId: car.id, data: res.data }))
             .catch(() => ({ carId: car.id, data: null }))
          );
          
          const results = await Promise.all(promises);
          const sheets = {};
          results.forEach(r => {
            if (r.data) sheets[r.carId] = r.data;
          });
          setTripSheets(sheets);
        }
      }
    } catch (error) {
      toast.error('Nu s-au putut încărca mașinile');
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API}/users`, getAuthHeaders());
      // Show admin users only to other admins (needed for 2FA email configuration)
      // Non-admin users should not see admin accounts
      const isCurrentUserAdmin = auth.user?.roles?.includes('admin') || auth.user?.role === 'admin';
      const driversList = isCurrentUserAdmin 
        ? response.data 
        : response.data.filter(u => !u.roles?.includes('admin') && u.role !== 'admin');
      setDrivers(driversList);
      // Alerts will be recalculated automatically by useEffect
    } catch (error) {
      toast.error('Nu s-au putut încărca angajații');
    }
  };

  // Fetch fleet routes
  const fetchFleetRoutes = async () => {
    try {
      const response = await axios.get(`${API}/routes`, getAuthHeaders());
      setFleetRoutes(response.data.routes || []);
    } catch (error) {
      console.error('Failed to fetch routes:', error);
    }
  };

  // Add new route
  const handleAddRoute = async (routeName, routeScop) => {
    try {
      const response = await axios.post(`${API}/routes`, { name: routeName, scop: routeScop || '' }, getAuthHeaders());
      setFleetRoutes(response.data.routes || []);
      toast.success('Traseu adăugat cu succes');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nu s-a putut adăuga traseul');
      return false;
    }
  };

  // Delete route
  const handleDeleteRoute = async (routeName) => {
    try {
      const response = await axios.delete(`${API}/routes/${encodeURIComponent(routeName)}`, getAuthHeaders());
      setFleetRoutes(response.data.routes || []);
      toast.success('Traseu șters cu succes');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nu s-a putut șterge traseul');
      return false;
    }
  };

  // Edit route
  const handleEditRoute = async (originalName, newName, newScop) => {
    try {
      const response = await axios.put(
        `${API}/routes/${encodeURIComponent(originalName)}`, 
        { name: newName, scop: newScop || '' }, 
        getAuthHeaders()
      );
      setFleetRoutes(response.data.routes || []);
      toast.success('Traseu actualizat cu succes');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nu s-a putut actualiza traseul');
      return false;
    }
  };

  // ============= CLOSING REASONS =============
  
  // Fetch closing reasons
  const fetchClosingReasons = async () => {
    try {
      const response = await axios.get(`${API}/closing-reasons`, getAuthHeaders());
      setClosingReasonsList(response.data.reasons || []);
    } catch (error) {
      console.error('Failed to fetch closing reasons:', error);
    }
  };

  // Add new closing reason
  const handleAddClosingReason = async (reasonId, reasonName) => {
    try {
      const response = await axios.post(`${API}/closing-reasons`, { id: reasonId, name: reasonName }, getAuthHeaders());
      setClosingReasonsList(response.data.reasons || []);
      toast.success('Motiv adăugat cu succes');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nu s-a putut adăuga motivul');
      return false;
    }
  };

  // Edit closing reason
  const handleEditClosingReason = async (originalId, newId, newName) => {
    try {
      const response = await axios.put(
        `${API}/closing-reasons/${encodeURIComponent(originalId)}`, 
        { id: newId, name: newName }, 
        getAuthHeaders()
      );
      setClosingReasonsList(response.data.reasons || []);
      toast.success('Motiv actualizat cu succes');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nu s-a putut actualiza motivul');
      return false;
    }
  };

  // Delete closing reason
  const handleDeleteClosingReason = async (reasonId) => {
    try {
      const response = await axios.delete(`${API}/closing-reasons/${encodeURIComponent(reasonId)}`, getAuthHeaders());
      setClosingReasonsList(response.data.reasons || []);
      toast.success('Motiv șters cu succes');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nu s-a putut șterge motivul');
      return false;
    }
  };

  // Fetch roles and permissions
  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`, getAuthHeaders());
      setRolesData(response.data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  // Save role permissions
  const saveRolePermissions = async (roleId, permissions) => {
    try {
      setSavingRoles(true);
      await axios.put(`${API}/roles/${roleId}/permissions`, { permissions }, getAuthHeaders());
      toast.success('Permisiunile au fost salvate cu succes');
      fetchRoles(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nu s-au putut salva permisiunile');
    } finally {
      setSavingRoles(false);
    }
  };

  // Reset role permissions to default
  const resetRolePermissions = async (roleId) => {
    if (!window.confirm('Sigur doriți să resetați permisiunile la valorile implicite?')) return;
    try {
      setSavingRoles(true);
      const response = await axios.delete(`${API}/roles/${roleId}/permissions`, getAuthHeaders());
      toast.success('Permisiunile au fost resetate');
      setEditingPermissions(response.data.permissions);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nu s-au putut reseta permisiunile');
    } finally {
      setSavingRoles(false);
    }
  };

  // Create new role
  const createRole = async (roleName, roleIdInput) => {
    if (!roleName?.trim()) {
      toast.error('Numele rolului este obligatoriu');
      return false;
    }
    
    const roleId = roleIdInput?.trim() || roleName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    
    try {
      setSavingRoles(true);
      const response = await axios.post(`${API}/roles`, {
        id: roleId,
        nume: roleName.trim()
      }, getAuthHeaders());
      
      toast.success(response.data.message);
      fetchRoles();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nu s-a putut crea rolul');
      return false;
    } finally {
      setSavingRoles(false);
    }
  };

  // Delete custom role
  const deleteRole = async (roleId, roleName) => {
    if (!window.confirm(`Sigur doriți să ștergeți rolul "${roleName}"?\n\nAtenție: Asigurați-vă că nu există utilizatori cu acest rol.`)) {
      return;
    }
    
    try {
      setSavingRoles(true);
      const response = await axios.delete(`${API}/roles/${roleId}`, getAuthHeaders());
      toast.success(response.data.message);
      setSelectedRole(null);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nu s-a putut șterge rolul');
    } finally {
      setSavingRoles(false);
    }
  };

  const fetchTrips = async () => {
    try {
      let url = `${API}/trips`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate.toISOString()}`);
      if (endDate) params.push(`end_date=${endDate.toISOString()}`);
      if (selectedDriverFilter && selectedDriverFilter !== 'all') params.push(`driver_id=${selectedDriverFilter}`);
      if (params.length) url += '?' + params.join('&');

      const response = await axios.get(url, getAuthHeaders());
      setTrips(response.data);
    } catch (error) {
      toast.error('Nu s-au putut încărca cursele');
    }
  };

  // Pontaj functions
  const fetchPontajData = async () => {
    try {
      setPontajLoading(true);
      const response = await axios.get(`${API}/pontaj/${pontajYear}/${pontajMonth}`, getAuthHeaders());
      // Convert string keys to numbers for days - data now contains {value, has_document, document_filename}
      const converted = {};
      Object.keys(response.data).forEach(empId => {
        converted[empId] = {};
        Object.keys(response.data[empId]).forEach(day => {
          converted[empId][parseInt(day)] = response.data[empId][day];
        });
      });
      setPontajData(converted);
    } catch (error) {
      console.error('Failed to fetch pontaj data');
      setPontajData({});
    } finally {
      setPontajLoading(false);
    }
  };

  // Sărbători legale functions
  const fetchSarbatori = async () => {
    try {
      setSarbatoriLoading(true);
      const response = await axios.get(`${API}/sarbatori`, getAuthHeaders());
      setSarbatoriList(response.data);
    } catch (error) {
      console.error('Failed to fetch sarbatori');
      setSarbatoriList([]);
    } finally {
      setSarbatoriLoading(false);
    }
  };

  const fetchSarbatoriLuna = async (year, month) => {
    try {
      const response = await axios.get(`${API}/sarbatori/${year}/${month}`, getAuthHeaders());
      return response.data;
    } catch (error) {
      console.error('Failed to fetch sarbatori luna');
      return [];
    }
  };

  const addSarbatoare = async () => {
    if (!newSarbatoare.zi || !newSarbatoare.luna || !newSarbatoare.nume) {
      toast.error('Completați toate câmpurile obligatorii');
      return;
    }
    try {
      await axios.post(`${API}/sarbatori`, {
        zi: parseInt(newSarbatoare.zi),
        luna: parseInt(newSarbatoare.luna),
        nume: newSarbatoare.nume,
        an: newSarbatoare.an ? parseInt(newSarbatoare.an) : null
      }, getAuthHeaders());
      toast.success('Sărbătoare adăugată cu succes');
      setNewSarbatoare({ zi: '', luna: '', nume: '', an: '' });
      fetchSarbatori();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la adăugarea sărbătorii');
    }
  };

  const deleteSarbatoare = async (id) => {
    try {
      await axios.delete(`${API}/sarbatori/${id}`, getAuthHeaders());
      toast.success('Sărbătoare ștearsă');
      fetchSarbatori();
    } catch (error) {
      toast.error('Eroare la ștergerea sărbătorii');
    }
  };

  // State pentru sărbătorile lunii curente (pentru afișare în pontaj)
  const [sarbatoriLunaCurenta, setSarbatoriLunaCurenta] = useState([]);

  // Fetch sărbătorile pentru luna selectată în pontaj
  useEffect(() => {
    const loadSarbatoriLuna = async () => {
      const sarb = await fetchSarbatoriLuna(pontajYear, pontajMonth);
      setSarbatoriLunaCurenta(sarb);
    };
    if (activeTab === 'pontaj' || activeTab === 'evidenta-ore') {
      loadSarbatoriLuna();
    }
  }, [pontajYear, pontajMonth, activeTab]);

  // Helper pentru a verifica dacă o zi este sărbătoare legală
  const isSarbatoare = (day) => {
    return sarbatoriLunaCurenta.some(s => s.zi === day);
  };

  const getSarbatoareNume = (day) => {
    const sarb = sarbatoriLunaCurenta.find(s => s.zi === day);
    return sarb ? sarb.nume : '';
  };

  // Helper to get pontaj value (handles both old format and new format with document info)
  const getPontajValue = (data) => {
    if (data === null || data === undefined) return null;
    if (typeof data === 'object' && data.value !== undefined) return data.value;
    return data; // backwards compatibility with old format
  };

  // Helper to get display name (nume + prenume if available, otherwise username)
  const getDisplayName = (driver) => {
    const nume = driver?.nume || '';
    const prenume = driver?.prenume || '';
    const fullName = `${nume} ${prenume}`.trim();
    return fullName || driver?.username || '';
  };

  const getPontajHasDocument = (data) => {
    if (data === null || data === undefined) return false;
    if (typeof data === 'object') return data.has_document || false;
    return false;
  };

  const getPontajDocumentFilename = (data) => {
    if (data === null || data === undefined) return null;
    if (typeof data === 'object') return data.document_filename || null;
    return null;
  };

  const savePontajEntry = async (employeeId, day, value, programStart = null, programSfarsit = null, pauzaStart = null, pauzaSfarsit = null, pending = null) => {
    try {
      const payload = {
        employee_id: employeeId,
        year: pontajYear,
        month: pontajMonth,
        day: day,
        value: value
      };
      // Flag vizual "în așteptare"
      if (pending !== null) {
        payload.pending = pending;
      }
      // Adaugă programul personalizat dacă este specificat
      if (programStart && programSfarsit) {
        payload.program_start = programStart;
        payload.program_sfarsit = programSfarsit;
      }
      // Adaugă pauza personalizată dacă este specificată
      if (pauzaStart && pauzaSfarsit) {
        payload.pauza_start = pauzaStart;
        payload.pauza_sfarsit = pauzaSfarsit;
      }
      await axios.post(`${API}/pontaj`, payload, getAuthHeaders());
    } catch (error) {
      toast.error('Nu s-a putut salva pontajul');
    }
  };

  const exportPontajPDF = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/pontaj/export/pdf/${pontajYear}/${pontajMonth}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const monthNames = ["", "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
                          "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];
      const filename = `Pontaj_${monthNames[pontajMonth]}_${pontajYear}.pdf`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exportat cu succes!');
    } catch (error) {
      toast.error('Eroare la exportul PDF');
    } finally {
      setLoading(false);
    }
  };

  const exportPontajExcel = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/pontaj/export/excel/${pontajYear}/${pontajMonth}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const monthNames = ["", "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
                          "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];
      const filename = `Pontaj_${monthNames[pontajMonth]}_${pontajYear}.xlsx`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel exportat cu succes!');
    } catch (error) {
      toast.error('Eroare la exportul Excel');
    } finally {
      setLoading(false);
    }
  };

  // Tichete Masă Dialog
  const openTicheteMasaExport = async () => {
    try {
      // Get saved valoare tichet
      const response = await axios.get(`${API}/settings/valoare-tichet`, getAuthHeaders());
      setValoareTichet(response.data.valoare || 40);
      
      // Calculate summary from current pontaj data
      let totalTichete = 0;
      let totalAngajati = 0;
      
      const today = new Date();
      const isCurrentMonth = pontajYear === today.getFullYear() && pontajMonth === today.getMonth() + 1;
      const daysInMonth = new Date(pontajYear, pontajMonth, 0).getDate();
      
      const pontajEmployees = drivers.filter(d => {
        if (d.in_pontaj === false) return false;
        if (d.angajat_de_la) {
          const hireDate = new Date(d.angajat_de_la);
          if (pontajYear < hireDate.getUTCFullYear() || (pontajYear === hireDate.getUTCFullYear() && pontajMonth < hireDate.getUTCMonth() + 1)) {
            return false;
          }
        }
        if (d.pontaj_pana_la) {
          const endDate = new Date(d.pontaj_pana_la);
          if (pontajYear > endDate.getUTCFullYear() || (pontajYear === endDate.getUTCFullYear() && pontajMonth > endDate.getUTCMonth() + 1)) {
            return false;
          }
        }
        return true;
      });
      
      pontajEmployees.forEach(driver => {
        const employeePontaj = pontajData[driver.id] || {};
        let empTichete = 0;
        
        // Get hire date info for this employee
        let hireDateDay = null;
        let monthBeforeHire = false;
        if (driver.angajat_de_la) {
          const hireDate = new Date(driver.angajat_de_la);
          if (pontajYear === hireDate.getUTCFullYear() && pontajMonth === hireDate.getUTCMonth() + 1) {
            hireDateDay = hireDate.getUTCDate();
          } else if (pontajYear < hireDate.getUTCFullYear() || (pontajYear === hireDate.getUTCFullYear() && pontajMonth < hireDate.getUTCMonth() + 1)) {
            monthBeforeHire = true;
          }
        }
        
        // Get resignation date info for this employee
        let resignationDay = null;
        if (driver.pontaj_pana_la) {
          const endDate = new Date(driver.pontaj_pana_la);
          if (pontajYear === endDate.getUTCFullYear() && pontajMonth === endDate.getUTCMonth() + 1) {
            resignationDay = endDate.getUTCDate();
          }
        }
        
        // Also check for DEM entry in pontaj to find resignation day
        if (!resignationDay) {
          for (let d = 1; d <= daysInMonth; d++) {
            const dayData = employeePontaj[d];
            const value = getPontajValue(dayData);
            if (value === 'DEM') {
              resignationDay = d;
              break;
            }
          }
        }
        
        for (let d = 1; d <= daysInMonth; d++) {
          const dayDate = new Date(pontajYear, pontajMonth - 1, d);
          const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6;
          const isHoliday = isSarbatoare(d);
          const isFutureDay = isCurrentMonth && d > today.getDate();
          
          // Skip future days
          if (isFutureDay) continue;
          
          // Skip weekends and holidays
          if (isWeekend || isHoliday) continue;
          
          // Skip days before hire date
          const dayBeforeHire = monthBeforeHire || (hireDateDay && d < hireDateDay);
          if (dayBeforeHire) continue;
          
          // Skip days after resignation (demisie)
          if (resignationDay && d > resignationDay) continue;
          
          const dayData = employeePontaj[d];
          const value = getPontajValue(dayData);
          
          // Skip CO, CM, CNC, CD, NM, CFP, X, DEM
          if (value === 'CO' || value === 'CM' || value === 'CNC' || value === 'CD' || value === 'NM' || value === 'CFP' || value === 'X' || value === 'DEM') continue;
          
          empTichete++;
        }
        
        if (empTichete > 0) {
          totalTichete += empTichete;
          totalAngajati++;
        }
      });
      
      setTicheteSummary({
        totalAngajati,
        totalTichete,
        valoareTotala: totalTichete * (response.data.valoare || 40)
      });
      
      setTicheteMasaDialog(true);
    } catch (error) {
      toast.error('Eroare la încărcarea datelor');
    }
  };

  const exportTicheteMasa = async () => {
    try {
      setLoading(true);
      
      // Save valoare tichet first
      await axios.post(`${API}/settings/valoare-tichet`, { valoare: valoareTichet }, getAuthHeaders());
      
      const response = await axios.get(
        `${API}/export/tichete-masa/${pontajYear}/${pontajMonth}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const monthNames = ["", "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
                          "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];
      const filename = `Tichete_Masa_${monthNames[pontajMonth]}_${pontajYear}.xlsx`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setTicheteMasaDialog(false);
      toast.success('Export tichete masă realizat cu succes!');
    } catch (error) {
      toast.error('Eroare la exportul tichetelor de masă');
    } finally {
      setLoading(false);
    }
  };

  // Export Evidență Ore PDF
  const exportEvidentaOrePDF = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/evidenta-ore/export/pdf/${pontajYear}/${pontajMonth}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Evidenta_Ore_${pontajMonth}_${pontajYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exportat cu succes!');
    } catch (error) {
      toast.error('Eroare la exportul PDF');
    } finally {
      setLoading(false);
    }
  };

  // Export Evidență Ore Excel
  const exportEvidentaOreExcel = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/evidenta-ore/export/excel/${pontajYear}/${pontajMonth}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Evidenta_Ore_${pontajMonth}_${pontajYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel exportat cu succes!');
    } catch (error) {
      toast.error('Eroare la exportul Excel');
    } finally {
      setLoading(false);
    }
  };

  // ============ Concedii Functions ============
  const fetchConcediiData = async () => {
    try {
      setConcediiLoading(true);
      const response = await axios.get(`${API}/concedii/sumar/${concediiYear}?filter=${concediiFilter}`, getAuthHeaders());
      setConcediiData(response.data);
    } catch (error) {
      console.error('Failed to fetch concedii data:', error);
      toast.error('Eroare la încărcarea datelor de concediu');
    } finally {
      setConcediiLoading(false);
    }
  };

  const fetchConcediiDetail = async (employeeId, employeeName, month, tip) => {
    try {
      const response = await axios.get(
        `${API}/concedii/detalii/${employeeId}/${concediiYear}/${month}?tip=${tip}`,
        getAuthHeaders()
      );
      // Returnează datele pentru a fi folosite de componentă
      return {
        intervals: response.data.intervals || [],
        totalDays: response.data.total_days || 0
      };
    } catch (error) {
      console.error('Failed to fetch concedii details:', error);
      toast.error('Eroare la încărcarea detaliilor');
      return null;
    }
  };

  const saveSoldConcediu = async (employeeId, soldValue) => {
    try {
      await axios.post(`${API}/concedii/sold`, {
        employee_id: employeeId,
        an: concediiYear,
        sold_an_anterior: soldValue
      }, getAuthHeaders());
      toast.success('Sold actualizat cu succes');
      fetchConcediiData();
    } catch (error) {
      toast.error('Eroare la salvarea soldului');
    }
  };

  const exportConcediiPDF = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/concedii/export/pdf/${concediiYear}?tip=${concediiSubTab}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const tipNames = { CO: 'Concedii_Odihna', CM: 'Concedii_Medicale', CFP: 'Concedii_Fara_Plata', CNC: 'Concediu_Nastere_Copil', CD: 'Concediu_Deces' };
      const filename = `${tipNames[concediiSubTab] || 'Concedii'}_${concediiYear}.pdf`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exportat cu succes!');
    } catch (error) {
      toast.error('Eroare la exportul PDF');
    } finally {
      setLoading(false);
    }
  };

  const exportConcediiExcel = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/concedii/export/excel/${concediiYear}?tip=${concediiSubTab}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const tipNames = { CO: 'Concedii_Odihna', CM: 'Concedii_Medicale', CFP: 'Concedii_Fara_Plata', CNC: 'Concediu_Nastere_Copil', CD: 'Concediu_Deces' };
      const filename = `${tipNames[concediiSubTab] || 'Concedii'}_${concediiYear}.xlsx`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel exportat cu succes!');
    } catch (error) {
      toast.error('Eroare la exportul Excel');
    } finally {
      setLoading(false);
    }
  };

  // Load concedii data when tab, year or filter changes
  useEffect(() => {
    if (activeTab === 'concedii') {
      fetchConcediiData();
    }
  }, [activeTab, concediiYear, concediiFilter]);

  // ============ Programator CO Functions ============
  const fetchProgramatorData = async () => {
    try {
      setProgramatorLoading(true);
      // Use dedicated programator-co endpoint with separate permissions
      const response = await axios.get(`${API}/programator-co/${programatorYear}/${programatorMonth}`, getAuthHeaders());
      const programatorResult = response.data;
      
      // Get holidays for this month
      try {
        const sarbResponse = await axios.get(`${API}/sarbatori/${programatorYear}/${programatorMonth}`, getAuthHeaders());
        setProgramatorSarbatori(sarbResponse.data || []);
      } catch {
        setProgramatorSarbatori([]);
      }
      
      setProgramatorData(programatorResult);
    } catch (error) {
      console.error('Failed to fetch programator data:', error);
      toast.error('Eroare la încărcarea datelor');
    } finally {
      setProgramatorLoading(false);
    }
  };

  // Load programator data when tab or month/year changes
  useEffect(() => {
    if (activeTab === 'programator-co') {
      fetchProgramatorData();
    }
  }, [activeTab, programatorMonth, programatorYear]);

  // ============ Landing Page Content Functions ============
  const fetchLandingContent = async () => {
    try {
      setLandingLoading(true);
      const response = await axios.get(`${API}/landing-content`, getAuthHeaders());
      setLandingContent(response.data);
    } catch (error) {
      console.error('Failed to fetch landing content:', error);
    } finally {
      setLandingLoading(false);
    }
  };

  const saveLandingContent = async () => {
    try {
      setLandingLoading(true);
      await axios.put(`${API}/landing-content`, landingContent, getAuthHeaders());
      toast.success('Conținutul site-ului a fost salvat!');
    } catch (error) {
      toast.error('Eroare la salvarea conținutului');
    } finally {
      setLandingLoading(false);
    }
  };

  // Load landing content when tab is selected
  useEffect(() => {
    if (activeTab === 'edit-site') {
      fetchLandingContent();
    }
  }, [activeTab]);

  // ============ Trasabilitate Functions ============
  const fetchAuditLogs = async (page = 1) => {
    try {
      setAuditLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 50);
      if (auditFilters.user) params.append('user_filter', auditFilters.user);
      if (auditFilters.action) params.append('action_filter', auditFilters.action);
      if (auditFilters.dateFrom) params.append('date_from', auditFilters.dateFrom);
      if (auditFilters.dateTo) params.append('date_to', auditFilters.dateTo);
      if (auditFilters.search) params.append('search', auditFilters.search);
      
      const response = await axios.get(`${API}/audit-logs?${params.toString()}`, getAuthHeaders());
      setAuditLogs(response.data.logs);
      setAuditTotalPages(response.data.total_pages);
      setAuditTotal(response.data.total);
      setAuditPage(page);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Eroare la încărcarea jurnalului');
    } finally {
      setAuditLoading(false);
    }
  };

  const fetchAuditFilters = async () => {
    try {
      const [actionsRes, usersRes] = await Promise.all([
        axios.get(`${API}/audit-logs/actions`, getAuthHeaders()),
        axios.get(`${API}/audit-logs/users`, getAuthHeaders())
      ]);
      setAuditActions(actionsRes.data);
      setAuditUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch audit filters:', error);
    }
  };

  const exportAuditLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (auditFilters.user) params.append('user_filter', auditFilters.user);
      if (auditFilters.action) params.append('action_filter', auditFilters.action);
      if (auditFilters.dateFrom) params.append('date_from', auditFilters.dateFrom);
      if (auditFilters.dateTo) params.append('date_to', auditFilters.dateTo);
      
      const response = await axios.get(`${API}/audit-logs/export?${params.toString()}`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trasabilitate_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export descărcat!');
    } catch (error) {
      console.error('Failed to export audit logs:', error);
      toast.error('Eroare la export');
    }
  };

  // Load audit logs when tab is selected
  useEffect(() => {
    if (activeTab === 'trasabilitate') {
      fetchAuditLogs();
      fetchAuditFilters();
    }
  }, [activeTab]);

  // Reload audit logs when filters change
  useEffect(() => {
    if (activeTab === 'trasabilitate') {
      fetchAuditLogs(1);
    }
  }, [auditFilters]);

  // Action labels for display
  const actionLabels = {
    // Angajați
    angajat_creat: 'Angajat creat',
    angajat_modificat: 'Angajat modificat',
    angajat_sters: 'Angajat șters',
    acces_blocat: 'Acces blocat',
    acces_deblocat: 'Acces deblocat',
    parola_resetata: 'Parolă resetată',
    roluri_modificate: 'Roluri modificate',
    
    // Demisii
    demisie_setata: 'Demisie setată',
    demisie_anulata: 'Demisie anulată (lună)',
    demisie_anulata_complet: 'Demisie anulată (complet)',
    
    // Pontaj
    pontaj_modificat: 'Pontaj modificat',
    pontaj_document_upload: 'Document încărcat',
    pontaj_document_delete: 'Document șters',
    pontaj_interval_salvat: 'Interval pontaj salvat',
    pontaj_interval_sters: 'Interval pontaj șters',
    
    // Concedii
    concediu_sold_modificat: 'Sold CO modificat',
    
    // Import
    import_excel: 'Import Excel pontaj',
    import_combustibil: 'Import combustibil',
    import_combustibil_anulat: 'Import combustibil anulat',
    
    // Mașini
    masina_creata: 'Mașină creată',
    masina_modificata: 'Mașină modificată',
    masina_stearsa: 'Mașină ștearsă',
    expirare_actualizata: 'Expirare actualizată',
    
    // Curse
    cursa_inchisa_fortat: 'Cursă închisă forțat',
    cursa_modificata: 'Cursă modificată',
    cursa_stearsa: 'Cursă ștearsă',
    cursa_manuala_creata: 'Cursă manuală creată',
    
    // Setări
    setare_modificata: 'Setare modificată',
    sarbatoare_adaugata: 'Sărbătoare adăugată',
    sarbatoare_stearsa: 'Sărbătoare ștearsă',
    
    // Roluri și permisiuni
    rol_schimbat: 'Rol schimbat',
    rol_creat: 'Rol creat',
    rol_sters: 'Rol șters',
    permisiuni_modificate: 'Permisiuni modificate',
    permisiuni_resetate: 'Permisiuni resetate',
    roluri_modificate: 'Roluri utilizator modificate'
  };

  const cleanupOrphanDocuments = async () => {
    try {
      setLoading(true);
      // Get all employees visible in pontaj
      const employeesInPontaj = drivers.filter(d => d.in_pontaj !== false);
      let totalCleaned = 0;
      
      for (const emp of employeesInPontaj) {
        try {
          const response = await axios.post(
            `${API}/pontaj/cleanup-orphan-documents/${emp.id}/${pontajYear}/${pontajMonth}`,
            {},
            getAuthHeaders()
          );
          totalCleaned += response.data.cleaned_entries || 0;
        } catch (e) {
          // Ignore individual errors
        }
      }
      
      await fetchPontajData();
      
      if (totalCleaned > 0) {
        toast.success(`Curățare completă: ${totalCleaned} referințe orfane eliminate`);
      } else {
        toast.info('Nu au fost găsite documente orfane');
      }
    } catch (error) {
      toast.error('Eroare la curățare');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTripSheets = async (carId, month, year) => {
    try {
      const response = await axios.get(
        `${API}/cars/${carId}/trip-sheets?month=${month}&year=${year}`,
        getAuthHeaders()
      );
      setTripSheets(prev => ({
        ...prev,
        [carId]: response.data
      }));
    } catch (error) {
      toast.error('Nu s-au putut încărca foile de parcurs');
    }
  };

  // Pre-fetch trip sheets pentru toate mașinile (pentru afișarea alertelor pe linii)
  const fetchAllTripSheets = async (carsList, month, year) => {
    if (!carsList || carsList.length === 0) return;
    
    try {
      const promises = carsList.map(car => 
        axios.get(
          `${API}/cars/${car.id}/trip-sheets?month=${month}&year=${year}`,
          getAuthHeaders()
        ).then(response => ({ carId: car.id, data: response.data }))
         .catch(() => ({ carId: car.id, data: null }))
      );
      
      const results = await Promise.all(promises);
      const newTripSheets = {};
      results.forEach(result => {
        if (result.data) {
          newTripSheets[result.carId] = result.data;
        }
      });
      setTripSheets(newTripSheets);
    } catch (error) {
      console.error('Failed to fetch all trip sheets:', error);
    }
  };
  
  const handleExpandCar = async (carId) => {
    if (expandedCar === carId) {
      setExpandedCar(null);
    } else {
      setExpandedCar(carId);
      await fetchTripSheets(carId, selectedMonth, selectedYear);
    }
  };
  
  const handleMonthChange = async (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    if (expandedCar) {
      await fetchTripSheets(expandedCar, month, year);
    }
  };

  const handleCreateCar = async () => {
    if (!newCar.fuel_capacity || parseFloat(newCar.fuel_capacity) <= 0) {
      toast.error('Capacitatea rezervorului este obligatorie');
      return;
    }
    
    try {
      const carData = {
        name: newCar.name,
        license_plate: newCar.license_plate,
        vin: newCar.vin || null,
        fuel_capacity: parseFloat(newCar.fuel_capacity),
        insurance_expiry: newCar.insurance_expiry ? new Date(newCar.insurance_expiry).toISOString() : null,
        tax_road_expiry: newCar.tax_road_expiry ? new Date(newCar.tax_road_expiry).toISOString() : null,
        inspection_expiry: newCar.inspection_expiry ? new Date(newCar.inspection_expiry).toISOString() : null,
        casco_expiry: newCar.casco_expiry ? new Date(newCar.casco_expiry).toISOString() : null,
        copie_conforma_expiry: newCar.copie_conforma_expiry ? new Date(newCar.copie_conforma_expiry).toISOString() : null,
      };
      await axios.post(`${API}/cars`, carData, getAuthHeaders());
      toast.success('Mașină adăugată cu succes');
      setNewCar({ 
        name: '', 
        license_plate: '', 
        vin: '',
        fuel_capacity: '',
        insurance_expiry: null,
        tax_road_expiry: null,
        inspection_expiry: null,
        casco_expiry: null,
        copie_conforma_expiry: null
      });
      setOpenCarDialog(false);
      fetchCars();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nu s-a putut adăuga mașina');
    }
  };

  // Handler pentru click pe celula pontaj
  const handleCellClick = (driver, dayNum, currentValue) => {
    if (!pontajEditMode) {
      // View mode - check if has document
      const dayData = pontajData[driver.id]?.[dayNum];
      const hasDocument = dayData?.document_filename;
      const value = dayData?.value;
      if (hasDocument && (value === 'CO' || value === 'CM' || value === 'CNC' || value === 'CD' || value === 'CFP' || value === 'DEM' || value === 'NM')) {
        loadDocumentPreview(driver.id, dayNum, dayData.document_filename, value);
      } else if (value === 'CO' || value === 'CM' || value === 'CNC' || value === 'CD' || value === 'CFP' || value === 'DEM') {
        toast.info('Nu există document atașat pentru această zi');
      }
      return;
    }
    
    // Edit mode - check if day is editable
    const hireDate = driver.angajat_de_la ? new Date(driver.angajat_de_la) : null;
    const resignDate = driver.pontaj_pana_la ? new Date(driver.pontaj_pana_la) : null;
    const dayDate = new Date(pontajYear, pontajMonth - 1, dayNum);
    
    // Check hire date
    if (hireDate && dayDate < hireDate) {
      toast.error('Nu puteți edita - angajatul nu era încă angajat în această zi');
      return;
    }
    
    // Check resignation date
    if (resignDate) {
      const resignEndOfMonth = new Date(resignDate.getFullYear(), resignDate.getMonth() + 1, 0);
      if (dayDate > resignEndOfMonth) {
        toast.error('Nu puteți edita - angajatul a demisionat');
        return;
      }
    }
    
    // Get existing custom program for this day
    const existingDayData = pontajData[driver.id]?.[dayNum];
    const existingProgramStart = existingDayData?.program_start || '';
    const existingProgramSfarsit = existingDayData?.program_sfarsit || '';
    const hasExistingProgram = existingProgramStart && existingProgramSfarsit;
    const existingPauzaStart = existingDayData?.pauza_start || '';
    const existingPauzaSfarsit = existingDayData?.pauza_sfarsit || '';
    const hasExistingPauza = existingPauzaStart && existingPauzaSfarsit;
    
    const dayDateObj = new Date(pontajYear, pontajMonth - 1, dayNum);
    const isWeekend = dayDateObj.getDay() === 0 || dayDateObj.getDay() === 6;
    const today = new Date();
    const isFutureMonth = pontajYear > today.getFullYear() || 
      (pontajYear === today.getFullYear() && pontajMonth > today.getMonth() + 1);
    const isCurrentMonth = pontajYear === today.getFullYear() && pontajMonth === today.getMonth() + 1;
    const isFutureDay = isCurrentMonth && dayNum > today.getDate();
    // Combinăm: ziua este în viitor dacă e într-o lună viitoare SAU e o zi viitoare din luna curentă
    const isFuture = isFutureMonth || isFutureDay;
    
    setPontajDialog({
      open: true,
      employeeId: driver.id,
      employeeName: driver.username,
      day: dayNum,
      isWeekend: isWeekend,
      isFutureMonth: isFuture,  // Folosim isFuture pentru a include și zilele viitoare din luna curentă
      currentValue: currentValue,
      hasDocument: !!existingDayData?.document_filename,
      documentFilename: existingDayData?.document_filename,
      selectedType: currentValue !== null && currentValue !== undefined ? String(currentValue) : null,
      useInterval: false,
      endDay: null,
      customProgram: hasExistingProgram,
      customProgramStart: existingProgramStart || '08:00',
      customProgramSfarsit: existingProgramSfarsit || '17:00',
      customPauza: hasExistingPauza,
      customPauzaStart: existingPauzaStart || '12:00',
      customPauzaSfarsit: existingPauzaSfarsit || '13:00',
      pending: !!existingDayData?.pending,
      originalPending: !!existingDayData?.pending
    });
  };

  const handleEditCar = (car) => {
    setEditingCar({
      ...car,
      insurance_expiry: car.insurance_expiry ? new Date(car.insurance_expiry) : null,
      tax_road_expiry: car.tax_road_expiry ? new Date(car.tax_road_expiry) : null,
      inspection_expiry: car.inspection_expiry ? new Date(car.inspection_expiry) : null,
      casco_expiry: car.casco_expiry ? new Date(car.casco_expiry) : null,
      copie_conforma_expiry: car.copie_conforma_expiry ? new Date(car.copie_conforma_expiry) : null,
    });
    setOpenEditCarDialog(true);
  };

  const handleFileUpload = (fieldName, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedDocuments(prev => ({
          ...prev,
          [fieldName]: {
            data: reader.result,
            name: file.name
          }
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateCar = async () => {
    try {
      const originalCar = cars.find(c => c.id === editingCar.id);
      
      const expiryFields = [
        { key: 'insurance_expiry', current: editingCar.insurance_expiry, original: originalCar?.insurance_expiry },
        { key: 'tax_road_expiry', current: editingCar.tax_road_expiry, original: originalCar?.tax_road_expiry },
        { key: 'inspection_expiry', current: editingCar.inspection_expiry, original: originalCar?.inspection_expiry },
        { key: 'casco_expiry', current: editingCar.casco_expiry, original: originalCar?.casco_expiry },
        { key: 'copie_conforma_expiry', current: editingCar.copie_conforma_expiry, original: originalCar?.copie_conforma_expiry }
      ];

      // Prepare car data update including documents
      const carData = {
        name: editingCar.name,
        license_plate: editingCar.license_plate,
        vin: editingCar.vin || null,
        status: editingCar.status,
        centralizator_status: editingCar.centralizator_status || 'activ',
        fuel_capacity: editingCar.fuel_capacity ? parseFloat(editingCar.fuel_capacity) : null,
        fuel_type: editingCar.fuel_type || null,
        consum_normat: editingCar.consum_normat ? parseFloat(editingCar.consum_normat) : null,
        data_achizitiei: editingCar.data_achizitiei || null,
        copie_conforma_nenecesar: editingCar.copie_conforma_nenecesar || false
      };
      
      // Add document_achizitie if uploaded
      if (uploadedDocuments.document_achizitie) {
        carData.document_achizitie = uploadedDocuments.document_achizitie.data;
        carData.document_achizitie_name = uploadedDocuments.document_achizitie.name;
      }
      
      // Add document_civ (original) if uploaded
      if (uploadedDocuments.document_civ) {
        carData.document_civ = uploadedDocuments.document_civ.data;
        carData.document_civ_name = uploadedDocuments.document_civ.name;
      }
      
      // Add document_civ_copie if uploaded
      if (uploadedDocuments.document_civ_copie) {
        carData.document_civ_copie = uploadedDocuments.document_civ_copie.data;
        carData.document_civ_copie_name = uploadedDocuments.document_civ_copie.name;
      }

      // Update basic car info
      await axios.put(`${API}/cars/${editingCar.id}`, carData, getAuthHeaders());

      // Update each expiry field that changed OR has a document uploaded
      for (const field of expiryFields) {
        const currentValue = field.current ? field.current.toISOString() : null;
        const originalValue = field.original ? new Date(field.original).toISOString() : null;
        const hasDocument = uploadedDocuments[field.key];
        
        // Update if: date changed OR document uploaded (even if date didn't change)
        if (currentValue !== originalValue || hasDocument) {
          console.log(`Updating ${field.key}: ${originalValue} -> ${currentValue}, has document: ${!!hasDocument}`);
          await axios.post(
            `${API}/cars/${editingCar.id}/update-expiry`,
            {
              field_name: field.key,
              new_value: currentValue || originalValue, // Use current if exists, otherwise keep original
              document: hasDocument?.data || null,
              document_name: hasDocument?.name || null
            },
            getAuthHeaders()
          );
        }
      }

      toast.success('Mașină actualizată cu succes');
      setEditingCar(null);
      setUploadedDocuments({});
      setOpenEditCarDialog(false);
      fetchCars();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.detail || 'Nu s-a putut actualiza mașina');
    }
  };

  const handleCreateDriver = async () => {
    try {
      // Asigură că rolul principal e în lista de roluri
      const roles = newDriver.roles || [newDriver.role || 'driver'];
      if (!roles.includes(newDriver.role || 'driver')) {
        roles.unshift(newDriver.role || 'driver');
      }
      
      const driverData = {
        username: newDriver.username,
        password: newDriver.password,
        role: newDriver.role || 'driver',
        roles: roles,
        nume: newDriver.nume || null,
        prenume: newDriver.prenume || null,
        cnp: newDriver.cnp || null,
        id_expiry: newDriver.id_expiry ? new Date(newDriver.id_expiry).toISOString() : null,
        driver_licence_expiry: newDriver.driver_licence_expiry ? new Date(newDriver.driver_licence_expiry).toISOString() : null,
        work_medicine_expiry: newDriver.work_medicine_expiry ? new Date(newDriver.work_medicine_expiry).toISOString() : null,
        catg: newDriver.catg || null,
        norma_ore: newDriver.norma_ore || 8,
        zile_co_anuale: newDriver.zile_co_anuale || 21,
        program_start: newDriver.program_start || '08:00',
        program_sfarsit: newDriver.program_sfarsit || '17:00',
        program_pauza: newDriver.program_pauza !== false,
        pauza_start: newDriver.pauza_start || '12:00',
        pauza_sfarsit: newDriver.pauza_sfarsit || '13:00',
        angajat_de_la: newDriver.angajat_de_la ? new Date(newDriver.angajat_de_la).toISOString() : null,
        numar_contract: newDriver.numar_contract || null,
        functie: newDriver.functie || null
      };
      await axios.post(`${API}/users`, driverData, getAuthHeaders());
      toast.success('Angajat adăugat cu succes');
      setNewDriver({ 
        username: '', 
        password: '',
        role: 'driver',
        roles: [],
        nume: '',
        prenume: '',
        cnp: '',
        id_expiry: null,
        driver_licence_expiry: null,
        work_medicine_expiry: null,
        catg: '',
        norma_ore: 8,
        zile_co_anuale: 21,
        program_start: '08:00',
        program_sfarsit: '17:00',
        program_pauza: true,
        pauza_start: '12:00',
        pauza_sfarsit: '13:00',
        angajat_de_la: null,
        numar_contract: '',
        functie: ''
      });
      setOpenDriverDialog(false);
      fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nu s-a putut adăuga angajatul');
    }
  };

  const handleEditDriver = (driver) => {
    setEditingDriver({
      ...driver,
      id_expiry: driver.id_expiry ? new Date(driver.id_expiry) : null,
      driver_licence_expiry: driver.driver_licence_expiry ? new Date(driver.driver_licence_expiry) : null,
      work_medicine_expiry: driver.work_medicine_expiry ? new Date(driver.work_medicine_expiry) : null,
      norma_ore: driver.norma_ore || 8,
      zile_co_anuale: driver.zile_co_anuale || 21,
      program_start: driver.program_start || '08:00',
      program_sfarsit: driver.program_sfarsit || '17:00',
      program_pauza: driver.program_pauza !== false,
      pauza_start: driver.pauza_start || '12:00',
      pauza_sfarsit: driver.pauza_sfarsit || '13:00',
      angajat_de_la: driver.angajat_de_la ? driver.angajat_de_la.split('T')[0] : '',
      numar_contract: driver.numar_contract || '',
      nume: driver.nume || '',
      prenume: driver.prenume || '',
      cnp: driver.cnp || '',
      functie: driver.functie || '',
      roles: driver.roles || [driver.role],  // Asigură că roles e un array
    });
    setOpenEditDriverDialog(true);
  };

  const handleUpdateDriver = async () => {
    try {
      const driverData = {
        username: editingDriver.username,
        nume: editingDriver.nume || null,
        prenume: editingDriver.prenume || null,
        cnp: editingDriver.cnp || null,
        cont_bancar: editingDriver.cont_bancar || null,
        email: editingDriver.email || null,
        id_expiry: editingDriver.id_expiry ? editingDriver.id_expiry.toISOString() : null,
        driver_licence_expiry: editingDriver.driver_licence_expiry ? editingDriver.driver_licence_expiry.toISOString() : null,
        work_medicine_expiry: editingDriver.work_medicine_expiry ? editingDriver.work_medicine_expiry.toISOString() : null,
        catg: editingDriver.catg || null,
        norma_ore: editingDriver.norma_ore || 8,
        zile_co_anuale: editingDriver.zile_co_anuale || 21,
        program_start: editingDriver.program_start || '08:00',
        program_sfarsit: editingDriver.program_sfarsit || '17:00',
        program_pauza: editingDriver.program_pauza !== false,
        pauza_start: editingDriver.pauza_start || '12:00',
        pauza_sfarsit: editingDriver.pauza_sfarsit || '13:00',
        angajat_de_la: editingDriver.angajat_de_la ? new Date(editingDriver.angajat_de_la).toISOString() : null,
        numar_contract: editingDriver.numar_contract || null,
        functie: editingDriver.functie || null
      };
      
      // Actualizează datele angajatului
      await axios.put(`${API}/users/${editingDriver.id}`, driverData, getAuthHeaders());
      
      // Actualizează și rolurile dacă s-au schimbat
      const currentRoles = editingDriver.roles || [editingDriver.role];
      if (currentRoles.length > 0) {
        await axios.put(`${API}/users/${editingDriver.id}/roles`, { roles: currentRoles }, getAuthHeaders());
      }
      
      toast.success('Angajat actualizat cu succes');
      setEditingDriver(null);
      setOpenEditDriverDialog(false);
      fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nu s-a putut actualiza angajatul');
    }
  };

  const handleDeleteCar = async (carId) => {
    if (!window.confirm('Are you sure you want to delete this car?')) return;
    try {
      await axios.delete(`${API}/cars/${carId}`, getAuthHeaders());
      toast.success('Mașină ștearsă');
      fetchCars();
    } catch (error) {
      toast.error('Nu s-a putut șterge mașina');
    }
  };

  const handleDeleteDriver = async (driverId) => {
    if (!window.confirm('Are you sure you want to delete this driver?')) return;
    try {
      await axios.delete(`${API}/users/${driverId}`, getAuthHeaders());
      toast.success('Angajat șters');
      fetchDrivers();
    } catch (error) {
      toast.error('Nu s-a putut șterge angajatul');
    }
  };

  const handleToggleAcces = async (driverId, currentStatus) => {
    const action = currentStatus ? 'debloca' : 'bloca';
    if (!window.confirm(`Sigur vrei să ${action} accesul acestui angajat?`)) return;
    try {
      const response = await axios.put(`${API}/users/${driverId}/toggle-acces`, {}, getAuthHeaders());
      toast.success(response.data.message);
      fetchDrivers();
    } catch (error) {
      toast.error('Nu s-a putut modifica accesul');
    }
  };

  const handleOpenResetPassword = (driver) => {
    setResetPasswordUser(driver);
    setResetPasswordData({ password: '', confirmPassword: '' });
    setOpenResetPasswordDialog(true);
  };

  const handleResetPassword = async () => {
    if (!resetPasswordData.password) {
      toast.error('Te rog introdu parola nouă');
      return;
    }
    if (resetPasswordData.password !== resetPasswordData.confirmPassword) {
      toast.error('Parolele nu se potrivesc');
      return;
    }
    
    try {
      await axios.put(
        `${API}/users/${resetPasswordUser.id}/reset-password`,
        { new_password: resetPasswordData.password },
        getAuthHeaders()
      );
      toast.success(`Parola pentru ${resetPasswordUser.username} a fost resetată cu succes`);
      setOpenResetPasswordDialog(false);
      setResetPasswordUser(null);
      setResetPasswordData({ password: '', confirmPassword: '' });
    } catch (error) {
      toast.error('Eroare la resetarea parolei');
      console.error('Reset password error:', error);
    }
  };

  const handleShowHistory = async (car, fieldKey, fieldLabel) => {
    try {
      const response = await axios.get(`${API}/cars/${car.id}/history/${fieldKey}`, getAuthHeaders());
      setHistoryData({
        carName: car.name,
        licensePlate: car.license_plate,
        fieldLabel: fieldLabel,
        fieldKey: fieldKey,
        carId: car.id,
        history: response.data
      });
      setOpenHistoryDialog(true);
    } catch (error) {
      toast.error('Nu s-a putut încărca istoricul');
    }
  };

  const handleDeleteHistoryEntry = async (historyId) => {
    console.log('🔴 handleDeleteHistoryEntry called with:', historyId);
    
    // În preview (sandboxed iframe), confirm nu funcționează
    // În producție, va funcționa normal
    // Pentru testare în preview, sărim peste confirm
    const isPreview = window.location.hostname.includes('preview') || window.location.hostname.includes('emergentagent');
    
    if (!isPreview) {
      if (!window.confirm('Sigur vrei să ștergi această înregistrare din istoric?')) {
        return;
      }
    }
    
    try {
      console.log('🔴 Sending DELETE request...');
      await axios.delete(`${API}/cars/history/${historyId}`, getAuthHeaders());
      toast.success('Înregistrare ștearsă cu succes');
      
      // Reîncarcă istoricul
      const response = await axios.get(`${API}/cars/${historyData.carId}/history/${historyData.fieldKey}`, getAuthHeaders());
      setHistoryData({
        ...historyData,
        history: response.data
      });
    } catch (error) {
      console.error('Failed to delete history entry:', error);
      toast.error('Eroare la ștergerea înregistrării');
    }
  };


  // ============ Pending Documents Functions ============
  
  const fetchPendingDocuments = async (carId) => {
    try {
      const response = await axios.get(`${API}/cars/${carId}/pending-documents`, getAuthHeaders());
      setPendingDocuments(prev => ({
        ...prev,
        [carId]: response.data
      }));
    } catch (error) {
      console.error('Failed to fetch pending documents:', error);
    }
  };

  const handleOpenPendingDocDialog = (car, fieldKey, fieldLabel) => {
    setPendingDocData({
      carId: car.id,
      carName: car.name,
      fieldName: fieldKey,
      fieldLabel: fieldLabel,
      expiryDate: '',
      activationDate: '',
      document: null,
      documentName: ''
    });
    setOpenPendingDocDialog(true);
  };

  const handlePendingDocFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validare mărime (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Fișierul este prea mare. Mărimea maximă este 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPendingDocData(prev => ({
          ...prev,
          document: reader.result,
          documentName: file.name
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadPendingDocument = async () => {
    if (!pendingDocData.expiryDate) {
      toast.error('Te rog să selectezi data de expirare');
      return;
    }

    if (!pendingDocData.activationDate) {
      toast.error('Te rog să selectezi data de activare');
      return;
    }

    // Validare: data activare trebuie să fie <= data expirare
    if (new Date(pendingDocData.activationDate) > new Date(pendingDocData.expiryDate)) {
      toast.error('Data de activare nu poate fi după data de expirare');
      return;
    }

    try {
      await axios.post(
        `${API}/cars/${pendingDocData.carId}/pending-document`,
        {
          field_name: pendingDocData.fieldName,
          expiry_date: pendingDocData.expiryDate,
          activation_date: pendingDocData.activationDate,
          document: pendingDocData.document,
          document_name: pendingDocData.documentName
        },
        getAuthHeaders()
      );

      toast.success('Document viitor încărcat cu succes!');
      setOpenPendingDocDialog(false);
      
      // Reîncarcă pending documents pentru această mașină
      await fetchPendingDocuments(pendingDocData.carId);
      
      // Reîmprospătează mașinile pentru a actualiza badge-urile
      await fetchCars();
    } catch (error) {
      console.error('Failed to upload pending document:', error);
      toast.error(error.response?.data?.detail || 'Eroare la încărcarea documentului viitor');
    }
  };

  const handleCancelPendingDocument = async (docId, carId) => {
    console.log('🟢 handleCancelPendingDocument called with:', docId, carId);
    
    // În preview (sandboxed iframe), confirm nu funcționează
    // În producție, va funcționa normal
    // Pentru testare în preview, sărim peste confirm
    const isPreview = window.location.hostname.includes('preview') || window.location.hostname.includes('emergentagent');
    
    if (!isPreview) {
      if (!window.confirm('Sigur vrei să anulezi acest document viitor?')) {
        return;
      }
    }

    try {
      console.log('🟢 Sending DELETE request...');
      await axios.delete(`${API}/cars/pending-document/${docId}`, getAuthHeaders());
      toast.success('Document viitor anulat');
      
      // Reîncarcă pending documents
      await fetchPendingDocuments(carId);
      await fetchCars();
    } catch (error) {
      console.error('Failed to cancel pending document:', error);
      toast.error('Eroare la anularea documentului viitor');
    }
  };

  // Funcție pentru vizualizarea documentelor viitoare (pending)
  const handleViewPendingDocument = (pendingDoc, carName, licensePlate) => {
    if (!pendingDoc || !pendingDoc.document) {
      toast.info('Nu există document atașat pentru acest document viitor');
      return;
    }
    
    // Mapare câmpuri la etichete în română
    const fieldLabels = {
      'insurance_expiry': 'Asigurare RCA',
      'tax_road_expiry': 'Taxă Drum (Rovinietă)',
      'inspection_expiry': 'ITP',
      'casco_expiry': 'CASCO',
      'copie_conforma_expiry': 'Copie Conformă'
    };
    
    const label = fieldLabels[pendingDoc.field_name] || pendingDoc.field_name;
    
    setDocumentData({
      carName: carName,
      licensePlate: licensePlate,
      fieldLabel: `${label} (Document Viitor - Activ din ${new Date(pendingDoc.activation_date).toLocaleDateString('ro-RO')})`,
      document: pendingDoc.document,
      documentName: pendingDoc.document_name || `${label}_viitor.pdf`
    });
    setOpenDocumentDialog(true);
  };

  // Încarcă pending documents pentru toate mașinile la mount
  useEffect(() => {
    if (cars.length > 0) {
      cars.forEach(car => {
        fetchPendingDocuments(car.id);
      });
    }
  }, [cars.length]);


  // Funcție pentru afișarea documentelor (Document Achiziție, CIV)
  const handleShowDocument = (car, fieldKey, fieldLabel) => {
    const document = car[fieldKey];
    if (!document) {
      toast.info(`Nu există ${fieldLabel} atașat pentru această mașină`);
      return;
    }
    setDocumentData({
      carName: car.name,
      licensePlate: car.license_plate,
      fieldLabel: fieldLabel,
      document: document,
      documentName: car[`${fieldKey}_name`] || `${fieldLabel}.pdf`
    });
    setOpenDocumentDialog(true);
  };

  const handleExport = async (type) => {
    setLoading(true);
    try {
      let url = `${API}/trips/export/${type}`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate.toISOString()}`);
      if (endDate) params.push(`end_date=${endDate.toISOString()}`);
      if (selectedDriverFilter && selectedDriverFilter !== 'all') {
        params.push(`driver_id=${selectedDriverFilter}`);
      }
      if (params.length) url += '?' + params.join('&');

      const response = await axios.get(url, {
        ...getAuthHeaders(),
        responseType: 'blob',
      });

      // Determine file extension
      let extension = 'pdf';
      if (type === 'excel') extension = 'xlsx';
      else if (type === 'csv') extension = 'csv';

      // Determine filename based on filter
      let filename;
      if (selectedDriverFilter && selectedDriverFilter !== 'all') {
        const driver = drivers.find(d => d.id === selectedDriverFilter);
        const driverName = driver ? driver.username.replace(/\s+/g, '_') : 'sofer';
        filename = `raport_sofer_${driverName}_${new Date().toISOString().split('T')[0]}.${extension}`;
      } else {
        filename = `raport_flota_${new Date().toISOString().split('T')[0]}.${extension}`;
      }

      const blob = new Blob([response.data], {
        type: response.headers['content-type']
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
      
      toast.success(`Raport exportat ca ${type.toUpperCase()}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Eroare la exportul raportului');
    } finally {
      setLoading(false);
    }
  };

  const handleTripSheetExport = async (carId, type) => {
    setLoading(true);
    try {
      const car = cars.find(c => c.id === carId);
      const carName = car ? car.name.replace(/\s+/g, '_') : 'car';
      
      let url = `${API}/trips/export/${type}`;
      const params = [`car_id=${carId}`, `month=${selectedMonth}`, `year=${selectedYear}`];
      url += '?' + params.join('&');

      const response = await axios.get(url, {
        ...getAuthHeaders(),
        responseType: 'blob',
      });

      // Determine file extension and content type
      let extension = 'pdf';
      let contentType = 'application/pdf';
      if (type === 'excel') {
        extension = 'xlsx';
        contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      } else if (type === 'csv') {
        extension = 'csv';
        contentType = 'text/csv';
      }

      const monthName = new Date(selectedYear, selectedMonth - 1).toLocaleString('ro-RO', { month: 'long' });
      const blob = new Blob([response.data], { type: contentType });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Foi_parcurs_${carName}_${monthName}_${selectedYear}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
      
      toast.success(`Foi de parcurs exportate ca ${type.toUpperCase()}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Eroare la exportul foilor de parcurs');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTrip = (trip) => {
    // Sincronizăm fuel_liters/fuel_cost din fuel_entries dacă există
    const entries = trip.fuel_entries || [];
    const motorina = entries.filter(e => !e.fuel_type || e.fuel_type === 'motorina');
    const entriesLiters = motorina.reduce((sum, e) => sum + (e.liters || 0), 0);
    const entriesCost = motorina.reduce((sum, e) => sum + (e.cost || 0), 0);
    
    const syncedTrip = { ...trip };
    if (entriesLiters > 0) {
      syncedTrip.fuel_liters = entriesLiters;
      syncedTrip.fuel_cost = entriesCost;
    }
    
    setEditingTrip(syncedTrip);
    setOpenEditTripDialog(true);
  };

  const handleUpdateTrip = async () => {
    if (!editingTrip) return;
    
    setLoading(true);
    try {
      await axios.put(`${API}/trips/${editingTrip.id}`, editingTrip, getAuthHeaders());
      toast.success('Trip actualizat cu succes');
      setOpenEditTripDialog(false);
      setEditingTrip(null);
      // Refresh trip sheets
      if (expandedCar) {
        await fetchTripSheets(expandedCar, selectedMonth, selectedYear);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la actualizarea trip-ului');
    } finally {
      setLoading(false);
    }
  };

  // Equipment CRUD
  const fileToDataUrl = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });

  const handleAddEquipment = async () => {
    try {
      await axios.post(`${API}/equipments`, newEquipment, getAuthHeaders());
      toast.success('Echipament adăugat');
      setShowAddEquipmentDialog(false);
      setNewEquipment({ name: '', type: '', itp_expiry: '', nr_iscir: '', nr_fabricatie: '', an_fabricatie: '' });
      const res = await axios.get(`${API}/equipments`, getAuthHeaders());
      setEquipments(res.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
  };

  const handleEditEquipment = async () => {
    try {
      await axios.put(`${API}/equipments/${editingEquipment.id}`, editingEquipment, getAuthHeaders());
      toast.success('Echipament actualizat');
      setShowEditEquipmentDialog(false);
      setEditingEquipment(null);
      const res = await axios.get(`${API}/equipments`, getAuthHeaders());
      setEquipments(res.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
  };

  const handleDeleteEquipment = async (id) => {
    if (!window.confirm('Sigur doriți să ștergeți acest echipament?')) return;
    try {
      await axios.delete(`${API}/equipments/${id}`, getAuthHeaders());
      toast.success('Echipament șters');
      setEquipments(prev => prev.filter(e => e.id !== id));
      if (expandedEquipment === id) setExpandedEquipment(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
  };

  const toggleEquipmentRevisions = async (eqId) => {
    if (expandedEquipment === eqId) { setExpandedEquipment(null); return; }
    try {
      const res = await axios.get(`${API}/equipments/${eqId}/revisions`, getAuthHeaders());
      setEquipmentRevisions(res.data || []);
      setExpandedEquipment(eqId);
    } catch { toast.error('Eroare la încărcarea reviziilor'); }
  };

  const handleAddRevision = async () => {
    if (!expandedEquipment || !newRevision.date || !newRevision.type) return;
    try {
      await axios.post(`${API}/equipments/${expandedEquipment}/revisions`, {
        date: newRevision.date,
        type: newRevision.type,
        cost: newRevision.cost ? parseFloat(newRevision.cost) : null,
        notes: newRevision.notes || null,
        next_revision_date: newRevision.next_revision_date || null,
        category: newRevision.category || 'revizie',
        documents: newRevision.documents || []
      }, getAuthHeaders());
      toast.success('Revizie adăugată');
      setShowAddRevisionDialog(false);
      setNewRevision({ date: '', type: 'ITP', cost: '', notes: '', next_revision_date: '', category: 'revizie', documents: [] });
      // Refresh revisions + equipments (ITP may have updated)
      const [revRes, eqRes] = await Promise.all([
        axios.get(`${API}/equipments/${expandedEquipment}/revisions`, getAuthHeaders()),
        axios.get(`${API}/equipments`, getAuthHeaders())
      ]);
      setEquipmentRevisions(revRes.data || []);
      setEquipments(eqRes.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
  };

  const handleDeleteRevision = async (revisionId) => {
    if (!expandedEquipment || !window.confirm('Sigur doriți să ștergeți această revizie?')) return;
    try {
      await axios.delete(`${API}/equipments/${expandedEquipment}/revisions/${revisionId}`, getAuthHeaders());
      toast.success('Revizie ștearsă');
      setEquipmentRevisions(prev => prev.filter(r => r.id !== revisionId));
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
  };


  const handleDeleteTrip = async (tripId) => {
    if (!window.confirm('Sigur doriți să ștergeți această cursă? Acțiunea este ireversibilă.')) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(`${API}/trips/${tripId}`, getAuthHeaders());
      toast.success('Cursă ștearsă cu succes');
      // Refresh trip sheets
      if (expandedCar) {
        await fetchTripSheets(expandedCar, selectedMonth, selectedYear);
      }
      // Also refresh Reports tab to keep data in sync
      await fetchTrips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la ștergerea cursei');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTrip = (carId) => {
    // Setăm data implicită: data curentă dacă suntem în luna selectată, altfel prima zi a lunii
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    const defaultDay = (currentMonth === parseInt(selectedMonth) && currentYear === parseInt(selectedYear)) 
      ? pad(now.getDate()) 
      : '01';
    const defaultStartTime = `${selectedYear}-${pad(selectedMonth)}-${defaultDay}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    
    console.log('[DEBUG-DATE] handleAddTrip: defaultStartTime =', defaultStartTime, 'selectedMonth=', selectedMonth, 'selectedYear=', selectedYear);
    
    // IMPORTANT: Creăm state complet nou, fără a folosi ...newTrip (evităm stale closure)
    setNewTrip({
      car_id: carId,
      driver_id: '',
      driver_name: '',
      start_time: defaultStartTime,
      end_time: '',
      start_km: '',
      end_km: '',
      route: '',
      scop_deplasare: '',
      departure_location: '',
      destination: '',
      notes: '',
      closing_reason: '',
      fuel_liters: '',
      fuel_cost: ''
    });
    setOpenAddTripDialog(true);
  };

  const handleCreateTrip = async () => {
    setLoading(true);
    // Citim din ref pentru a evita stale closure (re-render-urile din setInterval pot cauza referințe vechi)
    const tripData = newTripRef.current;
    console.log('[DEBUG-DATE] handleCreateTrip: ref.start_time =', tripData.start_time, 'state.start_time =', newTrip.start_time);
    console.log('[DEBUG] Creating trip with data:', JSON.stringify(tripData, null, 2));
    try {
      await axios.post(`${API}/trips/manual`, tripData, getAuthHeaders());
      toast.success('Cursă adăugată cu succes');
      setOpenAddTripDialog(false);
      setNewTrip({
        car_id: '',
        driver_id: '',
        driver_name: '',
        start_time: '',
        end_time: '',
        start_km: '',
        end_km: '',
        route: '',
        scop_deplasare: '',
        departure_location: '',
        destination: '',
        notes: '',
        closing_reason: '',
        fuel_liters: '',
        fuel_cost: ''
      });
      // Refresh trip sheets
      if (expandedCar) {
        await fetchTripSheets(expandedCar, selectedMonth, selectedYear);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la adăugarea cursei');
    } finally {
      setLoading(false);
    }
  };

  const handleSetFuelLevel = (carId) => {
    console.log('handleSetFuelLevel called with carId:', carId);
    console.log('Current fuel level:', tripSheets[carId]?.fuel_level);
    setFuelLevelData({
      car_id: carId,
      month: selectedMonth,
      year: selectedYear,
      start_level: tripSheets[carId]?.fuel_level?.start_level || '',
      end_level: tripSheets[carId]?.fuel_level?.end_level || ''
    });
    console.log('Setting openFuelLevelDialog to true');
    setOpenFuelLevelDialog(true);
  };

  const handleSaveFuelLevel = async () => {
    setLoading(true);
    try {
      // Save start level
      if (fuelLevelData.start_level) {
        await axios.post(`${API}/monthly-fuel-levels`, {
          car_id: fuelLevelData.car_id,
          month: fuelLevelData.month,
          year: fuelLevelData.year,
          level: parseFloat(fuelLevelData.start_level),
          level_type: 'start'
        }, getAuthHeaders());
      }
      
      // Save end level
      if (fuelLevelData.end_level) {
        await axios.post(`${API}/monthly-fuel-levels`, {
          car_id: fuelLevelData.car_id,
          month: fuelLevelData.month,
          year: fuelLevelData.year,
          level: parseFloat(fuelLevelData.end_level),
          level_type: 'end'
        }, getAuthHeaders());
      }
      
      toast.success('Nivel combustibil salvat cu succes');
      setOpenFuelLevelDialog(false);
      
      // Refresh trip sheets
      if (expandedCar) {
        await fetchTripSheets(expandedCar, selectedMonth, selectedYear);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvarea nivelului de combustibil');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCentralizator = async (format) => {
    setLoading(true);
    try {
      // Setările PDF sunt acum citite direct din DB de backend
      const params = new URLSearchParams({
        month: centralizatorMonth,
        year: centralizatorYear
      });
      
      const response = await axios.get(
        `${API}/centralizator/export/${format}?${params.toString()}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const month_names = ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 
                           'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
      const month_name = month_names[centralizatorMonth - 1];
      
      const extensions = { pdf: 'pdf', excel: 'xlsx', csv: 'csv' };
      link.setAttribute('download', `centralizator_${month_name}_${centralizatorYear}.${extensions[format]}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Centralizator exportat ca ${format.toUpperCase()}`);
      setOpenCentralizatorDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la exportul centralizatorului');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCentralizatorAngajati = async (format) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/centralizator-angajati/export/${format}?month=${centralizatorAngajatiMonth}&year=${centralizatorAngajatiYear}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const month_names = ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 
                           'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
      const month_name = month_names[centralizatorAngajatiMonth - 1];
      
      const extensions = { pdf: 'pdf', excel: 'xlsx', csv: 'csv' };
      link.setAttribute('download', `centralizator_angajati_${month_name}_${centralizatorAngajatiYear}.${extensions[format]}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Centralizator angajați exportat ca ${format.toUpperCase()}`);
      setOpenCentralizatorAngajatiDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la exportul centralizatorului angajați');
    } finally {
      setLoading(false);
    }
  };

  // CSV Import handlers
  const handleCSVUpload = async () => {
    if (!csvFile) {
      toast.error('Selectați un fișier CSV');
      return;
    }
    
    setCSVUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      
      const response = await axios.post(`${API}/import-fuel/upload`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setCSVPreviewData(response.data);
      setOpenCSVUploadDialog(false);
      setOpenCSVPreviewDialog(true);
      toast.success('CSV procesat cu succes');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la procesarea CSV-ului');
    } finally {
      setCSVUploading(false);
    }
  };
  
  const handleConfirmImport = async () => {
    if (!csvPreviewData) return;
    
    setLoading(true);
    try {
      // Includem și alimentările negăsite (car_not_found) pentru reconciliere ulterioară
      const itemsToImport = csvPreviewData.preview.filter(
        item => item.status === 'ready' || item.status === 'car_not_found'
      );
      
      const response = await axios.post(`${API}/import-fuel/confirm`, {
        items: itemsToImport
      }, getAuthHeaders());
      
      // Afișăm mesajul complet de la server
      toast.success(response.data.message || `Import finalizat: ${response.data.imported_count} alimentări adăugate`);
      
      if (response.data.unmatched_count > 0) {
        toast.info(`${response.data.unmatched_count} alimentări salvate pentru reconciliere manuală în "Alimentări Import"`, {
          duration: 5000
        });
      }
      
      setOpenCSVPreviewDialog(false);
      setCSVPreviewData(null);
      setCSVFile(null);
      
      // Refresh data
      await fetchImportBatches();
      if (expandedCar) {
        await fetchTripSheets(expandedCar, selectedMonth, selectedYear);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la importul alimentărilor');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchImportBatches = async () => {
    try {
      const response = await axios.get(`${API}/import-fuel/batches`, getAuthHeaders());
      setImportBatches(response.data);
    } catch (error) {
      console.error('Eroare la încărcarea istoricului:', error);
    }
  };
  
  const handleUndoLastImport = async () => {
    if (importBatches.length === 0) {
      toast.error('Nu există importuri de anulat');
      return;
    }
    
    const lastBatch = importBatches[0];
    if (!window.confirm(`Sigur doriți să anulați ultimul import? (${lastBatch.items_count} alimentări)`)) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(`${API}/import-fuel/batch/${lastBatch.id}`, getAuthHeaders());
      toast.success('Import anulat cu succes');
      
      await fetchImportBatches();
      if (expandedCar) {
        await fetchTripSheets(expandedCar, selectedMonth, selectedYear);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la anularea importului');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveTrips = async () => {
    try {
      const response = await axios.get(`${API}/trips/active`, getAuthHeaders());
      setActiveTrips(response.data);
    } catch (error) {
      console.error('Error fetching active trips:', error);
    }
  };

  // Fetch cursele din ziua curentă (doar pentru admin)
  const fetchTodayTrips = async () => {
    if (auth?.user?.role !== 'admin') return;
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API}/trips?start_date=${today}&end_date=${today}`, getAuthHeaders());
      // Sortăm după ora de start (cele mai recente primele)
      const sorted = (response.data || []).sort((a, b) => 
        new Date(b.start_time) - new Date(a.start_time)
      );
      setTodayTrips(sorted);
    } catch (error) {
      console.error('Error fetching today trips:', error);
    }
  };
  
  const handleOpenCloseDialog = (trip) => {
    setClosingTrip(trip);
    setCloseTripData({
      end_km: trip.end_km || '',
      fuel_liters: '',
      fuel_cost: '',
      closing_reason: 'final_day'
    });
    setOpenCloseTripDialog(true);
  };
  
  const handleForceCloseTrip = async () => {
    if (!closingTrip) return;
    
    // Validation
    if (!closeTripData.end_km) {
      toast.error('Kilometrajul final este obligatoriu!');
      return;
    }
    
    const endKm = parseInt(closeTripData.end_km);
    const startKm = closingTrip.start_km || 0;
    
    if (endKm < startKm) {
      toast.error(`Kilometrajul final (${endKm}) nu poate fi mai mic decât cel inițial (${startKm})!`);
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/trips/${closingTrip.id}/force-close`, {
        end_km: endKm,
        fuel_liters: closeTripData.fuel_liters ? parseFloat(closeTripData.fuel_liters) : null,
        fuel_cost: closeTripData.fuel_cost ? parseFloat(closeTripData.fuel_cost) : null,
        closing_reason: closeTripData.closing_reason
      }, getAuthHeaders());
      
      toast.success('Cursă închisă cu succes');
      setOpenCloseTripDialog(false);
      setClosingTrip(null);
      await fetchActiveTrips(); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la închiderea cursei');
    } finally {
      setLoading(false);
    }
  };
  
  // Polling for active trips - always fetch for badge, more frequent when on tab
  useEffect(() => {
    fetchActiveTrips(); // Initial fetch
    fetchTodayTrips(); // Fetch today's trips (admin only)
    
    // If on curse-active tab, refresh every 30 seconds
    // Otherwise refresh every 60 seconds (for badge only)
    const interval = setInterval(() => {
      fetchActiveTrips();
      if (activeTab === 'curse-active') {
        fetchTodayTrips();
      }
    }, activeTab === 'curse-active' ? 30000 : 60000);
    
    return () => clearInterval(interval);
  }, [activeTab]);

  const calculateDistance = (trip) => {
    if (trip.end_km && trip.start_km) {
      return (trip.end_km - trip.start_km).toFixed(2);
    }
    return 'N/A';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pb-16">
      <div className="flex">
        {/* Left Sidebar */}
        <div className="w-64 min-h-screen bg-white shadow-lg flex flex-col">
          <div className="p-6 border-b">
            <h1 className="text-2xl font-bold text-slate-800" data-testid="admin-dashboard-title">C.Manager</h1>
            <p className="text-sm text-slate-600 mt-1">Bun venit, {auth.user?.username}</p>
            
            {/* Selector de rol - apare doar dacă utilizatorul are mai multe roluri */}
            {auth.user?.roles && auth.user.roles.length > 1 && (
              <div className="mt-3">
                <label className="text-xs text-slate-500 block mb-1">Rol activ:</label>
                <select
                  value={auth.user?.role || ''}
                  onChange={async (e) => {
                    const newRole = e.target.value;
                    const result = await auth.switchRole(newRole);
                    if (result.success) {
                      toast.success(result.message);
                      // Redirecționează în funcție de noul rol
                      if (newRole === 'driver') {
                        window.location.href = '/driver';
                      } else {
                        window.location.href = '/admin';
                      }
                    } else {
                      toast.error(result.message);
                    }
                  }}
                  className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 bg-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {auth.user?.roles?.map(role => (
                    <option key={role} value={role}>
                      {role === 'admin' ? 'Administrator' : 
                       role === 'fleet_manager' ? 'Manager Flotă' :
                       role === 'contabilitate' ? 'Contabilitate' :
                       role === 'juridic' ? 'Juridic' :
                       role === 'dispatcher' ? 'Dispecer' :
                       role === 'tesa' ? 'TESA' :
                       role === 'driver' ? 'Șofer' : role}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
            {/* Secțiunea: Monitorizare - vizibilă pentru toți cu acces la mașini sau curse */}
            {hasAnyPermission('masini.vizualizare', 'curse.vizualizare', 'alerte.vizualizare') && (
            <>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 pt-2 pb-1">
              Monitorizare
            </div>
            {hasAnyPermission('masini.vizualizare', 'alerte.vizualizare') && (
            <button
              onClick={() => setActiveTab('alerte')}
              data-testid="alerte-tab"
              className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors relative ${
                activeTab === 'alerte'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              <Bell className="w-5 h-5" />
              <span className="font-medium">Alerte</span>
              {alerts.length > 0 && (
                <span className="ml-auto bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                  {alerts.length}
                </span>
              )}
            </button>
            )}
            
            {hasPermission('curse.vizualizare') && (
            <button
              onClick={() => setActiveTab('curse-active')}
              data-testid="curse-active-tab"
              className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors relative ${
                activeTab === 'curse-active'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              <Map className="w-5 h-5" />
              <span className="font-medium">Curse Active</span>
              <div className="ml-auto flex items-center gap-1">
                {activeTrips.filter(t => t.alert_level === 'normal').length > 0 && (
                  <span className="bg-green-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                    {activeTrips.filter(t => t.alert_level === 'normal').length}
                  </span>
                )}
                {activeTrips.filter(t => t.alert_level === 'warning').length > 0 && (
                  <span className="bg-blue-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                    {activeTrips.filter(t => t.alert_level === 'warning').length}
                  </span>
                )}
                {activeTrips.filter(t => t.alert_level === 'critical').length > 0 && (
                  <span className="bg-red-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                    {activeTrips.filter(t => t.alert_level === 'critical').length}
                  </span>
                )}
              </div>
            </button>
            )}
            </>
            )}
            
            {/* Secțiunea: Flotă - vizibilă pentru cei cu permisiuni pe mașini sau rapoarte */}
            {hasAnyPermission('masini.vizualizare', 'rapoarte.vizualizare') && (
              <>
                {/* Separator și Secțiunea: Flotă */}
                <div className="border-t border-slate-200 my-2"></div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 pt-1 pb-1">
                  Flotă
                </div>
                {hasPermission('masini.vizualizare') && (
                <button
                  onClick={() => setActiveTab('cars')}
                  data-testid="cars-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'cars'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Car className="w-5 h-5" />
                  <span className="font-medium">Autovehicule</span>
                </button>
                )}
                
                {hasPermission('rapoarte.vizualizare') && (
                <>
                <button
                  onClick={() => setActiveTab('reports')}
                  data-testid="reports-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'reports'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <FileText className="w-5 h-5" />
                  <span className="font-medium">Rapoarte</span>
                </button>
                
                <button
                  onClick={() => setActiveTab('foi-de-parcurs')}
                  data-testid="foi-de-parcurs-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'foi-de-parcurs'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <FileText className="w-5 h-5" />
                  <span className="font-medium">Foi de parcurs</span>
                </button>

                <button
                  onClick={() => setActiveTab('mentenanta')}
                  data-testid="mentenanta-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'mentenanta'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Wrench className="w-5 h-5" />
                  <span className="font-medium">Mentenanță</span>
                </button>

                <button
                  onClick={() => setActiveTab('setari-flota')}
                  data-testid="setari-flota-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'setari-flota'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Settings className="w-5 h-5" />
                  <span className="font-medium">Setări Flotă</span>
                </button>
                </>
                )}
              </>
            )}
            
            {/* Secțiunea: Echipamente și Imobile */}
            {hasAnyPermission('masini.vizualizare', 'setari.editare') && (
              <>
                <div className="border-t border-slate-200 my-2"></div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 pt-1 pb-1">
                  Echipamente și Imobile
                </div>
                <button
                  onClick={() => setActiveTab('echipamente')}
                  data-testid="echipamente-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'echipamente'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Wrench className="w-5 h-5" />
                  <span className="font-medium">Echipamente</span>
                </button>
                <button
                  onClick={() => setActiveTab('imobile')}
                  data-testid="imobile-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'imobile'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Building className="w-5 h-5" />
                  <span className="font-medium">Imobile</span>
                </button>
              </>
            )}
                
            {/* Separator și Secțiunea: Personal - vizibilă pentru cei cu permisiuni de personal */}
            {hasAnyPermission('angajati.vizualizare', 'pontaj.vizualizare', 'concedii.vizualizare', 'programator_co.vizualizare') && (
              <>
                <div className="border-t border-slate-200 my-2"></div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 pt-1 pb-1">
                  Personal
                </div>
                {hasPermission('angajati.vizualizare') && (
                <button
                  onClick={() => setActiveTab('drivers')}
                  data-testid="drivers-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'drivers'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Users className="w-5 h-5" />
                  <span className="font-medium">Angajați</span>
                </button>
                )}
                
                {hasPermission('pontaj.vizualizare') && (
                <button
                  onClick={() => setActiveTab('pontaj')}
                  data-testid="pontaj-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'pontaj'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Clock className="w-5 h-5" />
                  <span className="font-medium">Pontaj</span>
                </button>
                )}
                
                {hasPermission('pontaj.vizualizare') && (
                <button
                  onClick={() => setActiveTab('evidenta-ore')}
                  data-testid="evidenta-ore-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'evidenta-ore'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <FileText className="w-5 h-5" />
                  <span className="font-medium">Evidență ore</span>
                </button>
                )}
                
                {hasPermission('concedii.vizualizare') && (
                <button
                  onClick={() => setActiveTab('concedii')}
                  data-testid="concedii-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'concedii'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Palmtree className="w-5 h-5" />
                  <span className="font-medium">Concedii</span>
                </button>
                )}
                
                {hasPermission('programator_co.vizualizare') && (
                <button
                  onClick={() => setActiveTab('programator-co')}
                  data-testid="programator-co-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'programator-co'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <CalendarDays className="w-5 h-5" />
                  <span className="font-medium">Programator CO</span>
                </button>
                )}
              </>
            )}
            
            {/* Secțiunea: Motivare - între Personal și Financiar */}
            {hasAnyPermission('motivare.vizualizare', 'motivare.editare', 'monitorizare.vizualizare', 'setari.editare') && (
              <>
                <div className="border-t border-slate-200 my-2"></div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 pt-1 pb-1">
                  Motivare
                </div>
                
                {hasAnyPermission('motivare.vizualizare', 'setari.editare') && (
                <button
                  onClick={() => setActiveTab('motivare')}
                  data-testid="motivare-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'motivare'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Award className="w-5 h-5" />
                  <span className="font-medium">Motivare</span>
                </button>
                )}

                {hasAnyPermission('motivare.vizualizare', 'setari.editare') && (
                <button
                  onClick={() => setActiveTab('motivare-analiza')}
                  data-testid="motivare-analiza-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'motivare-analiza'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <BarChart3 className="w-5 h-5" />
                  <span className="font-medium">Analiză</span>
                </button>
                )}

                {hasAnyPermission('monitorizare.vizualizare', 'setari.editare') && (
                <button
                  onClick={() => setActiveTab('monitorizare-diferente')}
                  data-testid="monitorizare-diferente-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'monitorizare-diferente'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">Monitorizare Diferențe</span>
                </button>
                )}

                {hasAnyPermission('motivare.editare', 'setari.editare') && (
                <button
                  onClick={() => setActiveTab('setari-motivare')}
                  data-testid="setari-motivare-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'setari-motivare'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <SlidersHorizontal className="w-5 h-5" />
                  <span className="font-medium">Setări Motivare</span>
                </button>
                )}
              </>
            )}
            
            {/* Foto facturi (mobil) - vizibil pentru cine are facturi.foto sau financiar.editare */}
            {hasAnyPermission('facturi.foto', 'financiar.editare') && (
              <>
                <div className="border-t border-slate-200 my-2"></div>
                <button
                  onClick={() => navigate('/facturi-foto')}
                  data-testid="facturi-foto-nav"
                  className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors text-slate-700 hover:bg-slate-100"
                >
                  <Camera className="w-5 h-5" />
                  <span className="font-medium">Foto documente</span>
                  <span className="ml-auto text-[10px] uppercase text-slate-400">telefon</span>
                </button>
              </>
            )}

            {/* Secțiunea: Financiar - vizibilă pentru admin sau financiar.vizualizare */}
            {hasAnyPermission('financiar.vizualizare', 'setari.editare') && (
              <>
                <div className="border-t border-slate-200 my-2"></div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 pt-1 pb-1">
                  Financiar
                </div>
                
                <button
                  onClick={() => setActiveTab('financiar')}
                  data-testid="financiar-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'financiar'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Receipt className="w-5 h-5" />
                  <span className="font-medium">Costuri personal</span>
                </button>
                <button
                  onClick={() => setActiveTab('cheltuieli-furnizori')}
                  data-testid="cheltuieli-furnizori-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'cheltuieli-furnizori'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <FileText className="w-5 h-5" />
                  <span className="font-medium">Documente furnizori</span>
                </button>
                <button
                  onClick={() => { setFurnizorFocus(null); setActiveTab('furnizori'); }}
                  data-testid="furnizori-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'furnizori'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Building2 className="w-5 h-5" />
                  <span className="font-medium">Furnizori</span>
                </button>
                <button
                  onClick={() => setActiveTab('facturi-emise')}
                  data-testid="facturi-emise-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'facturi-emise'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <FileOutput className="w-5 h-5" />
                  <span className="font-medium">Facturi emise</span>
                </button>
              </>
            )}
                
            {/* Secțiunea: Setări - vizibilă pentru admin sau trasabilitate */}
            {hasAnyPermission('trasabilitate.vizualizare', 'setari.editare', 'roluri.vizualizare') && (
              <>
                <div className="border-t border-slate-200 my-2"></div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 pt-1 pb-1">
                  Setări
                </div>
                
                {hasPermission('trasabilitate.vizualizare') && (
                <button
                  onClick={() => setActiveTab('trasabilitate')}
                  data-testid="trasabilitate-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'trasabilitate'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <FileText className="w-5 h-5" />
                  <span className="font-medium">Trasabilitate</span>
                </button>
                )}
                
                {hasPermission('roluri.vizualizare') && (
                <button
                  onClick={() => setActiveTab('admin-roles')}
                  data-testid="admin-roles-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'admin-roles'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Shield className="w-5 h-5" />
                  <span className="font-medium">Administrare Roluri</span>
                </button>
                )}
                
                {hasPermission('rapoarte_erori.vizualizare') && (
                <button
                  onClick={() => setActiveTab('error-reports')}
                  data-testid="error-reports-tab-btn"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'error-reports'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">Rapoarte de Erori</span>
                </button>
                )}

                {hasPermission('setari.editare') && (
                <button
                  onClick={() => setActiveTab('email-config')}
                  data-testid="email-config-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'email-config'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Mail className="w-5 h-5" />
                  <span className="font-medium">Notificări Email</span>
                </button>
                )}

                {hasPermission('setari.editare') && (
                <button
                  onClick={() => setActiveTab('formulare-pdf')}
                  data-testid="formulare-pdf-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'formulare-pdf'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <FileSignature className="w-5 h-5" />
                  <span className="font-medium">Formulare PDF</span>
                </button>
                )}
                
                {hasPermission('setari.editare') && (
                <button
                  onClick={() => setActiveTab('edit-site')}
                  data-testid="edit-site-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'edit-site'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Settings className="w-5 h-5" />
                  <span className="font-medium">Editare Site</span>
                  {contactMessagesCount > 0 && (
                    <span className={`ml-auto text-xs font-bold px-2 py-1 rounded-full ${
                      activeTab === 'edit-site' 
                        ? 'bg-white text-blue-600' 
                        : 'bg-red-600 text-white'
                    }`}>
                      {contactMessagesCount}
                    </span>
                  )}
                </button>
                )}
                
                {hasPermission('setari.editare') && (
                <button
                  onClick={() => setActiveTab('documente-interne')}
                  data-testid="documente-interne-tab"
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-colors ${
                    activeTab === 'documente-interne'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <FolderOpen className="w-5 h-5" />
                  <span className="font-medium">Documente Interne</span>
                </button>
                )}
              </>
            )}
          </nav>
          
          {/* Backup Dropdown */}
          <div className="p-4 border-t">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" />
                  Backup Date
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="start">
                <DropdownMenuLabel>Backup Complet</DropdownMenuLabel>
                <DropdownMenuItem onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/backup/complet/json`, { ...getAuthHeaders(), responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `CManager_Backup_Complet_${new Date().toISOString().slice(0,10)}.json`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    toast.success('Backup JSON complet descărcat!');
                  } catch { toast.error('Eroare la backup'); }
                }}>
                  📄 Complet - JSON
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/backup/complet/excel`, { ...getAuthHeaders(), responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `CManager_Backup_Complet_${new Date().toISOString().slice(0,10)}.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    toast.success('Backup Excel complet descărcat!');
                  } catch { toast.error('Eroare la backup'); }
                }}>
                  📊 Complet - Excel
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Date Critice</DropdownMenuLabel>
                <DropdownMenuItem onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/backup/critic/json`, { ...getAuthHeaders(), responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `CManager_Backup_Critic_${new Date().toISOString().slice(0,10)}.json`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    toast.success('Backup JSON date critice descărcat!');
                  } catch { toast.error('Eroare la backup'); }
                }}>
                  📄 Critice - JSON
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/backup/critic/excel`, { ...getAuthHeaders(), responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `CManager_Backup_Critic_${new Date().toISOString().slice(0,10)}.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    toast.success('Backup Excel date critice descărcat!');
                  } catch { toast.error('Eroare la backup'); }
                }}>
                  📊 Critice - Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          <div className="p-4 pt-0 space-y-2">
            <ReportProblemButton auth={auth} section="admin" variant="ghost" className="w-full text-slate-500 hover:text-slate-700" label="Raportează o problemă" />
            <Button
              variant="outline"
              onClick={() => {
                auth.logout();
                window.location.href = '/login';
              }}
              data-testid="logout-button"
              className="w-full flex items-center justify-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {activeTab === 'curse-active' && hasPermission('curse.vizualizare') && (
            <CurseActiveTab
              activeTrips={activeTrips}
              todayTrips={todayTrips}
              cars={cars}
              handleOpenCloseDialog={handleOpenCloseDialog}
              loading={loading}
              auth={auth}
            />
          )}

          {activeTab === 'alerte' && hasAnyPermission('masini.vizualizare', 'alerte.vizualizare') && (
            <AlerteTab 
              alerts={alerts} 
              onViewPendingDocument={handleViewPendingDocument}
            />
          )}

          {/* Tab Motivare */}
          {activeTab === 'motivare' && hasAnyPermission('motivare.vizualizare', 'setari.editare') && (
            <MotivareTab canEdit={hasAnyPermission('motivare.editare', 'setari.editare')} />
          )}

          {/* Tab Analiză Motivare */}
          {activeTab === 'motivare-analiza' && hasAnyPermission('motivare.vizualizare', 'setari.editare') && (
            <MotivareAnalizaTab />
          )}

          {/* Tab Monitorizare Diferențe */}
          {activeTab === 'monitorizare-diferente' && hasAnyPermission('monitorizare.vizualizare', 'setari.editare') && (
            <MonitorizareDiferenteTab
              drivers={drivers}
              hasPermission={hasPermission}
              loading={loading}
            />
          )}

          {/* Tab Setări Motivare */}
          {activeTab === 'setari-motivare' && hasAnyPermission('motivare.editare', 'setari.editare') && (
            <SetariMotivareTab canEdit={hasAnyPermission('motivare.editare', 'setari.editare')} />
          )}

          {/* Tab Financiar - Gestiune Facturi */}
          {activeTab === 'financiar' && hasAnyPermission('financiar.vizualizare', 'setari.editare') && (
            <FinanciarTab hasPermission={hasPermission} token={auth.token} />
          )}

          {activeTab === 'cheltuieli-furnizori' && hasAnyPermission('financiar.vizualizare', 'setari.editare') && (
            <CheltuieliFurnizoriTab canEdit={hasAnyPermission('financiar.editare', 'setari.editare')} onOpenSupplier={(cod) => { setFurnizorFocus(cod); setActiveTab('furnizori'); }} />
          )}
          {activeTab === 'furnizori' && hasAnyPermission('financiar.vizualizare', 'setari.editare') && (
            <FurnizoriTab canEdit={hasAnyPermission('financiar.editare', 'setari.editare')} initialSupplier={furnizorFocus} />
          )}
          {activeTab === 'facturi-emise' && hasAnyPermission('financiar.vizualizare', 'setari.editare') && (
            <FacturiEmiseTab canEdit={hasAnyPermission('financiar.editare', 'setari.editare')} />
          )}

          {/* Tab Documente Interne */}
          {activeTab === 'documente-interne' && hasPermission('setari.editare') && (
            <DocumenteInterneTab hasPermission={hasPermission} />
          )}

          {/* Tab Mentenanță */}
          {activeTab === 'mentenanta' && (
            <MentenantaTab cars={cars} token={auth.token} />
          )}

          {/* Tab Setări Flotă */}
          {activeTab === 'setari-flota' && hasPermission('masini.editare') && (
            <SetariFlotaTab 
              routes={fleetRoutes}
              onAddRoute={handleAddRoute}
              onEditRoute={handleEditRoute}
              onDeleteRoute={handleDeleteRoute}
              closingReasons={closingReasonsList}
              onAddClosingReason={handleAddClosingReason}
              onEditClosingReason={handleEditClosingReason}
              onDeleteClosingReason={handleDeleteClosingReason}
              loading={loading}
            />
          )}
          
          {/* History Dialog */}
          <Dialog open={openHistoryDialog} onOpenChange={setOpenHistoryDialog}>
            <DialogContent className="w-[95vw] max-w-6xl h-[90vh] max-h-[90vh] flex flex-col">
              <DialogHeader className="flex-shrink-0">
                <DialogTitle>Expiry Date History</DialogTitle>
                <DialogDescription>
                  {historyData.carName} ({historyData.licensePlate}) - {historyData.fieldLabel}
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto min-h-0">
                {historyData.history.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <p>No history available for this field</p>
                    <p className="text-sm mt-2">History will be tracked from the first update</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {historyData.history.map((entry) => (
                      <div 
                        key={entry.id} 
                        className="p-4 border rounded-lg bg-slate-50 relative"
                        data-testid={`history-entry-${entry.id}`}
                      >
                        {/* Buton ștergere - doar pentru admin și fleet_manager */}
                        {(auth.user?.role === 'admin' || auth.user?.role === 'fleet_manager') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteHistoryEntry(entry.id);
                            }}
                            className="absolute top-2 right-2 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Șterge înregistrare"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        
                        <div className="flex flex-col lg:flex-row justify-between items-start gap-4 pr-10">
                          <div className="flex-1 w-full">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                              <span className="text-sm font-semibold text-slate-700">
                                Changed by: {entry.changed_by}
                              </span>
                              <span className="text-xs text-slate-500">
                                {new Date(entry.changed_at).toLocaleString()}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-slate-600 mb-1">Old Value:</p>
                                <p className="font-medium text-slate-800">
                                  {entry.old_value ? new Date(entry.old_value).toLocaleDateString() : 'Not set'}
                                </p>
                              </div>
                              <div>
                                <p className="text-slate-600 mb-1">New Value:</p>
                                <p className="font-medium text-green-700">
                                  {entry.new_value ? new Date(entry.new_value).toLocaleDateString() : 'Removed'}
                                </p>
                              </div>
                            </div>
                            {entry.document && (
                              <div className="mt-4 pt-4 border-t">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                                  <p className="text-slate-700 font-semibold text-sm">Attached Document</p>
                                  <a
                                    href={entry.document}
                                    download={entry.document_name}
                                    className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                                    data-testid={`download-document-${entry.id}`}
                                  >
                                    <Download className="w-4 h-4" />
                                    Download
                                  </a>
                                </div>
                                
                                {entry.document.startsWith('data:image/') ? (
                                  <div className="relative border rounded-lg overflow-hidden bg-slate-100">
                                    <img
                                      src={entry.document}
                                      alt={entry.document_name}
                                      className="w-full h-auto max-h-[50vh] sm:max-h-[60vh] object-contain cursor-pointer hover:opacity-90 transition"
                                      onClick={() => window.open(entry.document, '_blank')}
                                      data-testid={`document-image-${entry.id}`}
                                    />
                                    <div className="absolute bottom-2 right-2 bg-white/90 px-2 py-1 rounded text-xs text-slate-600">
                                      {entry.document_name}
                                    </div>
                                  </div>
                                ) : entry.document.startsWith('data:application/pdf') ? (
                                  <div className="border rounded-lg p-2 sm:p-4 bg-slate-50">
                                    <div className="flex items-center gap-3 mb-3">
                                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded flex items-center justify-center flex-shrink-0">
                                        <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="font-medium text-slate-800 truncate">{entry.document_name}</p>
                                        <p className="text-xs text-slate-500">PDF Document</p>
                                      </div>
                                    </div>
                                    <iframe
                                      src={entry.document}
                                      className="w-full h-[40vh] sm:h-[50vh] lg:h-[60vh] border rounded"
                                      title="PDF Preview"
                                      data-testid={`document-pdf-${entry.id}`}
                                    />
                                  </div>
                                ) : (
                                  <div className="border rounded-lg p-4 bg-slate-50 text-center">
                                    <FileText className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                                    <p className="text-sm text-slate-600">{entry.document_name}</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Document Viewer Dialog (pentru Document Achiziție și CIV) */}
          <Dialog open={openDocumentDialog} onOpenChange={setOpenDocumentDialog}>
            <DialogContent className="w-[95vw] max-w-5xl h-[90vh] max-h-[90vh] flex flex-col">
              <DialogHeader className="flex-shrink-0">
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  {documentData.fieldLabel}
                </DialogTitle>
                <DialogDescription>
                  {documentData.carName} ({documentData.licensePlate})
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-auto min-h-0">
                {documentData.document ? (
                  <div className="h-full">
                    {/* Butoane acțiuni */}
                    <div className="flex gap-2 mb-4 pb-3 border-b">
                      <a
                        href={documentData.document}
                        download={documentData.documentName}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Descarcă
                      </a>
                      <button
                        onClick={() => {
                          const printWindow = window.open(documentData.document, '_blank');
                          if (printWindow) {
                            printWindow.onload = () => printWindow.print();
                          }
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                      >
                        <FileText className="w-4 h-4" />
                        Printează
                      </button>
                    </div>
                    
                    {/* Afișare document */}
                    {documentData.document.startsWith('data:image/') ? (
                      <div className="flex items-center justify-center bg-slate-100 rounded-lg p-4 h-[calc(100%-60px)]">
                        <img
                          src={documentData.document}
                          alt={documentData.documentName}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    ) : documentData.document.startsWith('data:application/pdf') ? (
                      <iframe
                        src={documentData.document}
                        className="w-full h-[calc(100%-60px)] border rounded-lg"
                        title={documentData.fieldLabel}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                        <FileText className="w-16 h-16 mb-4" />
                        <p>Documentul nu poate fi previzualizat</p>
                        <a
                          href={documentData.document}
                          download={documentData.documentName}
                          className="mt-4 text-blue-600 hover:underline"
                        >
                          Descarcă documentul
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                    <FileText className="w-16 h-16 mb-4 text-slate-300" />
                    <p>Nu există document atașat</p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>


          {/* Pending Document Upload Dialog (Încarcă Document Viitor) */}
          <Dialog open={openPendingDocDialog} onOpenChange={setOpenPendingDocDialog}>
            <DialogContent className="w-[95vw] max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-green-600" />
                  Încarcă Document Viitor
                </DialogTitle>
                <DialogDescription>
                  {pendingDocData.carName} - {pendingDocData.fieldLabel}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Încarcă un document care va deveni activ automat la data specificată.
                  Util pentru a încărca RCA sau ITP nou înainte ca cel vechi să expire.
                </p>

                <div className="grid gap-4">
                  {/* Data de Expirare (noua dată) */}
                  <div>
                    <Label htmlFor="pending-expiry-date">
                      Data Expirare Document Nou <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="pending-expiry-date"
                      type="date"
                      value={pendingDocData.expiryDate}
                      onChange={(e) => setPendingDocData(prev => ({ ...prev, expiryDate: e.target.value }))}
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Când expiră noul document (ex: RCA nou expiră pe 15 februarie 2026)
                    </p>
                  </div>

                  {/* Data de Activare */}
                  <div>
                    <Label htmlFor="pending-activation-date">
                      Data Activare (Când devine activ) <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="pending-activation-date"
                      type="date"
                      value={pendingDocData.activationDate}
                      onChange={(e) => setPendingDocData(prev => ({ ...prev, activationDate: e.target.value }))}
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Când documentul nou devine activ (de obicei, data expirării documentului vechi)
                    </p>
                  </div>

                  {/* Upload Document (Opțional) */}
                  <div>
                    <Label htmlFor="pending-document-upload">
                      Document (PDF, JPG, PNG) - Opțional
                    </Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        id="pending-document-upload"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handlePendingDocFileChange}
                        className="flex-1"
                      />
                      {pendingDocData.documentName && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPendingDocData(prev => ({ ...prev, document: null, documentName: '' }))}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    {pendingDocData.documentName && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ {pendingDocData.documentName}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setOpenPendingDocDialog(false)}
                  >
                    Anulează
                  </Button>
                  <Button
                    onClick={handleUploadPendingDocument}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Încarcă Document Viitor
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          
          {/* Tab: Echipamente */}
          {activeTab === 'echipamente' && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Wrench className="w-5 h-5 text-blue-600" />
                        Echipamente
                      </CardTitle>
                      <CardDescription>Gestionare echipamente: denumire, tip, ITP</CardDescription>
                    </div>
                    <Button onClick={() => setShowAddEquipmentDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="w-4 h-4 mr-1" /> Adaugă Echipament
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {equipments.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <Wrench className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Niciun echipament adăugat</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nr.</TableHead>
                          <TableHead>Denumire</TableHead>
                          <TableHead>Tip</TableHead>
                          <TableHead>Nr. ISCIR</TableHead>
                          <TableHead>Nr. Fabricație</TableHead>
                          <TableHead>An Fab.</TableHead>
                          <TableHead>ITP</TableHead>
                          <TableHead>Revizii și Reparații</TableHead>
                          <TableHead>Acțiuni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {equipments.map((eq, idx) => (
                          <React.Fragment key={eq.id}>
                            <TableRow className={expandedEquipment === eq.id ? 'bg-blue-50' : ''}>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell className="font-medium">{eq.name}</TableCell>
                              <TableCell>{eq.type || '-'}</TableCell>
                              <TableCell className="text-sm">{eq.nr_iscir || '-'}</TableCell>
                              <TableCell className="text-sm">{eq.nr_fabricatie || '-'}</TableCell>
                              <TableCell className="text-sm">{eq.an_fabricatie || '-'}</TableCell>
                              <TableCell>
                                {eq.itp_expiry ? (
                                  <button
                                    onClick={() => {
                                      if (eq.itp_documents && eq.itp_documents.length > 0) {
                                        setViewingEquipmentDocs({ title: `Raport Inspecție — ${eq.name}`, documents: eq.itp_documents });
                                      }
                                    }}
                                    className={`px-2 py-1 rounded text-xs font-medium ${
                                      new Date(eq.itp_expiry) < new Date() ? 'bg-red-100 text-red-700' :
                                      new Date(eq.itp_expiry) < new Date(Date.now() + 30*24*60*60*1000) ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-green-100 text-green-700'
                                    } ${eq.itp_documents && eq.itp_documents.length > 0 ? 'underline cursor-pointer hover:opacity-80' : ''}`}
                                  >
                                    {new Date(eq.itp_expiry).toLocaleDateString('ro-RO')}
                                    {eq.itp_documents && eq.itp_documents.length > 0 && ` (${eq.itp_documents.length} pag.)`}
                                  </button>
                                ) : '-'}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant={expandedEquipment === eq.id ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => toggleEquipmentRevisions(eq.id)}
                                  className="text-xs"
                                >
                                  <FileText className="w-3 h-3 mr-1" />
                                  Revizii și Reparații {(eq.revisions || []).length > 0 ? `(${(eq.revisions || []).length})` : ''}
                                </Button>
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="sm" onClick={() => { setEditingEquipment(eq); setShowEditEquipmentDialog(true); }}>
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteEquipment(eq.id)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                            {/* Panoul de revizii expandat */}
                            {expandedEquipment === eq.id && (
                              <TableRow>
                                <TableCell colSpan={9} className="bg-slate-50 p-4">
                                  <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                      <h4 className="font-semibold text-sm text-slate-700">Revizii și Reparații — {eq.name}</h4>
                                      <Button size="sm" onClick={() => { setShowAddRevisionDialog(true); setNewRevision({ date: '', type: 'ITP', cost: '', notes: '', next_revision_date: '', category: 'revizie' }); }}
                                        className="bg-blue-600 hover:bg-blue-700">
                                        <Plus className="w-3 h-3 mr-1" /> Adaugă Revizie sau Reparație
                                      </Button>
                                    </div>
                                    {equipmentRevisions.length === 0 ? (
                                      <p className="text-sm text-slate-400 text-center py-4">Nicio revizie înregistrată</p>
                                    ) : (
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            <TableHead></TableHead>
                                            <TableHead>Data</TableHead>
                                            <TableHead>Tip</TableHead>
                                            <TableHead>Cost (RON)</TableHead>
                                            <TableHead>Următoarea revizie</TableHead>
                                            <TableHead>Observații</TableHead>
                                            <TableHead>Doc.</TableHead>
                                            <TableHead></TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {equipmentRevisions.map(rev => (
                                            <TableRow key={rev.id}>
                                              <TableCell>
                                                <span className="text-xs font-medium text-slate-700">{rev.category === 'reparatie' ? 'Reparație' : 'Revizie'}</span>
                                              </TableCell>
                                              <TableCell className="text-sm">{new Date(rev.date).toLocaleDateString('ro-RO')}</TableCell>
                                              <TableCell>
                                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">{rev.type}</span>
                                              </TableCell>
                                              <TableCell className="text-sm">{rev.cost ? `${rev.cost.toLocaleString('ro-RO')} RON` : '-'}</TableCell>
                                              <TableCell className="text-sm">{rev.next_revision_date ? new Date(rev.next_revision_date).toLocaleDateString('ro-RO') : '-'}</TableCell>
                                              <TableCell className="text-sm text-slate-500 max-w-xs truncate">{rev.notes || '-'}</TableCell>
                                              <TableCell>
                                                {rev.documents && rev.documents.length > 0 ? (
                                                  <button onClick={() => setViewingEquipmentDocs({ title: `${rev.category === 'reparatie' ? 'Reparație' : 'Revizie'} ${rev.type} — ${new Date(rev.date).toLocaleDateString('ro-RO')}`, documents: rev.documents })}
                                                    className="text-xs underline text-slate-600 hover:text-slate-800">{rev.documents.length} doc.</button>
                                                ) : '-'}
                                              </TableCell>
                                              <TableCell>
                                                <Button variant="ghost" size="sm" className="text-red-500 h-7 w-7 p-0" onClick={() => handleDeleteRevision(rev.id)}>
                                                  <Trash2 className="w-3 h-3" />
                                                </Button>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tab: Imobile */}
          {activeTab === 'imobile' && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="w-5 h-5 text-blue-600" />
                    Imobile
                  </CardTitle>
                  <CardDescription>În construcție</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-16 text-slate-400">
                    <Building className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-sm">În dezvoltare</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'cars' && hasPermission('masini.vizualizare') && (
            <MasiniTab
              cars={cars}
              newCar={newCar}
              setNewCar={setNewCar}
              editingCar={editingCar}
              setEditingCar={setEditingCar}
              openCarDialog={openCarDialog}
              setOpenCarDialog={setOpenCarDialog}
              openEditCarDialog={openEditCarDialog}
              setOpenEditCarDialog={setOpenEditCarDialog}
              openPopovers={openPopovers}
              setOpenPopovers={setOpenPopovers}
              uploadedDocuments={uploadedDocuments}
              handleCreateCar={handleCreateCar}
              handleEditCar={handleEditCar}
              handleUpdateCar={handleUpdateCar}
              handleDeleteCar={handleDeleteCar}
              handleFileUpload={handleFileUpload}
              handleShowHistory={handleShowHistory}
              handleShowDocument={handleShowDocument}
              handleOpenPendingDocDialog={handleOpenPendingDocDialog}
              pendingDocuments={pendingDocuments}
              handleCancelPendingDocument={handleCancelPendingDocument}
              handleViewPendingDocument={handleViewPendingDocument}
              hasPermission={hasPermission}
            />
          )}

          {activeTab === 'drivers' && hasPermission('angajati.vizualizare') && (
            <AngajatiTab
              drivers={drivers}
              newDriver={newDriver}
              setNewDriver={setNewDriver}
              editingDriver={editingDriver}
              setEditingDriver={setEditingDriver}
              openDriverDialog={openDriverDialog}
              setOpenDriverDialog={setOpenDriverDialog}
              openEditDriverDialog={openEditDriverDialog}
              setOpenEditDriverDialog={setOpenEditDriverDialog}
              openResetPasswordDialog={openResetPasswordDialog}
              setOpenResetPasswordDialog={setOpenResetPasswordDialog}
              resetPasswordUser={resetPasswordUser}
              resetPasswordData={resetPasswordData}
              setResetPasswordData={setResetPasswordData}
              employeeStatusFilter={employeeStatusFilter}
              setEmployeeStatusFilter={setEmployeeStatusFilter}
              employeeSearchFilter={employeeSearchFilter}
              setEmployeeSearchFilter={setEmployeeSearchFilter}
              pontajEndDateDialog={pontajEndDateDialog}
              setPontajEndDateDialog={setPontajEndDateDialog}
              handleCreateDriver={handleCreateDriver}
              handleEditDriver={handleEditDriver}
              handleUpdateDriver={handleUpdateDriver}
              handleDeleteDriver={handleDeleteDriver}
              handleOpenResetPassword={handleOpenResetPassword}
              handleResetPassword={handleResetPassword}
              handleToggleAcces={handleToggleAcces}
              fetchDrivers={fetchDrivers}
              fetchPontajData={fetchPontajData}
              getDisplayName={getDisplayName}
              getAuthHeaders={getAuthHeaders}
              hasPermission={hasPermission}
              API={API}
            />
          )}

          {activeTab === 'reports' && hasPermission('rapoarte.vizualizare') && (
            <RapoarteTab
              trips={trips}
              drivers={drivers}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              selectedDriverFilter={selectedDriverFilter}
              setSelectedDriverFilter={setSelectedDriverFilter}
              fetchTrips={fetchTrips}
              handleExport={handleExport}
              setOpenCentralizatorAngajatiDialog={setOpenCentralizatorAngajatiDialog}
              loading={loading}
            />
          )}
          
          {activeTab === 'foi-de-parcurs' && hasPermission('rapoarte.vizualizare') && (
            <FoiDeParcursTab
              cars={cars}
              tripSheets={tripSheets}
              expandedCar={expandedCar}
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              carSearchQuery={carSearchQuery}
              setCarSearchQuery={setCarSearchQuery}
              handleExpandCar={handleExpandCar}
              handleMonthChange={handleMonthChange}
              handleTripSheetExport={handleTripSheetExport}
              handleAddTrip={handleAddTrip}
              handleSetFuelLevel={handleSetFuelLevel}
              handleEditTrip={handleEditTrip}
              handleDeleteTrip={handleDeleteTrip}
              setOpenCentralizatorDialog={setOpenCentralizatorDialog}
              setOpenCSVUploadDialog={setOpenCSVUploadDialog}
              fetchImportBatches={fetchImportBatches}
              setShowImportHistory={setShowImportHistory}
              importBatches={importBatches}
              handleUndoLastImport={handleUndoLastImport}
              loading={loading}
              auth={auth}
              fuelAlerts={fuelAlerts}
              refreshTripSheets={async () => { if (expandedCar) await fetchTripSheets(expandedCar, selectedMonth, selectedYear); }}
            />
          )}

          {/* Tab Pontaj */}
          {activeTab === 'pontaj' && hasPermission('pontaj.vizualizare') && (
            <PontajTab
              pontajMonth={pontajMonth}
              pontajYear={pontajYear}
              pontajData={pontajData}
              pontajLoading={pontajLoading}
              pontajEditMode={pontajEditMode}
              drivers={drivers}
              setPontajMonth={setPontajMonth}
              setPontajYear={setPontajYear}
              setPontajEditMode={setPontajEditMode}
              setImportDialog={setImportDialog}
              setSarbatoriDialog={setSarbatoriDialog}
              fetchSarbatori={fetchSarbatori}
              exportPontajPDF={exportPontajPDF}
              exportPontajExcel={exportPontajExcel}
              openTicheteMasaExport={openTicheteMasaExport}
              cleanupOrphanDocuments={cleanupOrphanDocuments}
              handleCellClick={handleCellClick}
              isSarbatoare={isSarbatoare}
              getSarbatoareNume={getSarbatoareNume}
              hasPermission={hasPermission}
              loading={loading}
            />
          )}

          {/* Tab Evidență Ore */}
          {activeTab === 'evidenta-ore' && hasPermission('pontaj.vizualizare') && (
            <EvidentaOreTab
              pontajMonth={pontajMonth}
              pontajYear={pontajYear}
              pontajData={pontajData}
              pontajLoading={pontajLoading}
              drivers={drivers}
              setPontajMonth={setPontajMonth}
              setPontajYear={setPontajYear}
              exportEvidentaOrePDF={exportEvidentaOrePDF}
              exportEvidentaOreExcel={exportEvidentaOreExcel}
              isSarbatoare={isSarbatoare}
              getSarbatoareNume={getSarbatoareNume}
              getDisplayName={getDisplayName}
              getPontajValue={getPontajValue}
              loading={loading}
            />
          )}

          {/* Tab Concedii */}
          {activeTab === 'concedii' && hasPermission('concedii.vizualizare') && (
            <ConcediiTab
              concediiYear={concediiYear}
              setConcediiYear={setConcediiYear}
              concediiFilter={concediiFilter}
              setConcediiFilter={setConcediiFilter}
              concediiSubTab={concediiSubTab}
              setConcediiSubTab={setConcediiSubTab}
              concediiData={concediiData}
              concediiLoading={concediiLoading}
              loading={loading}
              exportConcediiPDF={exportConcediiPDF}
              exportConcediiExcel={exportConcediiExcel}
              fetchConcediiDetail={fetchConcediiDetail}
              saveSoldConcediu={saveSoldConcediu}
              hasPermission={hasPermission}
              refreshConcediiData={fetchConcediiData}
              auth={auth}
            />
          )}
          {/* Tab Programator CO */}
          {activeTab === 'programator-co' && hasPermission('programator_co.vizualizare') && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <CalendarDays className="w-5 h-5 text-green-600" />
                        Programator Concedii de Odihnă
                      </CardTitle>
                      <CardDescription>Vizualizare calendar CO pentru toți angajații</CardDescription>
                    </div>
                    {/* Month/Year Navigation */}
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          if (programatorMonth === 1) {
                            setProgramatorMonth(12);
                            setProgramatorYear(programatorYear - 1);
                          } else {
                            setProgramatorMonth(programatorMonth - 1);
                          }
                        }}
                      >
                        ◀
                      </Button>
                      <div className="px-4 py-2 bg-slate-100 rounded-lg min-w-[180px] text-center">
                        <span className="font-semibold">
                          {['', 'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 
                            'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'][programatorMonth]} {programatorYear}
                        </span>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          if (programatorMonth === 12) {
                            setProgramatorMonth(1);
                            setProgramatorYear(programatorYear + 1);
                          } else {
                            setProgramatorMonth(programatorMonth + 1);
                          }
                        }}
                      >
                        ▶
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setProgramatorMonth(new Date().getMonth() + 1);
                          setProgramatorYear(new Date().getFullYear());
                        }}
                        className="ml-2"
                      >
                        Luna în curs
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {programatorLoading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                      <p className="mt-2 text-slate-500">Se încarcă...</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="sticky left-0 bg-slate-100 z-10 px-2 py-2 text-left font-semibold border w-8">Nr.</th>
                            <th className="sticky left-8 bg-slate-100 z-10 px-2 py-2 text-left font-semibold border min-w-[180px]">Nume și Prenume</th>
                            {Array.from({ length: new Date(programatorYear, programatorMonth, 0).getDate() }, (_, i) => {
                              const day = i + 1;
                              const date = new Date(programatorYear, programatorMonth - 1, day);
                              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                              const isSarbatoare = programatorSarbatori.some(s => s.zi === day);
                              const dayNames = ['D', 'L', 'Ma', 'Mi', 'J', 'V', 'S'];
                              
                              // Weekend și sărbători au aceeași culoare
                              const isNonWorkingDay = isWeekend || isSarbatoare;
                              let headerBg = isNonWorkingDay ? 'bg-slate-200' : '';
                              
                              return (
                                <th 
                                  key={day} 
                                  className={`px-1 py-1 text-center font-medium border w-9 ${headerBg}`}
                                >
                                  <div className="text-xs text-slate-500">{dayNames[date.getDay()]}</div>
                                  <div>{day}</div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {programatorData.length > 0 ? (
                            programatorData.map((emp, idx) => {
                              const displayName = `${emp.nume || ''} ${emp.prenume || ''}`.trim() || emp.username;
                              const daysInMonth = new Date(programatorYear, programatorMonth, 0).getDate();
                              
                              return (
                                <tr key={emp.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                  <td className="sticky left-0 bg-inherit z-10 px-2 py-1.5 text-center border font-medium">
                                    {emp.numar_contract || (idx + 1)}
                                  </td>
                                  <td className="sticky left-8 bg-inherit z-10 px-2 py-1.5 border font-medium truncate max-w-[180px]">
                                    {displayName}
                                  </td>
                                  {Array.from({ length: daysInMonth }, (_, i) => {
                                    const day = i + 1;
                                    const date = new Date(programatorYear, programatorMonth - 1, day);
                                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                                    const isSarbatoare = programatorSarbatori.some(s => s.zi === day);
                                    const leaveValue = emp.leave_days && emp.leave_days[day];
                                    
                                    // Determine cell background and text color based on leave type
                                    let bgClass = '';
                                    let textClass = '';
                                    let displayText = '';
                                    
                                    if (leaveValue === 'CO') {
                                      bgClass = 'bg-green-100';
                                      textClass = 'text-green-700';
                                      displayText = 'CO';
                                    } else if (leaveValue === 'CM') {
                                      bgClass = 'bg-orange-100';
                                      textClass = 'text-orange-600';
                                      displayText = 'CM';
                                    } else if (leaveValue === 'CFP') {
                                      bgClass = 'bg-purple-100';
                                      textClass = 'text-purple-600';
                                      displayText = 'CFP';
                                    } else if (leaveValue === 'X') {
                                      // X = liber sau zile după demisie
                                      bgClass = 'bg-red-100';
                                      textClass = 'text-red-600';
                                      displayText = 'X';
                                    } else if (leaveValue === 'DEM') {
                                      // DEM = ziua demisiei
                                      bgClass = 'bg-red-200';
                                      textClass = 'text-red-700';
                                      displayText = 'DEM';
                                    } else if (isWeekend || isSarbatoare) {
                                      // Weekend și sărbători au aceeași culoare, fără X
                                      bgClass = 'bg-slate-200';
                                    }
                                    
                                    return (
                                      <td 
                                        key={day}
                                        className={`px-1 py-1.5 text-center border text-xs ${bgClass}`}
                                      >
                                        {displayText ? (
                                          <span className={`font-bold ${textClass}`}>{displayText}</span>
                                        ) : null}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={33} className="px-4 py-8 text-center text-slate-500">
                                Nu există angajați de afișat
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {/* Legend */}
                  <div className="mt-4 flex items-center gap-4 text-xs text-slate-600 border-t pt-3">
                    <div className="flex items-center gap-1">
                      <span className="w-6 h-5 bg-green-100 border rounded flex items-center justify-center text-green-700 font-bold text-[10px]">CO</span>
                      <span>Concediu Odihnă</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-6 h-5 bg-orange-100 border rounded flex items-center justify-center text-orange-600 font-bold text-[10px]">CM</span>
                      <span>Concediu Medical</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-7 h-5 bg-purple-100 border rounded flex items-center justify-center text-purple-600 font-bold text-[9px]">CFP</span>
                      <span>Fără Plată</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-6 h-5 bg-red-100 border rounded flex items-center justify-center text-red-600 font-bold text-[10px]">X</span>
                      <span>Liber / Post-Demisie</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-7 h-5 bg-red-200 border rounded flex items-center justify-center text-red-700 font-bold text-[9px]">DEM</span>
                      <span>Zi Demisie</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-6 h-5 bg-slate-200 border rounded"></span>
                      <span>Weekend / Sărbătoare</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tab Trasabilitate */}
          {activeTab === 'trasabilitate' && hasPermission('trasabilitate.vizualizare') && (
            <TrasabilitateTab
              auditLogs={auditLogs}
              auditLoading={auditLoading}
              auditFilters={auditFilters}
              setAuditFilters={setAuditFilters}
              auditUsers={auditUsers}
              auditActions={auditActions}
              auditTotal={auditTotal}
              auditPage={auditPage}
              auditTotalPages={auditTotalPages}
              fetchAuditLogs={fetchAuditLogs}
              exportAuditLogs={exportAuditLogs}
            />
          )}

          {/* Tab Administrare Roluri */}
          {activeTab === 'admin-roles' && hasPermission('roluri.vizualizare') && (
            <RoluriTab
              rolesData={rolesData}
              selectedRole={selectedRole}
              setSelectedRole={setSelectedRole}
              editingPermissions={editingPermissions}
              setEditingPermissions={setEditingPermissions}
              savingRoles={savingRoles}
              saveRolePermissions={saveRolePermissions}
              resetRolePermissions={resetRolePermissions}
              deleteRole={deleteRole}
              createRole={createRole}
              hasPermission={hasPermission}
            />
          )}

          {/* Tab Rapoarte de Erori */}
          {activeTab === 'error-reports' && hasPermission('rapoarte_erori.vizualizare') && (
            <ErrorReportsTab getAuthHeaders={getAuthHeaders} />
          )}

          {/* Tab Notificări Email */}
          {activeTab === 'email-config' && hasPermission('setari.editare') && (
            <EmailConfigTab getAuthHeaders={getAuthHeaders} />
          )}

          {activeTab === 'formulare-pdf' && hasPermission('setari.editare') && (
            <FormularePdfTab />
          )}

          {/* Tab Editare Site */}
          {activeTab === 'edit-site' && hasPermission('setari.editare') && (
            <div className="space-y-4">
              {/* Card principal editare conținut */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Settings className="w-5 h-5 text-red-600" />
                        Editare Conținut Site
                      </CardTitle>
                      <CardDescription>Modifică textele și informațiile afișate pe pagina principală</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => window.open('/', '_blank')}>
                        👁️ Previzualizare
                      </Button>
                      <Button onClick={saveLandingContent} disabled={landingLoading} className="bg-red-600 hover:bg-red-700">
                        {landingLoading ? 'Se salvează...' : '💾 Salvează modificările'}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Secțiunea Hero */}
                  <div className="border-b pb-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 bg-red-100 text-red-600 rounded flex items-center justify-center text-xs">1</span>
                      Secțiunea Hero (Banner Principal)
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Numele Companiei</Label>
                        <Input
                          value={landingContent.titlu || ''}
                          onChange={(e) => setLandingContent(prev => ({ ...prev, titlu: e.target.value }))}
                          placeholder="CrimacoTRP"
                        />
                        <p className="text-xs text-slate-500">Apare în header și footer</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Titlu Principal (Hero)</Label>
                        <Input
                          value={landingContent.subtitlu || ''}
                          onChange={(e) => setLandingContent(prev => ({ ...prev, subtitlu: e.target.value }))}
                          placeholder="Expertiză în transport și logistică"
                        />
                        <p className="text-xs text-slate-500">Textul mare pe imaginea de fundal</p>
                      </div>
                    </div>
                    <div className="space-y-2 mt-4">
                      <Label>Descriere Companie</Label>
                      <textarea
                        className="w-full min-h-[100px] p-3 border rounded-md text-sm"
                        value={landingContent.descriere || ''}
                        onChange={(e) => setLandingContent(prev => ({ ...prev, descriere: e.target.value }))}
                        placeholder="CrimacoTRP este o companie specializată în transport și logistică..."
                      />
                      <p className="text-xs text-slate-500">Textul care apare sub titlu în hero și în secțiunea "Despre noi"</p>
                    </div>
                  </div>

                  {/* Secțiunea Servicii */}
                  <div className="border-b pb-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 bg-red-100 text-red-600 rounded flex items-center justify-center text-xs">2</span>
                      Servicii (3 carduri)
                    </h3>
                    {(landingContent.servicii || []).map((serviciu, index) => (
                      <div key={index} className="grid md:grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg mb-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">Titlu Serviciu {index + 1}</Label>
                          <Input
                            value={serviciu.titlu || ''}
                            onChange={(e) => {
                              const newServicii = [...landingContent.servicii];
                              newServicii[index] = { ...newServicii[index], titlu: e.target.value };
                              setLandingContent(prev => ({ ...prev, servicii: newServicii }));
                            }}
                            placeholder="Ex: Curierat Rapid"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">Descriere Serviciu {index + 1}</Label>
                          <Input
                            value={serviciu.descriere || ''}
                            onChange={(e) => {
                              const newServicii = [...landingContent.servicii];
                              newServicii[index] = { ...newServicii[index], descriere: e.target.value };
                              setLandingContent(prev => ({ ...prev, servicii: newServicii }));
                            }}
                            placeholder="Ex: Livrări rapide și sigure"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Secțiunea Contact */}
                  <div className="border-b pb-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 bg-red-100 text-red-600 rounded flex items-center justify-center text-xs">3</span>
                      Informații Contact
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-slate-500">📧 Email</Label>
                        <Input
                          value={landingContent.contact?.email || ''}
                          onChange={(e) => setLandingContent(prev => ({ 
                            ...prev, 
                            contact: { ...prev.contact, email: e.target.value }
                          }))}
                          placeholder="contact@crimacotrp.ro"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-slate-500">📍 Adresă</Label>
                        <Input
                          value={landingContent.contact?.adresa || ''}
                          onChange={(e) => setLandingContent(prev => ({ 
                            ...prev, 
                            contact: { ...prev.contact, adresa: e.target.value }
                          }))}
                          placeholder="Str. Exemplu, Nr. 1, Oraș"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                    <p><strong>💡 Sfat:</strong> După ce salvezi modificările, deschide pagina principală pentru a vedea cum arată.</p>
                    <p className="mt-1">Site public: <code className="bg-red-100 px-1 rounded">crimacotrp.ro</code></p>
                    <p className="mt-1">Login angajați: <code className="bg-red-100 px-1 rounded">crimacotrp.ro/login</code></p>
                  </div>
                </CardContent>
              </Card>

              {/* Card mesaje de contact */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Mail className="w-5 h-5 text-red-600" />
                    Mesaje de Contact
                  </CardTitle>
                  <CardDescription>Mesajele primite de la vizitatori prin formularul de contact</CardDescription>
                </CardHeader>
                <CardContent>
                  <ContactMessagesSection onMessagesChange={setContactMessagesCount} />
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Edit Trip Dialog */}
      <Dialog open={openEditTripDialog} onOpenChange={setOpenEditTripDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editare Cursă</DialogTitle>
            <DialogDescription>Modifică detaliile trip-ului</DialogDescription>
          </DialogHeader>
          {editingTrip && (
            <div className="space-y-4">
              {/* Șofer - NOU */}
              <div className="space-y-2">
                <Label>Șofer</Label>
                <Select 
                  value={editingTrip.driver_id || ''}
                  onValueChange={(value) => {
                    const selectedDriver = drivers.find(d => d.id === value);
                    setEditingTrip({
                      ...editingTrip, 
                      driver_id: value,
                      driver_name: selectedDriver ? `${selectedDriver.nume || ''} ${selectedDriver.prenume || ''}`.trim() || selectedDriver.username : ''
                    });
                  }}
                >
                  <SelectTrigger className={editingTrip.driver_name === 'Import CSV' ? 'border-orange-500 bg-orange-50' : ''}>
                    <SelectValue placeholder={editingTrip.driver_name || 'Selectează șofer'} />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers.filter(d => (d.role === 'sofer' || d.role === 'driver') && (isActiveEmployee(d) || d.id === editingTrip.driver_id)).map(driver => (
                      <SelectItem key={driver.id} value={driver.id}>
                        {driver.nume || driver.prenume ? `${driver.nume || ''} ${driver.prenume || ''}`.trim() : driver.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editingTrip.driver_name === 'Import CSV' && (
                  <p className="text-xs text-orange-600">⚠️ Această cursă a fost creată de import CSV. Selectează șoferul corect.</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Km Start</Label>
                  <Input
                    type="number"
                    value={editingTrip.start_km || ''}
                    onChange={(e) => setEditingTrip({
                      ...editingTrip, 
                      start_km: e.target.value ? parseFloat(e.target.value) : 0
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Km End</Label>
                  <Input
                    type="number"
                    value={editingTrip.end_km || ''}
                    onChange={(e) => setEditingTrip({
                      ...editingTrip, 
                      end_km: e.target.value ? parseFloat(e.target.value) : null
                    })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Traseu</Label>
                <Input
                  value={editingTrip.route || ''}
                  onChange={(e) => setEditingTrip({...editingTrip, route: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label>Scop Deplasare</Label>
                <Input
                  value={editingTrip.scop_deplasare || ''}
                  onChange={(e) => setEditingTrip({...editingTrip, scop_deplasare: e.target.value})}
                  placeholder="Ex: Livrare colete conform aviz"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Alimentare (Litri)</Label>
                  <Input
                    type="number"
                    data-testid="edit-trip-fuel-liters"
                    value={editingTrip.fuel_liters || ''}
                    onChange={(e) => setEditingTrip({
                      ...editingTrip, 
                      fuel_liters: e.target.value ? parseFloat(e.target.value) : null
                    })}
                    placeholder="Ex: 60"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cost Combustibil (RON)</Label>
                  <Input
                    type="number"
                    data-testid="edit-trip-fuel-cost"
                    value={editingTrip.fuel_cost || ''}
                    onChange={(e) => setEditingTrip({
                      ...editingTrip, 
                      fuel_cost: e.target.value ? parseFloat(e.target.value) : null
                    })}
                    placeholder="Ex: 420"
                  />
                </div>
              </div>

              {/* Afișare fuel_entries existente */}
              {editingTrip.fuel_entries && editingTrip.fuel_entries.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Alimentări înregistrate:</Label>
                  <div className="space-y-1">
                    {editingTrip.fuel_entries.map((entry, idx) => (
                      <div key={entry.id || idx} data-testid={`edit-trip-fuel-entry-${idx}`} className="flex items-center justify-between bg-slate-50 rounded px-3 py-1.5 text-sm">
                        <span>
                          {entry.fuel_type === 'adblue' ? '🔵 AdBlue' : '⛽'} {entry.liters}L
                          {entry.cost ? ` • ${entry.cost} RON` : ''}
                          {entry.note ? ` • ${entry.note}` : ''}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`edit-trip-fuel-entry-delete-${idx}`}
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={async () => {
                            try {
                              await axios.delete(`${API}/trips/${editingTrip.id}/fuel-entry/${entry.id}`, getAuthHeaders());
                              const newEntries = editingTrip.fuel_entries.filter(e => e.id !== entry.id);
                              const motorina = newEntries.filter(e => !e.fuel_type || e.fuel_type === 'motorina');
                              setEditingTrip({
                                ...editingTrip,
                                fuel_entries: newEntries,
                                fuel_liters: motorina.reduce((s, e) => s + (e.liters || 0), 0) || null,
                                fuel_cost: motorina.reduce((s, e) => s + (e.cost || 0), 0) || null,
                              });
                              toast.success('Alimentare ștearsă');
                            } catch { toast.error('Eroare la ștergere'); }
                          }}
                          title="Șterge această alimentare"
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Stație Alimentare</Label>
                <Input
                  value={editingTrip.fuel_station || ''}
                  onChange={(e) => setEditingTrip({...editingTrip, fuel_station: e.target.value})}
                  placeholder="Ex: OMV Slobozia"
                />
              </div>

              <div className="space-y-2">
                <Label>Motiv Închidere</Label>
                <Select 
                  value={editingTrip.closing_reason || ''}
                  onValueChange={(value) => setEditingTrip({...editingTrip, closing_reason: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="final_day">Sfârșit cursă</SelectItem>
                    <SelectItem value="car_damaged">Mașină defectă</SelectItem>
                    <SelectItem value="engine_problem">Problemă motor</SelectItem>
                    <SelectItem value="other">Altele</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea
                  value={editingTrip.notes || ''}
                  onChange={(e) => setEditingTrip({...editingTrip, notes: e.target.value})}
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setOpenEditTripDialog(false)}>
                  Anulează
                </Button>
                <Button onClick={handleUpdateTrip} disabled={loading}>
                  {loading ? 'Se salvează...' : 'Salvează'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Add Trip Dialog */}
      <Dialog open={openAddTripDialog} onOpenChange={setOpenAddTripDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adaugă Cursă Nouă</DialogTitle>
            <DialogDescription>Completați detaliile cursei</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Șofer *</Label>
                <Select
                  value={newTrip.driver_id || ''}
                  onValueChange={(value) => {
                    const selectedDriver = drivers.find(d => d.id === value);
                    console.log('[DEBUG-DATE] driver onChange, current start_time BEFORE:', newTripRef.current.start_time);
                    setNewTrip(prev => {
                      console.log('[DEBUG-DATE] driver setNewTrip prev.start_time:', prev.start_time);
                      return {
                        ...prev, 
                        driver_id: value,
                        driver_name: selectedDriver ? selectedDriver.username : ''
                      };
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selectează șofer" />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers.filter(isActiveEmployee).map(driver => (
                      <SelectItem key={driver.id} value={driver.id}>
                        {driver.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Traseu</Label>
                <Input
                  value={newTrip.route || ''}
                  onChange={(e) => setNewTrip(prev => ({...prev, route: e.target.value}))}
                  placeholder="Ex: București - Brașov"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data/Ora Start *</Label>
                <Input
                  type="datetime-local"
                  value={newTrip.start_time || ''}
                  onChange={(e) => {
                    console.log('[DEBUG-DATE] start_time onChange:', e.target.value, 'previous:', newTripRef.current.start_time);
                    setNewTrip(prev => ({...prev, start_time: e.target.value}));
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>Data/Ora End</Label>
                <Input
                  type="datetime-local"
                  value={newTrip.end_time || ''}
                  onChange={(e) => {
                    console.log('[DEBUG-DATE] end_time onChange:', e.target.value);
                    setNewTrip(prev => ({...prev, end_time: e.target.value}));
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Km Start *</Label>
                <Input
                  type="number"
                  value={newTrip.start_km || ''}
                  onChange={(e) => setNewTrip(prev => ({...prev, start_km: parseFloat(e.target.value) || 0}))}
                  placeholder="Ex: 125000"
                />
              </div>
              <div className="space-y-2">
                <Label>Km End</Label>


                <Input
                  type="number"
                  value={newTrip.end_km || ''}
                  onChange={(e) => setNewTrip(prev => ({...prev, end_km: parseFloat(e.target.value) || null}))}
                  placeholder="Ex: 125150"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Locație Plecare</Label>
                <Input
                  value={newTrip.departure_location || ''}
                  onChange={(e) => setNewTrip(prev => ({...prev, departure_location: e.target.value}))}
                  placeholder="Ex: Depozit București"
                />
              </div>
              <div className="space-y-2">
                <Label>Destinație</Label>
                <Input
                  value={newTrip.destination || ''}
                  onChange={(e) => setNewTrip(prev => ({...prev, destination: e.target.value}))}
                  placeholder="Ex: Client Brașov"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Scop Deplasare</Label>
              <Input
                value={newTrip.scop_deplasare || ''}
                onChange={(e) => setNewTrip(prev => ({...prev, scop_deplasare: e.target.value}))}
                placeholder="Ex: Livrare colete conform aviz"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Alimentare (Litri)</Label>
                <Input
                  type="number"
                  value={newTrip.fuel_liters || ''}
                  onChange={(e) => setNewTrip(prev => ({...prev, fuel_liters: parseFloat(e.target.value) || null}))}
                  placeholder="Ex: 60"
                />
              </div>
              <div className="space-y-2">
                <Label>Cost Combustibil (RON)</Label>
                <Input
                  type="number"
                  value={newTrip.fuel_cost || ''}
                  onChange={(e) => setNewTrip(prev => ({...prev, fuel_cost: parseFloat(e.target.value) || null}))}
                  placeholder="Ex: 420"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Motiv Închidere</Label>
              <Select 
                value={newTrip.closing_reason || ''}
                onValueChange={(value) => setNewTrip(prev => ({...prev, closing_reason: value}))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selectează motiv" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="final_day">Sfârșit cursă</SelectItem>
                  <SelectItem value="car_damaged">Mașină defectă</SelectItem>
                  <SelectItem value="engine_problem">Problemă motor</SelectItem>
                  <SelectItem value="other">Altele</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={newTrip.notes || ''}
                onChange={(e) => setNewTrip(prev => ({...prev, notes: e.target.value}))}
                rows={3}
                placeholder="Adaugă note despre cursă..."
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setOpenAddTripDialog(false)}>
                Anulează
              </Button>
              <Button onClick={handleCreateTrip} disabled={loading || !newTrip.driver_id || !newTrip.start_km || !newTrip.start_time}>
                {loading ? 'Se adaugă...' : 'Adaugă Cursă'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Set Fuel Level Dialog - Correctly Placed */}
      <Dialog open={openFuelLevelDialog} onOpenChange={setOpenFuelLevelDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Setează Nivel Combustibil Rezervor</DialogTitle>
            <DialogDescription>
              Introduceți nivelul combustibilului din rezervor pentru {new Date(fuelLevelData.year, fuelLevelData.month - 1).toLocaleString('ro-RO', { month: 'long', year: 'numeric' })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nivel Început Lună (Litri)</Label>
              <Input
                type="number"
                value={fuelLevelData.start_level || ''}
                onChange={(e) => setFuelLevelData({...fuelLevelData, start_level: e.target.value})}
                placeholder="Ex: 45"
              />
              <p className="text-xs text-slate-500">Câți litri erau în rezervor la 1 {new Date(fuelLevelData.year, fuelLevelData.month - 1).toLocaleString('ro-RO', { month: 'long' })}?</p>
            </div>

            <div className="space-y-2">
              <Label>Nivel Sfârșit Lună (Litri)</Label>
              <Input
                type="number"
                value={fuelLevelData.end_level || ''}
                onChange={(e) => setFuelLevelData({...fuelLevelData, end_level: e.target.value})}
                placeholder="Ex: 38"
              />
              <p className="text-xs text-slate-500">Câți litri sunt în rezervor la sfârșitul lunii?</p>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
              <p className="font-medium">ℹ️ Informație</p>
              <p className="mt-1">Aceste valori vor apărea în secțiunea REZUMAT din PDF-ul exportat și în aplicație.</p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setOpenFuelLevelDialog(false)}>
                Anulează
              </Button>
              <Button 
                onClick={handleSaveFuelLevel} 
                disabled={loading || (!fuelLevelData.start_level && !fuelLevelData.end_level)}
              >
                {loading ? 'Se salvează...' : 'Salvează'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Centralizator Export Dialog */}
      <Dialog open={openCentralizatorDialog} onOpenChange={setOpenCentralizatorDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Export Centralizator Lunar Flotă</DialogTitle>
            <DialogDescription>
              Selectați perioada și formatul de export. Setările PDF se configurează din Setări Flotă &rarr; Setări PDF.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Perioada */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Luna</Label>
                <Select 
                  value={centralizatorMonth.toString()}
                  onValueChange={(value) => setCentralizatorMonth(parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Ianuarie</SelectItem>
                    <SelectItem value="2">Februarie</SelectItem>
                    <SelectItem value="3">Martie</SelectItem>
                    <SelectItem value="4">Aprilie</SelectItem>
                    <SelectItem value="5">Mai</SelectItem>
                    <SelectItem value="6">Iunie</SelectItem>
                    <SelectItem value="7">Iulie</SelectItem>
                    <SelectItem value="8">August</SelectItem>
                    <SelectItem value="9">Septembrie</SelectItem>
                    <SelectItem value="10">Octombrie</SelectItem>
                    <SelectItem value="11">Noiembrie</SelectItem>
                    <SelectItem value="12">Decembrie</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Anul</Label>
                <Select 
                  value={centralizatorYear.toString()}
                  onValueChange={(value) => setCentralizatorYear(parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2023">2023</SelectItem>
                    <SelectItem value="2024">2024</SelectItem>
                    <SelectItem value="2025">2025</SelectItem>
                    <SelectItem value="2026">2026</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Export buttons */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Selectați formatul de export:</Label>
              <div className="flex gap-2">
                <Button 
                  onClick={() => handleExportCentralizator('pdf')}
                  disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  data-testid="centralizator-export-pdf-btn"
                >
                  {loading ? '...' : 'PDF'}
                </Button>
                <Button 
                  onClick={() => handleExportCentralizator('excel')}
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="centralizator-export-excel-btn"
                >
                  {loading ? '...' : 'Excel'}
                </Button>
                <Button 
                  onClick={() => handleExportCentralizator('csv')}
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="centralizator-export-csv-btn"
                >
                  {loading ? '...' : 'CSV'}
                </Button>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setOpenCentralizatorDialog(false)}>
                Închide
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Centralizator Angajați Export Dialog */}
      <Dialog open={openCentralizatorAngajatiDialog} onOpenChange={setOpenCentralizatorAngajatiDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Centralizator Angajați</DialogTitle>
            <DialogDescription>
              Selectați luna și anul pentru care doriți să exportați centralizatorul angajaților
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Luna</Label>
                <Select 
                  value={centralizatorAngajatiMonth.toString()}
                  onValueChange={(value) => setCentralizatorAngajatiMonth(parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Ianuarie</SelectItem>
                    <SelectItem value="2">Februarie</SelectItem>
                    <SelectItem value="3">Martie</SelectItem>
                    <SelectItem value="4">Aprilie</SelectItem>
                    <SelectItem value="5">Mai</SelectItem>
                    <SelectItem value="6">Iunie</SelectItem>
                    <SelectItem value="7">Iulie</SelectItem>
                    <SelectItem value="8">August</SelectItem>
                    <SelectItem value="9">Septembrie</SelectItem>
                    <SelectItem value="10">Octombrie</SelectItem>
                    <SelectItem value="11">Noiembrie</SelectItem>
                    <SelectItem value="12">Decembrie</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Anul</Label>
                <Select 
                  value={centralizatorAngajatiYear.toString()}
                  onValueChange={(value) => setCentralizatorAngajatiYear(parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2023">2023</SelectItem>
                    <SelectItem value="2024">2024</SelectItem>
                    <SelectItem value="2025">2025</SelectItem>
                    <SelectItem value="2026">2026</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-lg text-sm">
              <p className="font-medium text-slate-700 mb-2">👥 Centralizatorul va include:</p>
              <ul className="space-y-1 text-slate-600">
                <li>• Toți angajații (șoferi)</li>
                <li>• Total km parcurși per angajat</li>
                <li>• Număr curse completate</li>
                <li>• Total general pentru luna selectată</li>
              </ul>
            </div>

            <div className="space-y-2">
              <Label className="text-base">Selectați formatul de export:</Label>
              <div className="flex gap-2">
                <Button 
                  onClick={() => handleExportCentralizatorAngajati('pdf')}
                  disabled={loading}
                  className="flex-1"
                  variant="outline"
                >
                  {loading ? '...' : '📄 PDF'}
                </Button>
                <Button 
                  onClick={() => handleExportCentralizatorAngajati('excel')}
                  disabled={loading}
                  className="flex-1"
                  variant="outline"
                >
                  {loading ? '...' : '📊 Excel'}
                </Button>
                <Button 
                  onClick={() => handleExportCentralizatorAngajati('csv')}
                  disabled={loading}
                  className="flex-1"
                  variant="outline"
                >
                  {loading ? '...' : '📋 CSV'}
                </Button>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setOpenCentralizatorAngajatiDialog(false)}>
                Închide
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={openCSVUploadDialog} onOpenChange={setOpenCSVUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Alimentări CSV</DialogTitle>
            <DialogDescription>
              Încărcați fișierul CSV primit de la furnizorul de combustibil
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Fișier CSV</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setCSVFile(e.target.files[0])}
              />
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg text-sm">
              <p className="font-medium text-blue-900 mb-2">ℹ️ Informații importante:</p>
              <ul className="space-y-1 text-blue-700">
                <li>• Fișierul trebuie să fie în format CSV</li>
                <li>• Se vor detecta automat duplicatele</li>
                <li>• Alimentările vor fi atribuite la curse existente sau create ca intrări noi</li>
                <li>• Veți vedea un preview înainte de confirmare</li>
              </ul>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setOpenCSVUploadDialog(false);
                setCSVFile(null);
              }}>
                Anulează
              </Button>
              <Button 
                onClick={handleCSVUpload}
                disabled={!csvFile || csvUploading}
              >
                {csvUploading ? 'Se procesează...' : 'Continuă →'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* CSV Preview Dialog */}
      <Dialog open={openCSVPreviewDialog} onOpenChange={setOpenCSVPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview Import Alimentări</DialogTitle>
            <DialogDescription>
              Verificați datele înainte de a confirma importul
            </DialogDescription>
          </DialogHeader>
          
          {csvPreviewData && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-sm text-green-700">Gata de import</p>
                  <p className="text-2xl font-bold text-green-900">{csvPreviewData.summary.ready}</p>
                </div>
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-sm text-yellow-700">Duplicate</p>
                  <p className="text-2xl font-bold text-yellow-900">{csvPreviewData.summary.duplicates}</p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg">
                  <p className="text-sm text-red-700">Erori</p>
                  <p className="text-2xl font-bold text-red-900">{csvPreviewData.summary.errors}</p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg">
                  <p className="text-sm text-orange-700">Alerte capacitate</p>
                  <p className="text-2xl font-bold text-orange-900">{csvPreviewData.summary.alerts}</p>
                </div>
              </div>

              {/* Preview Table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  <Table>
                    <TableHeader className="bg-slate-50 sticky top-0">
                      <TableRow>
                        <TableHead>Status</TableHead>
                        <TableHead>Mașină</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Litri</TableHead>
                        <TableHead>Cost</TableHead>
                        <TableHead>Locație</TableHead>
                        <TableHead>Detalii</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {csvPreviewData.preview.map((item, idx) => (
                        <TableRow key={idx} className={
                          item.status === 'ready' ? 'bg-green-50' :
                          item.status === 'duplicate' ? 'bg-yellow-50' :
                          'bg-red-50'
                        }>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              item.status === 'ready' ? 'bg-green-200 text-green-800' :
                              item.status === 'duplicate' ? 'bg-yellow-200 text-yellow-800' :
                              'bg-red-200 text-red-800'
                            }`}>
                              {item.status === 'ready' ? '✓ Gata' :
                               item.status === 'duplicate' ? '⚠ Duplicat' :
                               '✗ Eroare'}
                            </span>
                            {item.alert_type && (
                              <span className={`ml-1 px-2 py-1 rounded text-xs font-medium ${
                                item.alert_type === 'critical' ? 'bg-red-200 text-red-800' :
                                'bg-orange-200 text-orange-800'
                              }`}>
                                {item.alert_type === 'critical' ? '🚨 Peste capacitate' : '⚠️ Aproape plin'}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{item.car_name || item.license_plate}</div>
                              <div className="text-xs text-slate-500">{item.license_plate}</div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {new Date(item.date).toLocaleString('ro-RO', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell className="font-medium">{item.quantity}L</TableCell>
                          <TableCell>{item.cost.toFixed(2)} RON</TableCell>
                          <TableCell className="text-sm">{item.location}</TableCell>
                          <TableCell className="text-xs text-slate-600">
                            {item.message}
                            {item.capacity && (
                              <div className="text-slate-500 mt-1">Capacitate: {item.capacity}L</div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t">
                <div className="text-sm text-slate-600">
                  Se vor importa <span className="font-bold text-green-600">{csvPreviewData.summary.ready}</span> alimentări
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => {
                    setOpenCSVPreviewDialog(false);
                    setCSVPreviewData(null);
                    setCSVFile(null);
                  }}>
                    Anulează
                  </Button>
                  <Button 
                    onClick={handleConfirmImport}
                    disabled={loading || csvPreviewData.summary.ready === 0}
                  >
                    {loading ? 'Se importă...' : `✓ Confirmă Import (${csvPreviewData.summary.ready})`}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pontaj Edit Dialog */}
      <Dialog open={pontajDialog.open} onOpenChange={(open) => {
        if (!open) {
          setPontajDialog({ 
            open: false, employeeId: null, employeeName: '', day: null, isWeekend: false,
            selectedType: null, useInterval: false, endDay: null, intervalDocument: null, intervalDocumentName: null,
            customProgram: false, customProgramStart: '08:00', customProgramSfarsit: '17:00',
            customPauza: false, customPauzaStart: '12:00', customPauzaSfarsit: '13:00'
          });
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Editare Pontaj
            </DialogTitle>
            <DialogDescription>
              {pontajDialog.employeeName} - Ziua {pontajDialog.day}
              {pontajDialog.isWeekend && <span className="ml-2 text-orange-600">(Weekend)</span>}
              {pontajDialog.isFutureMonth && <span className="ml-2 text-blue-600">(Zi viitoare)</span>}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            {pontajDialog.isFutureMonth ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                ℹ️ Pentru zilele viitoare puteți adăuga doar <strong>CO</strong>, <strong>CM</strong>, <strong>CNC</strong>, <strong>CD</strong>, <strong>CFP</strong>, <strong>DEM</strong> sau <strong>Șterge</strong>.
              </div>
            ) : (
              <p className="text-sm text-slate-600">Selectează tipul de pontaj:</p>
            )}
            
            {/* Selector ore lucrate - doar pentru luna curentă și trecute */}
            {!pontajDialog.isFutureMonth && (
            <div className="mb-3">
              <p className="text-xs text-slate-500 mb-2">Ore lucrate:</p>
              <div className="grid grid-cols-4 gap-2">
                {[2, 4, 6, 8].map(ore => (
                  <button
                    key={ore}
                    onClick={() => setPontajDialog(prev => ({ ...prev, selectedType: String(ore), useInterval: false }))}
                    className={`p-2 rounded-lg border-2 transition-colors text-center ${
                      pontajDialog.selectedType === String(ore)
                        ? 'border-green-500 bg-green-100 ring-2 ring-green-300' 
                        : 'border-green-300 bg-green-50 hover:bg-green-100'
                    }`}
                  >
                    <div className="font-bold text-green-700">{ore}</div>
                    <div className="text-[10px] text-green-600">ore</div>
                  </button>
                ))}
              </div>
              
              {/* Opțiune program personalizat - doar pentru ore (nu CO, CM, etc.) */}
              {['2', '4', '6', '8'].includes(pontajDialog.selectedType) && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={pontajDialog.customProgram || false}
                      onChange={(e) => setPontajDialog(prev => ({ ...prev, customProgram: e.target.checked }))}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-600">Specifică programul pentru această zi</span>
                  </label>
                  
                  {pontajDialog.customProgram && (
                    <>
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div>
                          <label className="text-xs text-slate-500">Ora start</label>
                          <TimeSelect
                            value={pontajDialog.customProgramStart || '08:00'}
                            onChange={(val) => setPontajDialog(prev => ({ ...prev, customProgramStart: val }))}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Ora sfârșit</label>
                          <TimeSelect
                            value={pontajDialog.customProgramSfarsit || '17:00'}
                            onChange={(val) => setPontajDialog(prev => ({ ...prev, customProgramSfarsit: val }))}
                          />
                        </div>
                      </div>
                      
                      {/* Opțiune pauză de masă */}
                      <label className="flex items-center gap-2 cursor-pointer mt-3">
                        <input
                          type="checkbox"
                          checked={pontajDialog.customPauza || false}
                          onChange={(e) => setPontajDialog(prev => ({ ...prev, customPauza: e.target.checked }))}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                        <span className="text-sm text-slate-600">Include pauză de masă</span>
                      </label>
                      
                      {pontajDialog.customPauza && (
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div>
                            <label className="text-xs text-slate-500">Pauză de la</label>
                            <TimeSelect
                              value={pontajDialog.customPauzaStart || '12:00'}
                              onChange={(val) => setPontajDialog(prev => ({ ...prev, customPauzaStart: val }))}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Pauză până la</label>
                            <TimeSelect
                              value={pontajDialog.customPauzaSfarsit || '13:00'}
                              onChange={(val) => setPontajDialog(prev => ({ ...prev, customPauzaSfarsit: val }))}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            )}
            
            {/* Secțiune Alt tip - în zilele viitoare doar CO, CM, CNC, CD, CFP */}
            <p className="text-xs text-slate-500 mb-2">{pontajDialog.isFutureMonth ? 'Tipuri permise pentru zile viitoare:' : 'Sau alt tip:'}</p>
            <div className="grid grid-cols-3 gap-2">
              
              {/* CO - permis și în zilele viitoare, dezactivat în zilele de sărbătoare */}
              <button
                onClick={() => {
                  if (isSarbatoare(pontajDialog.day)) {
                    toast.error('În zilele libere legale nu se poate acorda Concediu de Odihnă (CO).');
                    return;
                  }
                  setPontajDialog(prev => ({ ...prev, selectedType: 'CO' }));
                }}
                className={`p-3 rounded-lg border-2 transition-colors text-center ${
                  isSarbatoare(pontajDialog.day)
                    ? 'border-slate-200 bg-slate-100 opacity-50 cursor-not-allowed'
                    : pontajDialog.selectedType === 'CO' 
                      ? 'border-blue-500 bg-blue-100 ring-2 ring-blue-300' 
                      : 'border-blue-300 bg-blue-50 hover:bg-blue-100'
                }`}
              >
                <div className="text-xl mb-0.5">🔵</div>
                <div className={`font-semibold text-sm ${isSarbatoare(pontajDialog.day) ? 'text-slate-400' : 'text-blue-700'}`}>CO</div>
              </button>
              
              {/* CM - permis în zilele de sărbătoare și lunile viitoare */}
              <button
                onClick={() => setPontajDialog(prev => ({ ...prev, selectedType: 'CM' }))}
                className={`p-3 rounded-lg border-2 transition-colors text-center ${
                  pontajDialog.selectedType === 'CM' 
                    ? 'border-orange-500 bg-orange-100 ring-2 ring-orange-300' 
                    : 'border-orange-300 bg-orange-50 hover:bg-orange-100'
                }`}
              >
                <div className="text-xl mb-0.5">🟠</div>
                <div className="font-semibold text-orange-700 text-sm">CM</div>
              </button>
              
              {/* CNC - Concediu Naștere Copil - permis și în zilele viitoare */}
              <button
                onClick={() => {
                  if (isSarbatoare(pontajDialog.day)) {
                    toast.error('În zilele libere legale nu se poate acorda Concediu Naștere Copil (CNC).');
                    return;
                  }
                  setPontajDialog(prev => ({ ...prev, selectedType: 'CNC' }));
                }}
                className={`p-3 rounded-lg border-2 transition-colors text-center ${
                  isSarbatoare(pontajDialog.day)
                    ? 'border-slate-200 bg-slate-100 opacity-50 cursor-not-allowed'
                    : pontajDialog.selectedType === 'CNC' 
                      ? 'border-pink-500 bg-pink-100 ring-2 ring-pink-300' 
                      : 'border-pink-300 bg-pink-50 hover:bg-pink-100'
                }`}
              >
                <div className="text-xl mb-0.5">👶</div>
                <div className={`font-semibold text-sm ${isSarbatoare(pontajDialog.day) ? 'text-slate-400' : 'text-pink-700'}`}>CNC</div>
              </button>
              
              {/* CD - Concediu Deces - permis și în zilele viitoare */}
              <button
                onClick={() => {
                  if (isSarbatoare(pontajDialog.day)) {
                    toast.error('În zilele libere legale nu se poate acorda Concediu Deces (CD).');
                    return;
                  }
                  setPontajDialog(prev => ({ ...prev, selectedType: 'CD' }));
                }}
                className={`p-3 rounded-lg border-2 transition-colors text-center ${
                  isSarbatoare(pontajDialog.day)
                    ? 'border-slate-200 bg-slate-100 opacity-50 cursor-not-allowed'
                    : pontajDialog.selectedType === 'CD' 
                      ? 'border-gray-700 bg-gray-200 ring-2 ring-gray-400' 
                      : 'border-gray-400 bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <div className="text-xl mb-0.5">🖤</div>
                <div className={`font-semibold text-sm ${isSarbatoare(pontajDialog.day) ? 'text-slate-400' : 'text-gray-700'}`}>CD</div>
              </button>
              
              {/* NM - dezactivat în zilele de sărbătoare și lunile viitoare */}
              {!pontajDialog.isFutureMonth && (
              <button
                onClick={() => {
                  if (isSarbatoare(pontajDialog.day)) {
                    toast.error('În zilele libere legale nu se poate acorda Absență Nemotivată (NM).');
                    return;
                  }
                  setPontajDialog(prev => ({ ...prev, selectedType: 'NM', useInterval: false }));
                }}
                className={`p-3 rounded-lg border-2 transition-colors text-center ${
                  isSarbatoare(pontajDialog.day)
                    ? 'border-slate-200 bg-slate-100 opacity-50 cursor-not-allowed'
                    : pontajDialog.selectedType === 'NM' 
                      ? 'border-red-500 bg-red-100 ring-2 ring-red-300' 
                      : 'border-red-300 bg-red-50 hover:bg-red-100'
                }`}
              >
                <div className="text-xl mb-0.5">🔴</div>
                <div className={`font-semibold text-sm ${isSarbatoare(pontajDialog.day) ? 'text-slate-400' : 'text-red-700'}`}>NM</div>
              </button>
              )}
              
              {/* CFP - permis și în zilele viitoare, dezactivat în zilele de sărbătoare */}
              <button
                onClick={() => {
                  if (isSarbatoare(pontajDialog.day)) {
                    toast.error('În zilele libere legale nu se poate acorda Concediu Fără Plată (CFP).');
                    return;
                  }
                  setPontajDialog(prev => ({ ...prev, selectedType: 'CFP' }));
                }}
                className={`p-3 rounded-lg border-2 transition-colors text-center ${
                  isSarbatoare(pontajDialog.day)
                    ? 'border-slate-200 bg-slate-100 opacity-50 cursor-not-allowed'
                    : pontajDialog.selectedType === 'CFP' 
                      ? 'border-yellow-500 bg-yellow-100 ring-2 ring-yellow-300' 
                      : 'border-yellow-300 bg-yellow-50 hover:bg-yellow-100'
                }`}
              >
                <div className="text-xl mb-0.5">🟡</div>
                <div className={`font-semibold text-sm ${isSarbatoare(pontajDialog.day) ? 'text-slate-400' : 'text-yellow-700'}`}>CFP</div>
              </button>
              
              {/* Demisie - permis și în zilele viitoare */}
              <button
                onClick={() => setPontajDialog(prev => ({ ...prev, selectedType: 'DEM', useInterval: false }))}
                className={`p-3 rounded-lg border-2 transition-colors text-center ${
                  pontajDialog.selectedType === 'DEM' 
                    ? 'border-purple-500 bg-purple-100 ring-2 ring-purple-300' 
                    : 'border-purple-300 bg-purple-50 hover:bg-purple-100'
                }`}
              >
                <div className="text-xl mb-0.5">🟣</div>
                <div className="font-semibold text-purple-700 text-sm">DEM</div>
              </button>
              
              {/* X - Șterge - disponibil și pentru zile viitoare pentru a corecta greșeli */}
              <button
                onClick={() => setPontajDialog(prev => ({ ...prev, selectedType: 'X' }))}
                className={`p-3 rounded-lg border-2 transition-colors text-center ${
                  pontajDialog.selectedType === 'X' 
                    ? 'border-slate-500 bg-slate-200 ring-2 ring-slate-300' 
                    : 'border-slate-300 bg-slate-100 hover:bg-slate-200'
                }`}
              >
                <div className="text-xl mb-0.5">⬜</div>
                <div className="font-semibold text-slate-700 text-sm">Șterge</div>
              </button>
            </div>
            
            {/* Flag "În așteptare" - marchează vizual celula fără a afecta calculele */}
            <div className="border-t pt-3">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="pending-checkbox">
                <input 
                  type="checkbox"
                  data-testid="pending-checkbox-input"
                  checked={pontajDialog.pending || false}
                  onChange={(e) => setPontajDialog(prev => ({ ...prev, pending: e.target.checked }))}
                  className="w-4 h-4 rounded border-orange-400 text-orange-500 focus:ring-orange-400"
                />
                <span className="text-sm font-medium text-orange-700">CO în așteptare</span>
                <span className="text-xs text-slate-400">(doar marcaj vizual, nu afectează calculele)</span>
              </label>
            </div>
            
            {/* Interval option - for CO, CM, CNC, CD, CFP, X (Șterge) - disponibil și în zile viitoare */}
            {(pontajDialog.selectedType === 'CO' || pontajDialog.selectedType === 'CM' || pontajDialog.selectedType === 'CNC' || pontajDialog.selectedType === 'CD' || pontajDialog.selectedType === 'CFP' || pontajDialog.selectedType === 'X') && (
              <div className="border-t pt-4 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={pontajDialog.useInterval}
                    onChange={(e) => setPontajDialog(prev => ({ 
                      ...prev, 
                      useInterval: e.target.checked,
                      endDay: e.target.checked ? prev.day : null
                    }))}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700">Aplică pentru mai multe zile</span>
                </label>
                
                {pontajDialog.useInterval && (
                  <div className="pl-6 space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-600">De la ziua</span>
                      <span className="font-semibold text-slate-800 bg-slate-100 px-2 py-1 rounded">{pontajDialog.day}</span>
                      <span className="text-sm text-slate-600">până la ziua</span>
                      <Select 
                        value={pontajDialog.endDay?.toString() || pontajDialog.day?.toString()} 
                        onValueChange={(v) => setPontajDialog(prev => ({ ...prev, endDay: parseInt(v) }))}
                      >
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: new Date(pontajYear, pontajMonth, 0).getDate() - pontajDialog.day + 1 }, (_, i) => (
                            <SelectItem key={pontajDialog.day + i} value={(pontajDialog.day + i).toString()}>
                              {pontajDialog.day + i}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {pontajDialog.selectedType === 'CO' && (
                      <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                        💡 CO va fi aplicat doar pe zilele lucrătoare (weekend-urile vor fi sărite)
                      </p>
                    )}
                    {pontajDialog.selectedType === 'CM' && (
                      <p className="text-xs text-orange-600 bg-orange-50 p-2 rounded">
                        💡 CM va fi aplicat pe toate zilele din interval (inclusiv weekend)
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Document Section - for CO, CM, CNC, CD, CFP, DEM, NM - permis pentru toate tipurile inclusiv zile viitoare */}
            {(pontajDialog.selectedType === 'CO' || pontajDialog.selectedType === 'CM' || pontajDialog.selectedType === 'CNC' || pontajDialog.selectedType === 'CD' || 
              pontajDialog.selectedType === 'CFP' || pontajDialog.selectedType === 'DEM' || 
              pontajDialog.selectedType === 'NM' || pontajDialog.selectedType === 'X') && (
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Document justificativ (opțional)
                  {pontajDialog.useInterval && <span className="text-xs text-slate-500">- se aplică la toate zilele</span>}
                </p>
                
                {(pontajDialog.hasDocument && !pontajDialog.useInterval) ? (
                  <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <span className="text-sm text-blue-800 font-medium truncate max-w-[150px]">{pontajDialog.documentFilename}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          loadDocumentPreview(
                            pontajDialog.employeeId, 
                            pontajDialog.day, 
                            pontajDialog.documentFilename, 
                            pontajDialog.currentValue
                          );
                        }}
                      >
                        👁 Vezi
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={async () => {
                          if (!confirm('Ești sigur că vrei să ștergi documentul?')) return;
                          try {
                            await axios.delete(`${API}/pontaj/document/${pontajDialog.employeeId}/${pontajYear}/${pontajMonth}/${pontajDialog.day}`, getAuthHeaders());
                            setPontajDialog(prev => ({ ...prev, hasDocument: false, documentFilename: null }));
                            fetchPontajData();
                            toast.success('Document șters');
                          } catch (e) { toast.error('Eroare la ștergere'); }
                        }}
                      >
                        🗑
                      </Button>
                    </div>
                  </div>
                ) : pontajDialog.intervalDocument ? (
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-green-800 font-medium truncate max-w-[200px]">{pontajDialog.intervalDocumentName}</span>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => setPontajDialog(prev => ({ ...prev, intervalDocument: null, intervalDocumentName: null }))}
                    >
                      🗑
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error('Fișierul este prea mare. Maximum 5MB.');
                          return;
                        }
                        
                        setPontajDialog(prev => ({ 
                          ...prev, 
                          intervalDocument: file,
                          intervalDocumentName: file.name
                        }));
                      }}
                    />
                    <div className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors">
                      <Plus className="w-5 h-5 text-slate-400" />
                      <span className="text-sm text-slate-600">Click pentru a încărca (PDF, JPG, PNG - max 5MB)</span>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Quick Add Document Button - pentru a adăuga document la o intrare existentă */}
            {pontajDialog.currentValue && !pontajDialog.hasDocument && !pontajDialog.useInterval && 
             ['CO', 'CM', 'CNC', 'CD', 'CFP', 'NM', 'DEM', 'X'].includes(pontajDialog.currentValue) && (
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Adaugă document la {pontajDialog.currentValue} existent
                </p>
                <div className="relative">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('Fișierul este prea mare. Maximum 5MB.');
                        return;
                      }
                      
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        await axios.post(
                          `${API}/pontaj/document?employee_id=${pontajDialog.employeeId}&year=${pontajYear}&month=${pontajMonth}&day=${pontajDialog.day}`,
                          formData,
                          {
                            ...getAuthHeaders(),
                            headers: {
                              ...getAuthHeaders().headers,
                              'Content-Type': 'multipart/form-data'
                            }
                          }
                        );
                        
                        toast.success('Document adăugat cu succes!');
                        await fetchPontajData();
                        
                        // Update dialog state to show the document
                        setPontajDialog(prev => ({
                          ...prev,
                          hasDocument: true,
                          documentFilename: file.name
                        }));
                      } catch (error) {
                        toast.error('Eroare la încărcarea documentului');
                      }
                    }}
                  />
                  <div className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors bg-green-50">
                    <Plus className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-700 font-medium">Click pentru a adăuga document (PDF, JPG, PNG - max 5MB)</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-between pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setPontajDialog({ 
                open: false, employeeId: null, employeeName: '', day: null, isWeekend: false,
                selectedType: null, useInterval: false, endDay: null, intervalDocument: null, intervalDocumentName: null
              })}
            >
              Anulează
            </Button>
            <Button 
              data-testid="pontaj-save-button"
              disabled={(!pontajDialog.selectedType && !pontajDialog.pending && pontajDialog.pending === pontajDialog.originalPending) || intervalSaving}
              onClick={async () => {
                const { selectedType, useInterval, endDay, intervalDocument, employeeId, day } = pontajDialog;
                
                if (selectedType === 'DEM') {
                  if (!confirm(`Ești sigur că vrei să marchezi DEMISIA pentru ${pontajDialog.employeeName}?`)) return;
                }
                
                setIntervalSaving(true);
                
                try {
                  if (useInterval && (selectedType === 'CO' || selectedType === 'CM' || selectedType === 'CNC' || selectedType === 'CD' || selectedType === 'CFP')) {
                    // Save interval for CO, CM, CNC, CD, CFP
                    const formData = new FormData();
                    formData.append('employee_id', employeeId);
                    formData.append('year', pontajYear);
                    formData.append('month', pontajMonth);
                    formData.append('start_day', day);
                    formData.append('end_day', endDay || day);
                    formData.append('value', selectedType);
                    if (intervalDocument) {
                      formData.append('file', intervalDocument);
                    }
                    
                    const response = await axios.post(
                      `${API}/pontaj/interval?employee_id=${employeeId}&year=${pontajYear}&month=${pontajMonth}&start_day=${day}&end_day=${endDay || day}&value=${selectedType}`,
                      formData,
                      {
                        ...getAuthHeaders(),
                        headers: {
                          ...getAuthHeaders().headers,
                          'Content-Type': 'multipart/form-data'
                        }
                      }
                    );
                    
                    await fetchPontajData();
                    let message = `${selectedType} aplicat pentru ${response.data.days_updated} zile`;
                    if (response.data.skipped_holidays > 0) {
                      message += ` (${response.data.skipped_holidays} zile libere legale sărite)`;
                    }
                    toast.success(message);
                  } else if (useInterval && selectedType === 'X') {
                    // Delete interval - only delete non-weekend days, leave weekends as X
                    const response = await axios.post(
                      `${API}/pontaj/sterge-interval?employee_id=${employeeId}&year=${pontajYear}&month=${pontajMonth}&start_day=${day}&end_day=${endDay || day}`,
                      {},
                      getAuthHeaders()
                    );
                    
                    await fetchPontajData();
                    toast.success(`Șterse ${response.data.days_deleted} zile (weekend-urile rămân X)`);
                  } else {
                    // Single day save
                    // Convertim orele (2, 4, 6, 8) în numere, X în null, restul rămân string (CO, CM, CNC, CD, etc.)
                    const numericHours = ['2', '4', '6', '8'];
                    const value = numericHours.includes(selectedType) ? parseInt(selectedType) : selectedType === 'X' ? null : selectedType;
                    
                    // Check if the previous value was DEM (to potentially reactivate employee)
                    const previousValue = pontajDialog.currentValue;
                    
                    // Check if employee has pontaj_pana_la set (resignation date)
                    const employee = drivers.find(d => d.id === employeeId);
                    const hasResignationDate = employee?.pontaj_pana_la != null;
                    
                    // If employee has resignation date and we're NOT setting DEM, ask to reactivate FIRST
                    let shouldReactivate = false;
                    if (hasResignationDate && selectedType !== 'DEM') {
                      shouldReactivate = confirm('Angajatul are setată o dată de demisie ("Până la..."). Vrei să anulezi demisia și să-l reactivezi în pontaj?');
                    }
                    
                    if (value !== null) {
                      // Include programul personalizat și pauza dacă sunt specificate
                      const { customProgram, customProgramStart, customProgramSfarsit, customPauza, customPauzaStart, customPauzaSfarsit, pending } = pontajDialog;
                      if (customProgram && customProgramStart && customProgramSfarsit) {
                        const pauzaStart = customPauza ? customPauzaStart : null;
                        const pauzaSfarsit = customPauza ? customPauzaSfarsit : null;
                        await savePontajEntry(employeeId, day, value, customProgramStart, customProgramSfarsit, pauzaStart, pauzaSfarsit, pending || false);
                      } else {
                        await savePontajEntry(employeeId, day, value, null, null, null, null, pending || false);
                      }
                    } else if (pontajDialog.pending) {
                      // Salvează doar flag-ul pending (fără valoare nouă - păstrează valoarea existentă sau null)
                      const existingValue = pontajDialog.currentValue;
                      await savePontajEntry(employeeId, day, existingValue, null, null, null, null, true);
                    } else if (pontajDialog.originalPending && !pontajDialog.pending) {
                      // Pending a fost debifat - salvează valoarea existentă cu pending=false
                      const existingValue = pontajDialog.currentValue;
                      await savePontajEntry(employeeId, day, existingValue, null, null, null, null, false);
                    } else {
                      // Delete entry
                      setPontajData(prev => {
                        const newData = { ...prev };
                        if (newData[employeeId]) {
                          delete newData[employeeId][day];
                        }
                        return newData;
                      });
                      await savePontajEntry(employeeId, day, null);
                    }
                    
                    // Upload document for single day if provided
                    if (intervalDocument && value !== null) {
                      const formData = new FormData();
                      formData.append('file', intervalDocument);
                      
                      await axios.post(
                        `${API}/pontaj/document?employee_id=${employeeId}&year=${pontajYear}&month=${pontajMonth}&day=${day}`,
                        formData,
                        {
                          ...getAuthHeaders(),
                          headers: {
                            ...getAuthHeaders().headers,
                            'Content-Type': 'multipart/form-data'
                          }
                        }
                      );
                    }
                    
                    // Refresh pontaj data to show changes
                    await fetchPontajData();
                    
                    // Handle DEM - set last month and mark remaining days as X
                    if (selectedType === 'DEM') {
                      try {
                        // Call the demisie endpoint which sets DEM, marks remaining days as X, and sets pontaj_pana_la
                        await axios.post(
                          `${API}/pontaj/demisie?employee_id=${employeeId}&year=${pontajYear}&month=${pontajMonth}&day=${day}`,
                          {},
                          getAuthHeaders()
                        );
                        await fetchPontajData();
                        fetchDrivers();
                        toast.success('Demisie înregistrată. Zilele rămase au fost marcate cu X.');
                      } catch (e) {
                        console.error('Error setting demisie:', e);
                        toast.error('Eroare la înregistrarea demisiei');
                      }
                    } else if (shouldReactivate) {
                      // User confirmed to reactivate the employee - clear ALL DEM and X entries from ALL months
                      try {
                        await axios.post(
                          `${API}/pontaj/anuleaza-demisie-complet?employee_id=${employeeId}`,
                          {},
                          getAuthHeaders()
                        );
                        
                        await fetchPontajData();
                        fetchDrivers();
                        toast.success('Demisie anulată complet. Toate marcajele X au fost șterse.');
                      } catch (e) {
                        console.error('Error canceling demisie:', e);
                        toast.error('Eroare la anularea demisiei');
                      }
                    } else {
                      const messages = {
                        '8': '8 ore',
                        'CO': 'Concediu Odihnă',
                        'CM': 'Concediu Medical',
                        'NM': 'Absență Nemotivată',
                        'CFP': 'Concediu Fără Plată',
                        'X': 'Șters'
                      };
                      toast.success(`Pontaj salvat: ${messages[selectedType] || selectedType}`);
                    }
                  }
                } catch (error) {
                  console.error('Error saving pontaj:', error);
                  toast.error('Eroare la salvare');
                } finally {
                  setIntervalSaving(false);
                  setPontajDialog({ 
                    open: false, employeeId: null, employeeName: '', day: null, isWeekend: false,
                    selectedType: null, useInterval: false, endDay: null, intervalDocument: null, intervalDocumentName: null
                  });
                }
              }}
              className={intervalSaving ? 'opacity-50' : ''}
            >
              {intervalSaving ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Se salvează...
                </>
              ) : (
                'Salvează'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={documentPreviewDialog.open} onOpenChange={(open) => {
        if (!open) {
          // Cleanup blob URL to free memory
          if (documentPreviewDialog.blobUrl) {
            URL.revokeObjectURL(documentPreviewDialog.blobUrl);
          }
          setDocumentPreviewDialog({ open: false, employeeId: null, employeeName: '', day: null, filename: null, blobUrl: null, loading: false });
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Document - {documentPreviewDialog.employeeName}, Ziua {documentPreviewDialog.day}
            </DialogTitle>
            <DialogDescription>
              {documentPreviewDialog.value === 'CO' && 'Concediu Odihnă'}
              {documentPreviewDialog.value === 'CNC' && 'Concediu Naștere Copil'}
              {documentPreviewDialog.value === 'CD' && 'Concediu Deces'}
              {documentPreviewDialog.value === 'CM' && 'Concediu Medical'}
              {documentPreviewDialog.value === 'CFP' && 'Concediu Fără Plată'}
              {documentPreviewDialog.value === 'DEM' && 'Demisie'}
              {documentPreviewDialog.value === 'NM' && 'Absență Nemotivată'}
              {documentPreviewDialog.filename && ` - ${documentPreviewDialog.filename}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto flex items-center justify-center min-h-[50vh]">
            {documentPreviewDialog.loading ? (
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                <span className="text-slate-600">Se încarcă documentul...</span>
              </div>
            ) : documentPreviewDialog.blobUrl ? (
              documentPreviewDialog.filename?.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={documentPreviewDialog.blobUrl}
                  className="w-full h-[70vh] border rounded-lg"
                  title="Document Preview"
                />
              ) : (
                <img
                  src={documentPreviewDialog.blobUrl}
                  alt="Document Preview"
                  className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                />
              )
            ) : (
              <div className="text-slate-500">Nu s-a putut încărca documentul</div>
            )}
          </div>
          
          <div className="flex justify-between pt-2">
            <Button 
              variant="outline"
              onClick={() => {
                if (documentPreviewDialog.blobUrl) {
                  const link = document.createElement('a');
                  link.href = documentPreviewDialog.blobUrl;
                  link.download = documentPreviewDialog.filename || 'document';
                  link.click();
                }
              }}
              disabled={!documentPreviewDialog.blobUrl}
            >
              <Download className="w-4 h-4 mr-2" />
              Descarcă
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                if (documentPreviewDialog.blobUrl) {
                  URL.revokeObjectURL(documentPreviewDialog.blobUrl);
                }
                setDocumentPreviewDialog({ open: false, employeeId: null, employeeName: '', day: null, filename: null, blobUrl: null, loading: false });
              }}
            >
              Închide
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog pentru setarea ultimei luni în pontaj */}
      <Dialog open={pontajEndDateDialog.open} onOpenChange={(open) => {
        if (!open) setPontajEndDateDialog({ open: false, userId: null, userName: '', currentDate: null });
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarX className="w-5 h-5 text-orange-600" />
              Setează Ultima Lună în Pontaj
            </DialogTitle>
            <DialogDescription>
              Alege ultima lună în care <span className="font-semibold">{pontajEndDateDialog.userName}</span> va apărea în pontaj. 
              Din luna următoare, angajatul nu va mai fi afișat.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Label className="text-sm text-slate-600 mb-1.5 block">Luna</Label>
                <Select 
                  value={selectedEndMonth.toString()} 
                  onValueChange={(v) => setSelectedEndMonth(parseInt(v))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 
                      'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'].map((m, i) => (
                      <SelectItem key={i+1} value={(i+1).toString()}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="text-sm text-slate-600 mb-1.5 block">Anul</Label>
                <Select 
                  value={selectedEndYear.toString()} 
                  onValueChange={(v) => setSelectedEndYear(parseInt(v))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027, 2028].map((y) => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
              <p className="font-medium">📌 Ce se întâmplă:</p>
              <p className="mt-1">Angajatul va fi afișat în pontaj până în <span className="font-semibold">
                {['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 
                  'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'][selectedEndMonth - 1]} {selectedEndYear}
              </span> inclusiv.</p>
            </div>
          </div>
          
          <div className="flex justify-between gap-2">
            {pontajEndDateDialog.currentDate && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    await axios.patch(`${API}/users/${pontajEndDateDialog.userId}/pontaj`, 
                      { in_pontaj: true, pontaj_pana_la: null }, 
                      getAuthHeaders()
                    );
                    fetchDrivers();
                    toast.success('Limită anulată - angajat activ permanent');
                    setPontajEndDateDialog({ open: false, userId: null, userName: '', currentDate: null });
                  } catch (e) { toast.error('Eroare la actualizare'); }
                }}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Anulează limita
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button
                variant="outline"
                onClick={() => setPontajEndDateDialog({ open: false, userId: null, userName: '', currentDate: null })}
              >
                Renunță
              </Button>
              <Button
                onClick={async () => {
                  try {
                    // Set date to last day of the selected month
                    const endDate = new Date(selectedEndYear, selectedEndMonth, 0);
                    await axios.patch(`${API}/users/${pontajEndDateDialog.userId}/pontaj`, 
                      { in_pontaj: true, pontaj_pana_la: endDate.toISOString() }, 
                      getAuthHeaders()
                    );
                    fetchDrivers();
                    toast.success('Ultima lună setată cu succes');
                    setPontajEndDateDialog({ open: false, userId: null, userName: '', currentDate: null });
                  } catch (e) { toast.error('Eroare la actualizare'); }
                }}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Salvează
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import History Dialog */}
      <Dialog open={showImportHistory} onOpenChange={setShowImportHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📋 Istoric Importuri Combustibil</DialogTitle>
            <DialogDescription>
              Lista tuturor importurilor CSV efectuate
            </DialogDescription>
          </DialogHeader>
          
          {importBatches.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p className="text-lg">Nu există importuri</p>
              <p className="text-sm mt-2">Importați un fișier CSV pentru a vedea istoricul aici</p>
            </div>
          ) : (
            <div className="space-y-3">
              {importBatches.map((batch, index) => (
                <div 
                  key={batch.id} 
                  className={`p-4 rounded-lg border ${index === 0 ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {index === 0 && (
                          <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                            Cel mai recent
                          </span>
                        )}
                        <span className="text-sm text-slate-500">
                          {new Date(batch.imported_at).toLocaleString('ro-RO', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <div className="text-lg font-semibold text-slate-800">
                        {batch.items_count} alimentări importate
                      </div>
                      <div className="text-sm text-slate-600 mt-1">
                        De către: <span className="font-medium">{batch.imported_by || 'Admin'}</span>
                      </div>
                      {batch.filename && (
                        <div className="text-xs text-slate-500 mt-1">
                          Fișier: {batch.filename}
                        </div>
                      )}
                    </div>
                    {index === 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          setShowImportHistory(false);
                          handleUndoLastImport();
                        }}
                        disabled={loading}
                      >
                        ↩️ Anulează
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Close Trip Dialog */}
      <Dialog open={openCloseTripDialog} onOpenChange={setOpenCloseTripDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Închide Cursă</DialogTitle>
            <DialogDescription>
              {closingTrip && `Închideți cursa pentru ${closingTrip.driver_name} - ${closingTrip.car_name}`}
            </DialogDescription>
          </DialogHeader>
          {closingTrip && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg text-sm">
                <p className="font-medium text-blue-900">Informații cursă:</p>
                <p className="text-blue-700">Km Start: <span className="font-semibold">{closingTrip.start_km || 0}</span></p>
                <p className="text-blue-700">Ora plecare: {new Date(closingTrip.start_time).toLocaleString('ro-RO')}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_km">Kilometraj Final * <span className="text-red-600">(obligatoriu)</span></Label>
                <Input
                  id="end_km"
                  type="number"
                  placeholder="Ex: 25000"
                  value={closeTripData.end_km}
                  onChange={(e) => setCloseTripData({...closeTripData, end_km: e.target.value})}
                  min={closingTrip.start_km || 0}
                />
                {closeTripData.end_km && (
                  <p className="text-sm text-slate-600">
                    Distanță: <span className="font-semibold">{parseInt(closeTripData.end_km) - (closingTrip.start_km || 0)} km</span>
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fuel_liters">Combustibil (litri)</Label>
                  <Input
                    id="fuel_liters"
                    type="number"
                    step="0.1"
                    placeholder="Ex: 45.5"
                    value={closeTripData.fuel_liters}
                    onChange={(e) => setCloseTripData({...closeTripData, fuel_liters: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fuel_cost">Cost combustibil (RON)</Label>
                  <Input
                    id="fuel_cost"
                    type="number"
                    step="0.01"
                    placeholder="Ex: 250.50"
                    value={closeTripData.fuel_cost}
                    onChange={(e) => setCloseTripData({...closeTripData, fuel_cost: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Motiv închidere</Label>
                <Select 
                  value={closeTripData.closing_reason}
                  onValueChange={(value) => setCloseTripData({...closeTripData, closing_reason: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="final_day">Sfârșit cursă</SelectItem>
                    <SelectItem value="admin_closed">Închis de admin</SelectItem>
                    <SelectItem value="driver_forgot">Șofer uitat</SelectItem>
                    <SelectItem value="other">Altele</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setOpenCloseTripDialog(false);
                    setClosingTrip(null);
                  }}
                >
                  Anulează
                </Button>
                <Button 
                  onClick={handleForceCloseTrip}
                  disabled={loading || !closeTripData.end_km}
                >
                  {loading ? 'Se închide...' : 'Închide Cursă'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Sărbători Legale */}
      <Dialog open={sarbatoriDialog} onOpenChange={setSarbatoriDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-violet-600" />
              Sărbători Legale
            </DialogTitle>
            <DialogDescription>
              Adăugați sau eliminați sărbători legale. Acestea vor fi marcate automat cu "X" în pontaj.
            </DialogDescription>
          </DialogHeader>
          
          {/* Formular adăugare sărbătoare */}
          <div className="border rounded-lg p-4 bg-violet-50">
            <h4 className="font-medium text-violet-800 mb-3">Adaugă sărbătoare nouă</h4>
            <div className="grid grid-cols-5 gap-3">
              <div>
                <Label className="text-xs">Zi *</Label>
                <Select 
                  value={newSarbatoare.zi} 
                  onValueChange={(v) => setNewSarbatoare({...newSarbatoare, zi: v})}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Zi" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({length: 31}, (_, i) => (
                      <SelectItem key={i+1} value={String(i+1)}>{i+1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Luna *</Label>
                <Select 
                  value={newSarbatoare.luna} 
                  onValueChange={(v) => setNewSarbatoare({...newSarbatoare, luna: v})}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Luna" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Ianuarie</SelectItem>
                    <SelectItem value="2">Februarie</SelectItem>
                    <SelectItem value="3">Martie</SelectItem>
                    <SelectItem value="4">Aprilie</SelectItem>
                    <SelectItem value="5">Mai</SelectItem>
                    <SelectItem value="6">Iunie</SelectItem>
                    <SelectItem value="7">Iulie</SelectItem>
                    <SelectItem value="8">August</SelectItem>
                    <SelectItem value="9">Septembrie</SelectItem>
                    <SelectItem value="10">Octombrie</SelectItem>
                    <SelectItem value="11">Noiembrie</SelectItem>
                    <SelectItem value="12">Decembrie</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Denumire *</Label>
                <Input 
                  placeholder="Ex: Boboteaza"
                  value={newSarbatoare.nume}
                  onChange={(e) => setNewSarbatoare({...newSarbatoare, nume: e.target.value})}
                  className="bg-white"
                />
              </div>
              <div>
                <Label className="text-xs">An (opțional)</Label>
                <Input 
                  placeholder="2025"
                  type="number"
                  value={newSarbatoare.an}
                  onChange={(e) => setNewSarbatoare({...newSarbatoare, an: e.target.value})}
                  className="bg-white"
                />
                <p className="text-[10px] text-violet-600 mt-1">Lăsați gol pentru recurent</p>
              </div>
            </div>
            <Button 
              onClick={addSarbatoare}
              className="mt-3 bg-violet-600 hover:bg-violet-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              Adaugă sărbătoare
            </Button>
          </div>
          
          {/* Lista sărbători existente */}
          <div className="mt-4">
            <h4 className="font-medium mb-3">Sărbători existente</h4>
            {sarbatoriLoading ? (
              <div className="text-center py-4 text-slate-500">Se încarcă...</div>
            ) : sarbatoriList.length === 0 ? (
              <div className="text-center py-4 text-slate-500 bg-slate-50 rounded-lg">
                Nu există sărbători configurate. Adăugați prima sărbătoare folosind formularul de mai sus.
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Zi</th>
                      <th className="px-3 py-2 text-left">Luna</th>
                      <th className="px-3 py-2 text-left">Denumire</th>
                      <th className="px-3 py-2 text-left">An</th>
                      <th className="px-3 py-2 text-center w-20">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sarbatoriList
                      .sort((a, b) => {
                        if (a.luna !== b.luna) return a.luna - b.luna;
                        return a.zi - b.zi;
                      })
                      .map((sarb) => {
                        const luniNume = ['', 'Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                        return (
                          <tr key={sarb.id} className="border-t hover:bg-slate-50">
                            <td className="px-3 py-2 font-medium">{sarb.zi}</td>
                            <td className="px-3 py-2">{luniNume[sarb.luna]}</td>
                            <td className="px-3 py-2">{sarb.nume}</td>
                            <td className="px-3 py-2 text-slate-500">
                              {sarb.an || <span className="text-violet-600">Anual</span>}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteSarbatoare(sarb.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Export Tichete Masă */}
      <Dialog open={ticheteMasaDialog} onOpenChange={setTicheteMasaDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-purple-600" />
              Export Tichete Masă
            </DialogTitle>
            <DialogDescription>
              Exportă lista angajaților cu numărul de tichete de masă pentru luna selectată.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Luna și Anul */}
            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-sm text-purple-700 font-medium">
                Luna: {['', 'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 
                        'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'][pontajMonth]} {pontajYear}
              </p>
            </div>
            
            {/* Valoare unitară tichet */}
            <div>
              <Label className="text-sm font-medium">Valoare unitară tichet (RON)</Label>
              <Input 
                type="number"
                value={valoareTichet}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) || 0;
                  setValoareTichet(val);
                  setTicheteSummary(prev => ({
                    ...prev,
                    valoareTotala: prev.totalTichete * val
                  }));
                }}
                className="mt-1"
                min="0"
                step="0.01"
              />
            </div>
            
            {/* Sumar */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total angajați:</span>
                <span className="font-medium">{ticheteSummary.totalAngajati}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total tichete:</span>
                <span className="font-medium">{ticheteSummary.totalTichete}</span>
              </div>
              <div className="border-t pt-2 flex justify-between">
                <span className="text-gray-700 font-medium">Valoare totală:</span>
                <span className="font-bold text-purple-700">{(ticheteSummary.totalTichete * valoareTichet).toLocaleString('ro-RO')} RON</span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTicheteMasaDialog(false)}>
              Anulează
            </Button>
            <Button 
              onClick={exportTicheteMasa}
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {loading ? 'Se exportă...' : 'Exportă Excel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Import Excel Pontaj */}
      <Dialog open={importDialog} onOpenChange={(open) => {
        if (!open) {
          setImportDialog(false);
          setImportFile(null);
          setImportPreview(null);
          setImportMappings({});
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Import Pontaj din Excel - {["", "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie", "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"][pontajMonth]} {pontajYear}
            </DialogTitle>
            <DialogDescription>
              Încărcați un fișier Excel cu pontajul pentru luna selectată. Sistemul va identifica angajații și va importa datele.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Step 1: File Upload */}
            {!importPreview && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    id="import-excel-file"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      setImportFile(file);
                      setImportLoading(true);
                      
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        const response = await axios.post(
                          `${API}/pontaj/import-excel/preview?year=${pontajYear}&month=${pontajMonth}`,
                          formData,
                          {
                            ...getAuthHeaders(),
                            headers: {
                              ...getAuthHeaders().headers,
                              'Content-Type': 'multipart/form-data'
                            }
                          }
                        );
                        
                        setImportPreview(response.data);
                        
                        // Auto-set mappings for matched employees
                        const autoMappings = {};
                        response.data.employees.forEach(emp => {
                          if (emp.matched) {
                            autoMappings[emp.row] = {
                              employee_id: emp.matched,
                              name: emp.name,
                              create_new: false
                            };
                          }
                        });
                        setImportMappings(autoMappings);
                        
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Eroare la procesarea fișierului');
                      } finally {
                        setImportLoading(false);
                      }
                    }}
                  />
                  <label htmlFor="import-excel-file" className="cursor-pointer">
                    <FileText className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                    <p className="text-lg font-medium text-slate-700">Click pentru a selecta fișierul Excel</p>
                    <p className="text-sm text-slate-500 mt-1">.xlsx sau .xls</p>
                  </label>
                </div>
                
                {importLoading && (
                  <div className="flex items-center justify-center gap-2 py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span className="text-slate-600">Se analizează fișierul...</span>
                  </div>
                )}
              </div>
            )}
            
            {/* Step 2: Preview & Mapping */}
            {importPreview && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-blue-800">
                    Fișier: {importFile?.name} | {importPreview.employees.length} angajați găsiți
                  </p>
                  <p className="text-blue-700 mt-1">
                    Verificați potrivirile și selectați angajații pentru care să se importe datele.
                  </p>
                </div>
                
                <div className="max-h-96 overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Import</th>
                        <th className="px-3 py-2 text-left font-medium">Nume în Excel</th>
                        <th className="px-3 py-2 text-left font-medium">Potrivire în sistem</th>
                        <th className="px-3 py-2 text-left font-medium">Acțiune</th>
                        <th className="px-3 py-2 text-left font-medium">Data demisiei</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.employees.map((emp, idx) => (
                        <tr key={idx} className={`border-t ${importMappings[emp.row] ? 'bg-green-50' : ''}`}>
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={!!importMappings[emp.row]}
                              onChange={(e) => {
                                if (!e.target.checked) {
                                  const newMappings = { ...importMappings };
                                  delete newMappings[emp.row];
                                  setImportMappings(newMappings);
                                } else if (emp.matched) {
                                  setImportMappings({
                                    ...importMappings,
                                    [emp.row]: {
                                      employee_id: emp.matched,
                                      name: emp.name,
                                      create_new: false
                                    }
                                  });
                                }
                              }}
                              className="w-4 h-4"
                            />
                          </td>
                          <td className="px-3 py-2 font-medium">{emp.name}</td>
                          <td className="px-3 py-2">
                            {emp.matched ? (
                              <span className="text-green-700">
                                ✓ {emp.matched_name} ({Math.round(emp.match_score * 100)}%)
                              </span>
                            ) : emp.possible_matches.length > 0 ? (
                              <select
                                className="w-full border rounded px-2 py-1 text-sm"
                                value={importMappings[emp.row]?.employee_id || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === 'create_new') {
                                    setImportMappings({
                                      ...importMappings,
                                      [emp.row]: {
                                        employee_id: null,
                                        name: emp.name,
                                        create_new: true,
                                        pontaj_pana_la: null
                                      }
                                    });
                                  } else if (value) {
                                    const match = emp.possible_matches.find(m => m.id === value);
                                    setImportMappings({
                                      ...importMappings,
                                      [emp.row]: {
                                        employee_id: value,
                                        name: emp.name,
                                        create_new: false
                                      }
                                    });
                                  } else {
                                    const newMappings = { ...importMappings };
                                    delete newMappings[emp.row];
                                    setImportMappings(newMappings);
                                  }
                                }}
                              >
                                <option value="">-- Selectează --</option>
                                {emp.possible_matches.map(match => (
                                  <option key={match.id} value={match.id}>
                                    {match.name} ({Math.round(match.score * 100)}%)
                                    {match.fost_angajat ? ' [fost]' : ''}
                                  </option>
                                ))}
                                <option value="create_new">➕ Creează angajat nou (fost)</option>
                              </select>
                            ) : (
                              <span className="text-red-600">Nicio potrivire</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {!emp.matched && emp.possible_matches.length === 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setImportMappings({
                                    ...importMappings,
                                    [emp.row]: {
                                      employee_id: null,
                                      name: emp.name,
                                      create_new: true,
                                      pontaj_pana_la: null
                                    }
                                  });
                                }}
                                className={importMappings[emp.row]?.create_new ? 'bg-blue-100' : ''}
                              >
                                {importMappings[emp.row]?.create_new ? '✓ Se va crea' : 'Creează nou'}
                              </Button>
                            )}
                          </td>
                          {/* Coloana pentru data demisiei - doar pentru angajați noi */}
                          <td className="px-3 py-2">
                            {importMappings[emp.row]?.create_new && (
                              <input
                                type="date"
                                className="border rounded px-2 py-1 text-sm w-36"
                                value={importMappings[emp.row]?.pontaj_pana_la || ''}
                                onChange={(e) => {
                                  setImportMappings({
                                    ...importMappings,
                                    [emp.row]: {
                                      ...importMappings[emp.row],
                                      pontaj_pana_la: e.target.value
                                    }
                                  });
                                }}
                                placeholder="Data demisie"
                              />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-600">
                    {Object.keys(importMappings).length} din {importPreview.employees.length} selectați pentru import
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setImportPreview(null);
                      setImportFile(null);
                      setImportMappings({});
                    }}
                  >
                    Alege alt fișier
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialog(false)}>
              Anulează
            </Button>
            {importPreview && (
              <Button
                onClick={async () => {
                  if (Object.keys(importMappings).length === 0) {
                    toast.error('Selectați cel puțin un angajat pentru import');
                    return;
                  }
                  
                  setImportLoading(true);
                  try {
                    const formData = new FormData();
                    formData.append('file', importFile);
                    formData.append('year', pontajYear);
                    formData.append('month', pontajMonth);
                    formData.append('mappings', JSON.stringify(importMappings));
                    
                    const response = await axios.post(
                      `${API}/pontaj/import-excel/execute`,
                      formData,
                      {
                        ...getAuthHeaders(),
                        headers: {
                          ...getAuthHeaders().headers,
                          'Content-Type': 'multipart/form-data'
                        }
                      }
                    );
                    
                    let message = `Import finalizat! ${response.data.imported_count} angajați importați.`;
                    if (response.data.created_employees.length > 0) {
                      message += ` ${response.data.created_employees.length} angajați noi creați.`;
                    }
                    if (response.data.errors.length > 0) {
                      message += ` ${response.data.errors.length} erori.`;
                    }
                    
                    toast.success(message);
                    
                    // Refresh data
                    await fetchPontajData();
                    await fetchDrivers();
                    
                    setImportDialog(false);
                    setImportFile(null);
                    setImportPreview(null);
                    setImportMappings({});
                    
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Eroare la import');
                  } finally {
                    setImportLoading(false);
                  }
                }}
                disabled={importLoading || Object.keys(importMappings).length === 0}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {importLoading ? 'Se importă...' : `Importă ${Object.keys(importMappings).length} angajați`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Adaugă Echipament */}
      <Dialog open={showAddEquipmentDialog} onOpenChange={setShowAddEquipmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adaugă Echipament</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Denumire</Label><Input value={newEquipment.name} onChange={e => setNewEquipment({...newEquipment, name: e.target.value})} placeholder="Ex: Stivuitor Linde H30" /></div>
            <div><Label>Tip</Label><Input value={newEquipment.type} onChange={e => setNewEquipment({...newEquipment, type: e.target.value})} placeholder="Ex: Stivuitor, Nacela, Transpalet" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Nr. înregistrare ISCIR</Label><Input value={newEquipment.nr_iscir} onChange={e => setNewEquipment({...newEquipment, nr_iscir: e.target.value})} placeholder="Ex: RS-12345" /></div>
              <div><Label>Nr. fabricație</Label><Input value={newEquipment.nr_fabricatie} onChange={e => setNewEquipment({...newEquipment, nr_fabricatie: e.target.value})} placeholder="Ex: H30D-12345" /></div>
              <div><Label>An fabricație</Label><Input value={newEquipment.an_fabricatie} onChange={e => setNewEquipment({...newEquipment, an_fabricatie: e.target.value})} placeholder="Ex: 2019" /></div>
            </div>
            <div><Label>Data expirare ITP</Label><Input type="date" value={newEquipment.itp_expiry} onChange={e => setNewEquipment({...newEquipment, itp_expiry: e.target.value})} /></div>
            <Button onClick={handleAddEquipment} disabled={!newEquipment.name} className="w-full">Adaugă</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Editare Echipament */}
      <Dialog open={showEditEquipmentDialog} onOpenChange={setShowEditEquipmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editare Echipament</DialogTitle>
          </DialogHeader>
          {editingEquipment && (
            <div className="space-y-3">
              <div><Label>Denumire</Label><Input value={editingEquipment.name} onChange={e => setEditingEquipment({...editingEquipment, name: e.target.value})} /></div>
              <div><Label>Tip</Label><Input value={editingEquipment.type || ''} onChange={e => setEditingEquipment({...editingEquipment, type: e.target.value})} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Nr. înregistrare ISCIR</Label><Input value={editingEquipment.nr_iscir || ''} onChange={e => setEditingEquipment({...editingEquipment, nr_iscir: e.target.value})} /></div>
                <div><Label>Nr. fabricație</Label><Input value={editingEquipment.nr_fabricatie || ''} onChange={e => setEditingEquipment({...editingEquipment, nr_fabricatie: e.target.value})} /></div>
                <div><Label>An fabricație</Label><Input value={editingEquipment.an_fabricatie || ''} onChange={e => setEditingEquipment({...editingEquipment, an_fabricatie: e.target.value})} /></div>
              </div>
              <div><Label>Data expirare ITP</Label><Input type="date" value={editingEquipment.itp_expiry || ''} onChange={e => setEditingEquipment({...editingEquipment, itp_expiry: e.target.value})} /></div>
              <div>
                <Label>Raport Inspecție ITP (foto/PDF, mai multe pagini)</Label>
                <Input type="file" accept="image/*,.pdf" multiple onChange={async (e) => {
                  const files = Array.from(e.target.files);
                  const dataUrls = await Promise.all(files.map(f => fileToDataUrl(f)));
                  setEditingEquipment({...editingEquipment, itp_documents: [...(editingEquipment.itp_documents || []), ...dataUrls]});
                }} />
                {editingEquipment.itp_documents && editingEquipment.itp_documents.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <span className="text-xs text-slate-500">{editingEquipment.itp_documents.length} document(e) încărcate</span>
                    <div className="flex gap-2 flex-wrap">
                      {editingEquipment.itp_documents.map((doc, i) => (
                        <div key={i} className="relative group">
                          {doc.startsWith('data:image') ? (
                            <img src={doc} alt={`Pag ${i+1}`} className="w-16 h-20 object-cover rounded border" />
                          ) : (
                            <div className="w-16 h-20 rounded border bg-slate-50 flex items-center justify-center text-xs text-slate-500">PDF {i+1}</div>
                          )}
                          <button onClick={() => setEditingEquipment({...editingEquipment, itp_documents: editingEquipment.itp_documents.filter((_, j) => j !== i)})}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] leading-none flex items-center justify-center opacity-0 group-hover:opacity-100">✕</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <Button onClick={handleEditEquipment} disabled={!editingEquipment.name} className="w-full">Salvează</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Adaugă Revizie sau Reparație */}
      <Dialog open={showAddRevisionDialog} onOpenChange={setShowAddRevisionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adaugă Revizie sau Reparație</DialogTitle>
            <DialogDescription>Poți adăuga și înregistrări din trecut pentru istoric complet</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Categorie</Label>
              <div className="flex gap-2 mt-1">
                <button type="button" onClick={() => setNewRevision({...newRevision, category: 'revizie', type: 'ITP'})}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    newRevision.category === 'revizie' ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                  }`}>Revizie</button>
                <button type="button" onClick={() => setNewRevision({...newRevision, category: 'reparatie', type: 'Reparație generală'})}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    newRevision.category === 'reparatie' ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                  }`}>Reparație</button>
              </div>
            </div>
            <div><Label>Data</Label><Input type="date" value={newRevision.date} onChange={e => setNewRevision({...newRevision, date: e.target.value})} /></div>
            <div>
              <Label>Tip</Label>
              <select className="w-full border rounded-md px-3 py-2 text-sm" value={newRevision.type} onChange={e => setNewRevision({...newRevision, type: e.target.value})}>
                {newRevision.category === 'revizie' ? (
                  <>
                    <option value="ITP">ITP</option>
                    <option value="Revizie periodică">Revizie periodică</option>
                    <option value="Verificare ISCIR">Verificare ISCIR</option>
                    <option value="Revizie tehnică">Revizie tehnică</option>
                    <option value="Altele">Altele</option>
                  </>
                ) : (
                  <>
                    <option value="Reparație generală">Reparație generală</option>
                    <option value="Reparație motor">Reparație motor</option>
                    <option value="Reparație hidraulică">Reparație hidraulică</option>
                    <option value="Reparație electrică">Reparație electrică</option>
                    <option value="Înlocuire piese">Înlocuire piese</option>
                    <option value="Altele">Altele</option>
                  </>
                )}
              </select>
            </div>
            <div><Label>Cost (RON)</Label><Input type="number" value={newRevision.cost} onChange={e => setNewRevision({...newRevision, cost: e.target.value})} placeholder="Ex: 500" /></div>
            <div><Label>Data următoarei revizii</Label><Input type="date" value={newRevision.next_revision_date} onChange={e => setNewRevision({...newRevision, next_revision_date: e.target.value})} /></div>
            <div><Label>Observații</Label><Input value={newRevision.notes} onChange={e => setNewRevision({...newRevision, notes: e.target.value})} placeholder="Detalii" /></div>
            <div>
              <Label>Document (PV revizie / deviz reparație)</Label>
              <Input type="file" accept="image/*,.pdf" multiple onChange={async (e) => {
                const files = Array.from(e.target.files);
                const dataUrls = await Promise.all(files.map(f => fileToDataUrl(f)));
                setNewRevision(prev => ({...prev, documents: [...(prev.documents || []), ...dataUrls]}));
              }} />
              {newRevision.documents && newRevision.documents.length > 0 && (
                <span className="text-xs text-slate-500 mt-1 block">{newRevision.documents.length} fișier(e) atașate</span>
              )}
            </div>
            <Button onClick={handleAddRevision} disabled={!newRevision.date || !newRevision.type} className="w-full">Adaugă</Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Dialog Vizualizare Documente Echipament */}
      <Dialog open={!!viewingEquipmentDocs} onOpenChange={() => setViewingEquipmentDocs(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewingEquipmentDocs?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {viewingEquipmentDocs?.documents?.map((doc, i) => (
              <div key={i} className="border rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-3 py-1 text-xs text-slate-500 font-medium">Pagina {i + 1}</div>
                {doc.startsWith('data:image') ? (
                  <img src={doc} alt={`Pagina ${i + 1}`} className="w-full" />
                ) : doc.startsWith('data:application/pdf') ? (
                  <iframe src={doc} className="w-full h-[600px]" title={`Pagina ${i + 1}`} />
                ) : (
                  <div className="p-4 text-center text-slate-400">Format necunoscut</div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}